// PowerBIDashboard.js - Complete with Caching and Role-Based Access

import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  Alert,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Progress,
} from "reactstrap";
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import Chart from "react-apexcharts";
import { toast } from "react-toastify";
import { getTataDashboardData } from "helpers/dashboard_helper";
import { fetchAllTraining } from "helpers/training_helper";
import { getAllStudentsForTata } from "helpers/student_helper";
import Loader from "components/Loader/Loader";
import { format, subDays } from "date-fns";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import BarChartIcon from "@mui/icons-material/BarChart";
import SchoolIcon from "@mui/icons-material/School";
import GroupsIcon from "@mui/icons-material/Groups";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CloseIcon from "@mui/icons-material/Close";
import PieChartIcon from "@mui/icons-material/PieChart";
import { useNavigate } from "react-router-dom";
import moment from "moment";

// Import batch-related helpers
import {
  fetchAllBatch,
  fetchAllBatchOfTrainer,
  getAllTrainerOfBatch,
  getAllStudentOfBatch,
} from "helpers/batch_helper";
import { getResult } from "helpers/exam_helper";
import { downloadBatchReport, url } from "helpers/url_helper";
import axios from "axios";
import JSZip from "jszip";

// ============================
// CACHE MANAGEMENT UTILITY
// ============================
const CACHE = {
  DASHBOARD_DATA: "dashboard_cache_data_v2",
  TRAINING_DATA: "dashboard_training_cache_v2",
  LOCATION_DATA: "dashboard_location_cache_v2",
  BATCH_DATA: "dashboard_batch_cache_v2",
  CACHE_TIMESTAMP: "dashboard_cache_timestamp_v2",
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes

  // Save data to cache
  set(key, data) {
    try {
      const cacheData = {
        timestamp: Date.now(),
        data: data,
      };
      localStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      console.error("Cache save error:", error);
    }
  },

  // Get data from cache
  get(key) {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return null;
      
      const cacheData = JSON.parse(cached);
      
      // Check if cache is expired
      if (Date.now() - cacheData.timestamp > CACHE.CACHE_DURATION) {
        localStorage.removeItem(key);
        return null;
      }
      
      return cacheData.data;
    } catch (error) {
      return null;
    }
  },

  // Clear all cache
  clear() {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith("dashboard_") || key.startsWith("batch_")) {
        localStorage.removeItem(key);
      }
    });
  },

  // Check if cache exists and is valid
  isValid(key) {
    try {
      const cached = localStorage.getItem(key);
      if (!cached) return false;
      const cacheData = JSON.parse(cached);
      return Date.now() - cacheData.timestamp <= CACHE.CACHE_DURATION;
    } catch {
      return false;
    }
  }
};

// ============================
// ROLE-BASED ACCESS UTILITY
// ============================
const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "{}");
  } catch {
    return {};
  }
};

const getUserRole = () => {
  const user = getCurrentUser();
  return user?.userType || "";
};

const isTrainer = () => {
  const role = getUserRole();
  return role === "Trainer" || role === "Tata Trainer";
};

const isDepotManager = () => {
  return getUserRole() === "Depot Manager";
};

const isAdmin = () => {
  const role = getUserRole();
  return role === "Admin" || role === "Super Admin" || role === "Organization Admin";
};

// ============================
// REGISTER FONTS FOR PDF
// ============================
Font.register({
  family: "Helvetica",
  fonts: [
    { src: "https://fonts.gstatic.com/s/helvetica/v23/Helvetica.ttf" },
    {
      src: "https://fonts.gstatic.com/s/helvetica/v23/Helvetica-Bold.ttf",
      fontWeight: "bold",
    },
  ],
});

// ============================
// PDF STYLES
// ============================
const pdfStyles = StyleSheet.create({
  page: { padding: 40, fontFamily: "Helvetica", fontSize: 10 },
  header: {
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#1e3c72",
    borderBottomStyle: "solid",
    paddingBottom: 10,
  },
  companyName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1e3c72",
    textAlign: "center",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "center",
    marginVertical: 15,
    color: "#1e3c72",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    backgroundColor: "#e6eef9",
    padding: 8,
    marginTop: 20,
    marginBottom: 15,
    borderLeftWidth: 4,
    borderLeftColor: "#1e3c72",
    borderLeftStyle: "solid",
  },
  infoTable: {
    width: "100%",
    marginVertical: 15,
    borderWidth: 1,
    borderColor: "#ddd",
    borderStyle: "solid",
  },
  infoRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    borderBottomStyle: "solid",
    minHeight: 30,
  },
  infoLabel: {
    width: "30%",
    backgroundColor: "#f5f5f5",
    padding: 8,
    fontWeight: "bold",
    fontSize: 10,
  },
  infoValue: { width: "70%", padding: 8, fontSize: 10 },
  statsCard: {
    marginBottom: 15,
    padding: 15,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderStyle: "solid",
    borderRadius: 5,
    backgroundColor: "#fafafa",
  },
  statRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
    paddingVertical: 3,
    borderBottomWidth: 1,
    borderBottomColor: "#edf2f7",
    borderBottomStyle: "solid",
  },
  statLabel: { fontSize: 10, color: "#4a5568" },
  statValue: { fontSize: 10, fontWeight: "bold", color: "#2d3748" },
  distributionTable: {
    width: "100%",
    marginTop: 10,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: "#ddd",
    borderStyle: "solid",
  },
  distHeader: {
    flexDirection: "row",
    backgroundColor: "#2c5282",
    color: "white",
    minHeight: 35,
    alignItems: "center",
  },
  distRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    borderBottomStyle: "solid",
    minHeight: 28,
    alignItems: "center",
  },
  colDistSn: { width: "8%" },
  colDistState: { width: "20%" },
  colDistDepot: { width: "25%" },
  colDistCategory: { width: "32%" },
  colDistCount: { width: "15%" },
  tableHeaderCell: { padding: 6, fontSize: 9, fontWeight: "bold", color: "#fff" },
  tableCell: { padding: 6, fontSize: 9 },
  footer: {
    marginTop: 40,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: "#ddd",
    borderTopStyle: "solid",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  signatureBox: { width: "45%" },
  signatureLine: {
    marginTop: 30,
    borderTopWidth: 1,
    borderTopColor: "#000",
    borderTopStyle: "solid",
    paddingTop: 5,
    textAlign: "center",
    fontSize: 9,
  },
  pageNumber: { position: "absolute", bottom: 30, right: 40, fontSize: 8, color: "#666" },
});

// ============================
// HELPER FUNCTIONS
// ============================
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  if (moment(dateStr, moment.ISO_8601, true).isValid()) return moment(dateStr);
  if (moment(dateStr, "DD/MM/YYYY", true).isValid()) return moment(dateStr, "DD/MM/YYYY");
  if (moment(dateStr, "DD-MM-YYYY", true).isValid()) return moment(dateStr, "DD-MM-YYYY");
  if (moment(dateStr, "YYYY-MM-DD", true).isValid()) return moment(dateStr, "YYYY-MM-DD");
  const parsed = moment(dateStr);
  return parsed.isValid() ? parsed : null;
};

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = parseDate(dateStr);
  if (date && date.isValid()) return date.format("DD/MM/YYYY");
  try {
    if (typeof dateStr === "string") {
      if (dateStr.includes("-")) {
        const [year, month, day] = dateStr.split("T")[0].split("-");
        return `${day}/${month}/${year}`;
      }
      if (dateStr.includes("/")) return dateStr;
    }
    if (dateStr instanceof Date) {
      const day = dateStr.getDate().toString().padStart(2, "0");
      const month = (dateStr.getMonth() + 1).toString().padStart(2, "0");
      const year = dateStr.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return String(dateStr);
  } catch {
    return String(dateStr);
  }
};

const formatTrainerNames = (trainers) => {
  if (!trainers || trainers.length === 0) return "No Trainer Assigned";
  const names = trainers.map((t) => {
    if (t.name) return t.name;
    if (t.firstName && t.lastName) return `${t.firstName} ${t.lastName}`;
    if (t.firstName) return t.firstName;
    return `Trainer ${t.id}`;
  });
  return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
};

// ============================
// CONCURRENCY POOL
// ============================
const asyncPool = async (concurrency, items, iteratorFn) => {
  const results = [];
  const executing = new Set();
  for (const [index, item] of items.entries()) {
    const promise = Promise.resolve().then(() => iteratorFn(item, index));
    results.push(promise);
    executing.add(promise);
    const clean = () => executing.delete(promise);
    promise.then(clean, clean);
    if (executing.size >= concurrency) await Promise.race(executing);
  }
  return Promise.all(results);
};

// ============================
// EXTRACT LOCATION FILTERS
// ============================
const extractLocationFilters = (students) => {
  if (!students || students.length === 0) {
    return { states: [], divisions: [], depots: [], depotsByState: {}, depotsByDivision: {}, statesList: [], divisionsList: [], totalDepotsCount: 0 };
  }
  const stateSet = new Set();
  const divisionSet = new Set();
  const depotMap = new Map();
  const depotByStateMap = new Map();
  const depotByDivisionMap = new Map();
  
  students.forEach((student) => {
    const state = student.tataStudentDetailsDto?.state || student.state || student.studentAddressInfo?.state;
    const division = student.tataStudentDetailsDto?.division || student.division;
    const depot = student.tataStudentDetailsDto?.depot || student.depot || student.branch;
    
    if (state && state !== "Unknown State" && !state.includes("Unknown") && state.trim() !== "") {
      stateSet.add(state.trim());
    }
    if (division && division !== "Unknown Division" && !division.includes("Unknown") && division.trim() !== "") {
      divisionSet.add(division.trim());
    }
    if (state && depot && state !== "Unknown State" && depot !== "Unknown Depot" && !state.includes("Unknown") && !depot.includes("Unknown") && state.trim() !== "" && depot.trim() !== "") {
      const key = `${state.trim()}-${depot.trim()}`;
      if (!depotMap.has(key)) {
        depotMap.set(key, { state: state.trim(), depot: depot.trim(), division: division?.trim() || "N/A" });
      }
      if (!depotByStateMap.has(state.trim())) {
        depotByStateMap.set(state.trim(), new Set());
      }
      depotByStateMap.get(state.trim()).add(depot.trim());
    }
    if (division && depot && division !== "Unknown Division" && depot !== "Unknown Depot" && !division.includes("Unknown") && !depot.includes("Unknown") && division.trim() !== "" && depot.trim() !== "") {
      if (!depotByDivisionMap.has(division.trim())) {
        depotByDivisionMap.set(division.trim(), new Set());
      }
      depotByDivisionMap.get(division.trim()).add(depot.trim());
    }
  });
  
  const states = Array.from(stateSet).sort();
  const divisions = Array.from(divisionSet).sort();
  const depotsByState = {};
  depotByStateMap.forEach((depots, state) => {
    depotsByState[state] = Array.from(depots).sort();
  });
  const depotsByDivision = {};
  depotByDivisionMap.forEach((depots, division) => {
    depotsByDivision[division] = Array.from(depots).sort();
  });
  
  return {
    states,
    divisions,
    depots: Array.from(depotMap.values()),
    depotsByState,
    depotsByDivision,
    statesList: states,
    divisionsList: divisions,
    totalDepotsCount: depotMap.size
  };
};

// ============================
// PROCESS TRAINING DISTRIBUTION
// ============================
const processTrainingDistribution = (trainings, selectedState, selectedDivision, selectedDepot) => {
  const distributionMap = {};
  if (!Array.isArray(trainings) || trainings.length === 0) return [];
  
  trainings.forEach((training) => {
    if (!training) return;
    const depot = training.depot || training.location || training.branch;
    const state = training.state;
    const division = training.division || "N/A";
    const category = training.type || training.trainingType || training.courseType || "General Training";
    
    if (selectedState && selectedState.value !== "all" && state !== selectedState.label) return;
    if (selectedDivision && selectedDivision.value !== "all" && division !== selectedDivision.label) return;
    if (selectedDepot && selectedDepot.value !== "all" && depot !== selectedDepot.label) return;
    
    if (!depot || !state || depot === "Unknown Depot" || state === "Unknown State" || depot.includes("Unknown") || state.includes("Unknown") || depot.trim() === "" || state.trim() === "") return;
    
    const key = `${depot.trim()}-${state.trim()}-${category}`;
    if (!distributionMap[key]) {
      distributionMap[key] = { depot: depot.trim(), state: state.trim(), division: division, category, count: 0 };
    }
    if (training.candidates && Array.isArray(training.candidates)) {
      distributionMap[key].count += training.candidates.length;
    } else if (training.participants && Array.isArray(training.participants)) {
      distributionMap[key].count += training.participants.length;
    } else if (training.count && typeof training.count === "number") {
      distributionMap[key].count += training.count;
    } else {
      distributionMap[key].count += 1;
    }
  });
  
  return Object.values(distributionMap).filter(item => item.count > 0).sort((a, b) => b.count - a.count);
};

// ============================
// MAKE CHART CONFIG
// ============================
const makeChart = (title, categories, data, type = "bar", colors = []) => ({
  title: { text: title, align: "left", style: { fontSize: "16px", fontWeight: "bold", color: "#333" } },
  chart: { type: type, toolbar: { show: true }, animations: { enabled: true, speed: 800 } },
  plotOptions: {
    bar: { horizontal: false, columnWidth: "60%", borderRadius: 6, dataLabels: { position: "top" } },
    pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Total", formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0) } } } },
  },
  dataLabels: { enabled: true, formatter: (val) => Math.round(val), offsetY: -20, style: { fontSize: "12px", colors: ["#304758"] } },
  stroke: { show: true, width: 2, colors: ["transparent"] },
  xaxis: { categories, labels: { style: { fontSize: "12px" } } },
  yaxis: { title: { text: type === "bar" ? "Number of Candidates" : "Count" }, labels: { formatter: (val) => Math.round(val) } },
  fill: { opacity: 1, type: "gradient", gradient: { shade: "light", type: "vertical", shadeIntensity: 0.25, gradientToColors: colors, inverseColors: false, opacityFrom: 0.85, opacityTo: 0.85, stops: [0, 100] } },
  tooltip: { y: { formatter: (val) => `${Math.round(val)} candidates` } },
  colors: colors.length ? colors : ["#4299e1", "#48bb78", "#ed8936", "#ecc94b"],
  grid: { borderColor: "#f1f1f1" },
  series: [{ name: "Count", data }],
});

// ============================
// PRINT PREVIEW MODAL
// ============================
const PrintPreviewModal = ({ isOpen, toggle, data, dateRange, trainingDistribution, stats, filters, chartsData }) => {
  const totalCandidates = stats.totalCandidates || 0;
  const nominated = stats.nominated || 0;
  const attempted = stats.attempted || 0;
  const passed = stats.passed || 0;
  const nominationRate = totalCandidates > 0 ? ((nominated / totalCandidates) * 100).toFixed(1) : 0;
  const attemptRate = nominated > 0 ? ((attempted / nominated) * 100).toFixed(1) : 0;
  const passRate = attempted > 0 ? ((passed / attempted) * 100).toFixed(1) : 0;
  const completionRate = data?.batches?.planned > 0 ? ((data.batches.completed / data.batches.planned) * 100).toFixed(1) : 0;

  const getFilterText = () => {
    const parts = [];
    if (filters.selectedState && filters.selectedState.value !== "all") parts.push(`State: ${filters.selectedState.label}`);
    if (filters.selectedDivision && filters.selectedDivision.value !== "all") parts.push(`Division: ${filters.selectedDivision.label}`);
    if (filters.selectedDepot && filters.selectedDepot.value !== "all") parts.push(`Depot: ${filters.selectedDepot.label}`);
    return parts.length > 0 ? parts.join(" • ") : "All Locations";
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Training Report - ${format(new Date(), "dd-MM-yyyy")}</title>
          <style>
            @media print {
              @page { size: A4 portrait; margin: 0.4in; }
              body { font-family: Arial, Helvetica, sans-serif; background: white; color: #1a202c; line-height: 1.4; }
              .no-print { display: none !important; }
              .print-report { max-width: 100%; margin: 0 auto; padding: 10px; }
              .print-header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #1e3c72; padding-bottom: 10px; }
              .print-title { font-size: 22px; font-weight: bold; color: #1e3c72; margin-bottom: 3px; }
              .print-meta { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; margin-bottom: 15px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
              .print-meta-item { display: flex; align-items: center; font-size: 11px; }
              .print-meta-label { font-weight: bold; color: #2d3748; min-width: 100px; }
              .print-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 15px; }
              .print-stat-card { background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; text-align: center; border-top: 3px solid #1e3c72; }
              .print-stat-value { font-size: 20px; font-weight: bold; color: #1e3c72; }
              .print-stat-percent { font-size: 10px; color: #48bb78; font-weight: 600; }
              .print-table { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 15px; }
              .print-table th { background: #1e3c72; color: white; padding: 8px; text-align: left; }
              .print-table td { padding: 6px; border-bottom: 1px solid #e2e8f0; }
              .print-table tr:nth-child(even) { background-color: #f8fafc; }
              .print-signature { display: flex; justify-content: space-between; margin-top: 20px; padding-top: 15px; border-top: 1px solid #e2e8f0; }
              .print-signature-box { width: 45%; }
              .print-signature-line { margin-top: 25px; border-top: 1px solid #000; padding-top: 5px; font-size: 10px; text-align: center; }
              .print-footer { margin-top: 15px; padding-top: 10px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 9px; color: #718096; }
            }
          </style>
        </head>
        <body>
          <div class="print-report">
            <div class="print-header">
              <h1 class="print-title">TML SMART CITY MOBILITY SOLUTIONS LIMITED</h1>
              <h2 style="font-size:18px;color:#1e3c72;margin-top:5px;">TRAINING ANALYTICS REPORT</h2>
              <p style="font-size:11px;color:#4a5568;">${format(new Date(dateRange.fromDate), "dd MMM yyyy")} - ${format(new Date(dateRange.toDate), "dd MMM yyyy")}</p>
            </div>
            
            <div class="print-meta">
              <div class="print-meta-item"><span class="print-meta-label">Filters:</span><span>${getFilterText()}</span></div>
              <div class="print-meta-item"><span class="print-meta-label">Generated:</span><span>${format(new Date(), "dd MMM yyyy HH:mm")}</span></div>
            </div>
            
            <div class="print-stats-grid">
              <div class="print-stat-card"><div>Total</div><div class="print-stat-value">${totalCandidates.toLocaleString()}</div></div>
              <div class="print-stat-card"><div>Nominated</div><div class="print-stat-value" style="color:#48bb78;">${nominated.toLocaleString()}</div><div class="print-stat-percent">${nominationRate}%</div></div>
              <div class="print-stat-card"><div>Attempted</div><div class="print-stat-value" style="color:#ed8936;">${attempted.toLocaleString()}</div><div class="print-stat-percent">${attemptRate}%</div></div>
              <div class="print-stat-card"><div>Passed</div><div class="print-stat-value" style="color:#4299e1;">${passed.toLocaleString()}</div><div class="print-stat-percent">${passRate}%</div></div>
            </div>
            
            <h3 style="color:#1e3c72;">Training Distribution</h3>
            <table class="print-table">
              <thead><tr><th>#</th><th>State</th><th>Depot</th><th>Category</th><th>Count</th></tr></thead>
              <tbody>
                ${trainingDistribution.slice(0, 15).map((item, i) => `
                  <tr><td>${i + 1}</td><td>${item.state}</td><td>${item.depot}</td><td>${item.category}</td><td>${item.count}</td></tr>
                `).join("")}
              </tbody>
              <tfoot><tr style="font-weight:bold;background:#edf2f7;"><td colspan="4" style="text-align:right;">Total:</td><td>${trainingDistribution.reduce((sum, t) => sum + (t.count || 0), 0).toLocaleString()}</td></tr></tfoot>
            </table>
            
            <div class="print-signature">
              <div class="print-signature-box">
                <p style="font-size:10px;">Training Manager</p>
                <div class="print-signature-line">Authorized Signatory</div>
              </div>
              <div class="print-signature-box">
                <p style="font-size:10px;">Date: ${format(new Date(), "dd/MM/yyyy")}</p>
                <p style="font-size:9px;color:#718096;margin-top:25px;">System Generated Report</p>
              </div>
            </div>
            
            <div class="print-footer">TML Smart City Mobility • ${format(new Date(), "dd MMM yyyy HH:mm")}</div>
          </div>
          <script>window.onload=function(){setTimeout(function(){window.print();window.onafterprint=function(){window.close();};},500);};<\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" fullscreen>
      <ModalHeader toggle={toggle} style={{ background: "#1e3c72", color: "white" }}>
        <AssessmentIcon style={{ marginRight: "12px" }} /> Print Preview
      </ModalHeader>
      <ModalBody style={{ background: "#f8fafc", padding: "20px" }}>
        <div style={{ background: "white", borderRadius: "12px", padding: "20px" }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 style={{ color: "#1e3c72" }}>TML SMART CITY MOBILITY</h4>
            <div style={{ background: "#1e3c72", color: "white", padding: "8px 15px", borderRadius: "8px" }}>
              <div style={{ fontSize: "18px", fontWeight: "bold" }}>{totalCandidates.toLocaleString()}</div>
              <div style={{ fontSize: "10px" }}>Participants</div>
            </div>
          </div>
          
          <Row className="mb-3">
            <Col md={3}><Card><CardBody className="text-center"><div style={{ fontSize: "11px", color: "#718096" }}>Total</div><div style={{ fontSize: "22px", fontWeight: "bold" }}>{totalCandidates.toLocaleString()}</div></CardBody></Card></Col>
            <Col md={3}><Card><CardBody className="text-center"><div style={{ fontSize: "11px", color: "#718096" }}>Nominated</div><div style={{ fontSize: "22px", fontWeight: "bold", color: "#48bb78" }}>{nominated.toLocaleString()}</div><div style={{ fontSize: "9px" }}>{nominationRate}%</div></CardBody></Card></Col>
            <Col md={3}><Card><CardBody className="text-center"><div style={{ fontSize: "11px", color: "#718096" }}>Attempted</div><div style={{ fontSize: "22px", fontWeight: "bold", color: "#ed8936" }}>{attempted.toLocaleString()}</div><div style={{ fontSize: "9px" }}>{attemptRate}%</div></CardBody></Card></Col>
            <Col md={3}><Card><CardBody className="text-center"><div style={{ fontSize: "11px", color: "#718096" }}>Passed</div><div style={{ fontSize: "22px", fontWeight: "bold", color: "#4299e1" }}>{passed.toLocaleString()}</div><div style={{ fontSize: "9px" }}>{passRate}%</div></CardBody></Card></Col>
          </Row>
          
          <div style={{ display: "flex", justifyContent: "center", gap: "15px", marginTop: "20px", padding: "15px", background: "#f8fafc", borderRadius: "8px" }}>
            <Button color="primary" onClick={handlePrint} style={{ padding: "10px 25px", background: "#1e3c72", border: "none", borderRadius: "8px" }}>
              <PrintIcon style={{ marginRight: "8px" }} /> Print Report
            </Button>
            <Button color="secondary" onClick={toggle} style={{ borderRadius: "8px" }}>
              <CloseIcon style={{ marginRight: "8px" }} /> Cancel
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

// ============================
// BATCH DETAILS TAB COMPONENT
// ============================
const BatchDetailsTab = ({ datePeriod, customStartDate, customEndDate, selectedState, selectedDepot, selectedDivision }) => {
  const [loader, setLoader] = useState(true);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [allBatch, setAllBatch] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [batchTrainersMap, setBatchTrainersMap] = useState({});
  const [batchStudentsMap, setBatchStudentsMap] = useState({});
  const [batchStudentsCount, setBatchStudentsCount] = useState({});
  const [passFailStatsMap, setPassFailStatsMap] = useState({});
  const [fetchProgress, setFetchProgress] = useState({ active: false, current: 0, total: 0, message: "" });
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [failedStudentsList, setFailedStudentsList] = useState([]);
  const [notAttemptedStudentsList, setNotAttemptedStudentsList] = useState([]);
  const [passedStudentsList, setPassedStudentsList] = useState([]);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [showNotAttemptedModal, setShowNotAttemptedModal] = useState(false);
  const [showPassedModal, setShowPassedModal] = useState(false);
  const [reportModalShow, setReportModalShow] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [allReportsModalShow, setAllReportsModalShow] = useState(false);
  const [selectedAllReportType, setSelectedAllReportType] = useState(null);
  const [consolidatedModalShow, setConsolidatedModalShow] = useState(false);
  const [consolidatedReportType, setConsolidatedReportType] = useState("DDT");
  const [batchReportData, setBatchReportData] = useState({});
  const [missingReports, setMissingReports] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isCacheUsed, setIsCacheUsed] = useState(false);

  // Chart states
  const [statusDistributionChart, setStatusDistributionChart] = useState(null);
  const [topBatchesChart, setTopBatchesChart] = useState(null);
  const [batchStatsChart, setBatchStatsChart] = useState(null);

  // Get current user role
  const currentUser = getCurrentUser();
  const userRole = getUserRole();
  const userId = currentUser?.id || "";
  const userDepot = currentUser?.depot || currentUser?.depotName || "";

  const reportOptions = [
    { value: "DDT", label: "DDT Report" },
    { value: "DASP", label: "DASP Report" },
    { value: "Other", label: "Other Report" },
  ];

  const consolidatedOptions = [
    { value: "DDT", label: "DDT Reports (Sheet per Batch)" },
    { value: "DASP", label: "DASP Reports (Sheet per Batch)" },
    { value: "Other", label: "Other Reports (Sheet per Batch)" },
  ];

  // ============================
  // FETCH STUDENT EXAM RESULT
  // ============================
  const fetchStudentExamResult = async (batchId, studentId) => {
    if (!batchId || !studentId) return null;
    try {
      const response = await getResult(`${batchId}/${studentId}`);
      if (response?.success && response.result) {
        let preTestScore = null, postTestScore = null;
        const preTest = response.result.preTest;
        const postTest = response.result.postTest;
        if (preTest !== undefined && preTest !== null) {
          preTestScore = typeof preTest === "number" ? preTest : (preTest.percentage || preTest.score || 0);
        }
        if (postTest !== undefined && postTest !== null) {
          postTestScore = typeof postTest === "number" ? postTest : (postTest.percentage || postTest.score || 0);
        }
        return { preTest: preTestScore !== null ? parseFloat(preTestScore) : null, postTest: postTestScore !== null ? parseFloat(postTestScore) : null, studentId, batchId };
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // ============================
  // FETCH EXAM RESULTS FOR BATCH
  // ============================
  const fetchExamResultsForBatch = async (batchId, students) => {
    if (!batchId || !students || students.length === 0) {
      return { passed: 0, failed: 0, notAttempted: students?.length || 0, totalStudents: students?.length || 0, students: [], examResults: {} };
    }
    try {
      const examResults = {};
      let passed = 0, failed = 0, notAttempted = 0;
      const studentDetails = [];
      
      const results = await asyncPool(10, students, async (student) => {
        const studentId = student.id || student._id;
        if (!studentId) return null;
        const result = await fetchStudentExamResult(batchId, studentId);
        return { student, studentId, result };
      });
      
      for (const { student, studentId, result } of results) {
        if (!student) continue;
        const studentName = student.name || student.fullName || `${student.firstName || ""} ${student.lastName || ""}`.trim() || "Unknown";
        const studentData = {
          id: studentId,
          name: studentName,
          mobile: student.mobile || student.phone || "",
          email: student.email || "",
          aadhaarNo: student.aadhaarNo || student.aadhaar_number || student.tataStudentDetails?.aadhaarNo || "--",
          contractorToken: student.contractorToken || student.tataStudentDetails?.contractorTokenNo || "--",
          studentState: student.tataStudentDetails?.state || student.state || "",
          studentDepot: student.tataStudentDetails?.depot || student.depot || "",
          trainingState: student.tataStudentDetails?.trainingState || "",
          trainingDepot: student.tataStudentDetails?.trainingDepot || "",
          postTestScore: null, preTestScore: null, status: "Not Attempted", attempted: false, batchId: batchId
        };
        if (result && result.postTest !== null) {
          examResults[studentId] = result;
          studentData.postTestScore = result.postTest;
          studentData.preTestScore = result.preTest;
          studentData.attempted = true;
          if (result.postTest >= 80) { 
            passed++; 
            studentData.status = "Passed"; 
          } else { 
            failed++; 
            studentData.status = "Failed"; 
          }
        } else { 
          notAttempted++; 
        }
        studentDetails.push(studentData);
      }
      return { passed, failed, notAttempted, totalStudents: students.length, students: studentDetails, examResults };
    } catch (error) {
      console.error(`Error fetching exam results for batch ${batchId}:`, error);
      return { passed: 0, failed: 0, notAttempted: students.length, totalStudents: students.length, students: [], examResults: {} };
    }
  };

  // ============================
  // GET ALL BATCHES WITH ROLE-BASED ACCESS
  // ============================
  const getAllBatches = useCallback(async () => {
    try {
      setLoader(true);
      
      // For Trainer/Tata Trainer - fetch only their batches
      if (isTrainer()) {
        const res = await fetchAllBatchOfTrainer();
        if (res?.success) {
          const batches = (res.result || []).map(batch => ({
            ...batch,
            id: batch.id || batch._id,
            batchName: batch.batchName,
            department: batch.department,
            state: batch.state,
            depot: batch.depot,
            trainingStartDate: batch.trainingStartDate,
            trainingEndDate: batch.trainingEndDate,
            totalStudents: batch.totalStudents || batch.students?.length || 0,
            passedStudents: batch.passedStudents || 0,
            failedStudents: batch.failedStudents || 0,
            notAttemptedStudents: batch.notAttemptedStudents || 0,
            passPercentage: batch.passPercentage || 0,
            trainerNames: batch.trainerNames || (batch.trainers?.map(t => t.name || t.firstName).join(", ") || "No Trainer Assigned"),
            totalTrainers: batch.totalTrainers || batch.trainers?.length || 0,
          }));
          setAllBatch(batches);
          setFilteredBatches(batches);
          toast.success(`Loaded ${batches.length} batches`);
          await loadBatchDetails(batches);
        }
      } 
      // For Depot Manager - filter by depot
      else if (isDepotManager() && userDepot) {
        const res = await fetchAllBatch();
        if (res?.success) {
          const allBatches = (res.result || []).map(batch => ({
            ...batch,
            id: batch.id || batch._id,
            batchName: batch.batchName,
            department: batch.department,
            state: batch.state,
            depot: batch.depot,
            trainingStartDate: batch.trainingStartDate,
            trainingEndDate: batch.trainingEndDate,
            totalStudents: batch.totalStudents || batch.students?.length || 0,
            passedStudents: batch.passedStudents || 0,
            failedStudents: batch.failedStudents || 0,
            notAttemptedStudents: batch.notAttemptedStudents || 0,
            passPercentage: batch.passPercentage || 0,
            trainerNames: batch.trainerNames || (batch.trainers?.map(t => t.name || t.firstName).join(", ") || "No Trainer Assigned"),
            totalTrainers: batch.totalTrainers || batch.trainers?.length || 0,
          }));
          
          // Filter by depot
          const filtered = allBatches.filter(b => b.depot === userDepot);
          setAllBatch(filtered);
          setFilteredBatches(filtered);
          toast.success(`Loaded ${filtered.length} batches for ${userDepot}`);
          await loadBatchDetails(filtered);
        }
      } 
      // For Admin - full access
      else {
        const res = await fetchAllBatch();
        if (res?.success) {
          const batches = (res.result || []).map(batch => ({
            ...batch,
            id: batch.id || batch._id,
            batchName: batch.batchName,
            department: batch.department,
            state: batch.state,
            depot: batch.depot,
            trainingStartDate: batch.trainingStartDate,
            trainingEndDate: batch.trainingEndDate,
            totalStudents: batch.totalStudents || batch.students?.length || 0,
            passedStudents: batch.passedStudents || 0,
            failedStudents: batch.failedStudents || 0,
            notAttemptedStudents: batch.notAttemptedStudents || 0,
            passPercentage: batch.passPercentage || 0,
            trainerNames: batch.trainerNames || (batch.trainers?.map(t => t.name || t.firstName).join(", ") || "No Trainer Assigned"),
            totalTrainers: batch.totalTrainers || batch.trainers?.length || 0,
          }));
          setAllBatch(batches);
          setFilteredBatches(batches);
          toast.success(`Loaded ${batches.length} batches`);
          await loadBatchDetails(batches);
        }
      }
    } catch (error) {
      console.error("Error in getAllBatches:", error);
      toast.error(error?.response?.data?.message || "Failed to load batches");
    } finally {
      setLoader(false);
    }
  }, [userDepot]);

  // ============================
  // LOAD BATCH DETAILS (Trainers, Students, Results)
  // ============================
  const loadBatchDetails = useCallback(async (batches) => {
    if (!batches || batches.length === 0) {
      setIsDataLoaded(true);
      return;
    }

    // Check cache first
    const cacheKey = `batch_details_${batches.map(b => b.id).join("_")}`;
    const cachedData = CACHE.get(cacheKey);
    if (cachedData) {
      setBatchTrainersMap(cachedData.trainersMap || {});
      setBatchStudentsMap(cachedData.studentsMap || {});
      setBatchStudentsCount(cachedData.studentsCount || {});
      setPassFailStatsMap(cachedData.passFailStats || {});
      setFailedStudentsList(cachedData.failedStudents || []);
      setNotAttemptedStudentsList(cachedData.notAttemptedStudents || []);
      setPassedStudentsList(cachedData.passedStudents || []);
      setIsCacheUsed(true);
      setIsDataLoaded(true);
      updateCharts(batches, cachedData.studentsCount || {}, cachedData.passFailStats || {});
      return;
    }

    setFetchProgress({ active: true, current: 0, total: batches.length, message: "Loading batch details..." });

    const trainersMap = {};
    const studentsMap = {};
    const studentsCountMap = {};
    const passFailMap = {};
    const allFailed = [];
    const allNotAttempted = [];
    const allPassed = [];

    await asyncPool(5, batches, async (batch) => {
      const batchId = batch.id;
      if (!batchId) return;
      
      try {
        const [trainers, students] = await Promise.all([
          getAllTrainerOfBatch(batchId).catch(() => []),
          getAllStudentOfBatch(batchId).catch(() => []),
        ]);
        trainersMap[batchId] = trainers?.success ? trainers.result || [] : [];
        studentsMap[batchId] = students?.success ? students.result || [] : [];
        studentsCountMap[batchId] = studentsMap[batchId].length;
      } catch (error) {
        trainersMap[batchId] = [];
        studentsMap[batchId] = [];
        studentsCountMap[batchId] = 0;
      }
    });

    setFetchProgress({ active: true, current: 0, total: batches.length, message: "Fetching exam results..." });

    let processed = 0;
    await asyncPool(5, batches, async (batch) => {
      const batchId = batch.id;
      if (batchId && studentsMap[batchId]) {
        const data = await fetchExamResultsForBatch(batchId, studentsMap[batchId]);
        passFailMap[batchId] = data;
        data.students.forEach((student) => {
          const studentWithBatch = { 
            ...student, 
            batchName: batch.batchName || "N/A", 
            batchState: batch.state || "", 
            batchDepot: batch.depot || "" 
          };
          if (student.status === "Failed") allFailed.push(studentWithBatch);
          else if (student.status === "Not Attempted") allNotAttempted.push(studentWithBatch);
          else if (student.status === "Passed") allPassed.push(studentWithBatch);
        });
      }
      processed++;
      setFetchProgress(prev => ({ ...prev, current: processed }));
    });

    setBatchTrainersMap(trainersMap);
    setBatchStudentsMap(studentsMap);
    setBatchStudentsCount(studentsCountMap);
    setPassFailStatsMap(passFailMap);
    setFailedStudentsList(allFailed);
    setNotAttemptedStudentsList(allNotAttempted);
    setPassedStudentsList(allPassed);

    // Save to cache
    CACHE.set(cacheKey, {
      trainersMap,
      studentsMap,
      studentsCount: studentsCountMap,
      passFailStats: passFailMap,
      failedStudents: allFailed,
      notAttemptedStudents: allNotAttempted,
      passedStudents: allPassed,
    });

    updateCharts(batches, studentsCountMap, passFailMap);
    setFetchProgress({ active: false, current: 0, total: 0, message: "" });
    setIsDataLoaded(true);
  }, []);

  // ============================
  // UPDATE CHARTS
  // ============================
  const updateCharts = (batches, studentsCountMap, passFailMap) => {
    const totalStudents = Object.values(studentsCountMap).reduce((a, b) => a + (b || 0), 0);
    const totalPassed = Object.values(passFailMap).reduce((a, b) => a + (b.passed || 0), 0);
    const totalFailed = Object.values(passFailMap).reduce((a, b) => a + (b.failed || 0), 0);
    const totalNotAttempted = Object.values(passFailMap).reduce((a, b) => a + (b.notAttempted || 0), 0);
    
    setStatusDistributionChart({
      options: {
        chart: { type: "donut" },
        labels: ["Passed (≥80%)", "Failed (<80%)", "Not Attempted"],
        colors: ["#48bb78", "#ed8936", "#ecc94b"],
        legend: { position: "bottom" },
        plotOptions: { 
          pie: { 
            donut: { 
              size: "65%", 
              labels: { 
                show: true, 
                total: { 
                  show: true, 
                  label: "Total", 
                  formatter: () => totalStudents 
                } 
              } 
            } 
          } 
        },
      },
      series: [totalPassed, totalFailed, totalNotAttempted],
    });
    
    const topBatches = batches
      .map((b) => ({ name: b.batchName || "N/A", students: studentsCountMap[b.id] || 0 }))
      .sort((a, b) => b.students - a.students)
      .slice(0, 10);
    setTopBatchesChart({
      options: {
        chart: { type: "bar" },
        plotOptions: { bar: { horizontal: true } },
        xaxis: { categories: topBatches.map((d) => d.name.substring(0, 20)) },
        colors: ["#4299e1"],
        title: { text: "Top 10 Batches by Student Count", align: "left", style: { fontSize: "14px", fontWeight: "bold" } },
      },
      series: [{ name: "Students", data: topBatches.map((d) => d.students) }],
    });
    
    const perf = batches
      .map((b) => {
        const total = studentsCountMap[b.id] || 0;
        const passed = passFailMap[b.id]?.passed || 0;
        return { name: b.batchName || "N/A", passRate: total > 0 ? (passed / total) * 100 : 0 };
      })
      .sort((a, b) => b.passRate - a.passRate)
      .slice(0, 10);
    setBatchStatsChart({
      options: {
        chart: { type: "bar" },
        plotOptions: { bar: { borderRadius: 6 } },
        xaxis: { categories: perf.map((d) => d.name.substring(0, 20)) },
        colors: ["#48bb78"],
        title: { text: "Top 10 Batches by Pass Rate", align: "left" },
        yaxis: { title: { text: "Pass Rate (%)" }, max: 100 },
      },
      series: [{ name: "Pass Rate (%)", data: perf.map((d) => d.passRate) }],
    });
  };

  // ============================
  // APPLY FILTERS
  // ============================
  const applyFilters = useCallback(() => {
    let filtered = [...allBatch];
    
    if (selectedState && selectedState !== "") {
      filtered = filtered.filter(batch => batch.state === selectedState);
    }
    
    if (selectedDepot && selectedDepot !== "") {
      filtered = filtered.filter(batch => batch.depot === selectedDepot);
    }
    
    if (datePeriod && datePeriod !== "ALL") {
      const today = moment().startOf('day');
      filtered = filtered.filter(batch => {
        if (!batch.trainingStartDate) return false;
        let startDate = parseDate(batch.trainingStartDate);
        if (!startDate || !startDate.isValid()) return false;
        startDate = startDate.startOf('day');
        switch (datePeriod) {
          case "TODAY": return startDate.isSame(today, 'day');
          case "WEEKLY": return startDate.isSame(today, 'week');
          case "MONTHLY": return startDate.isSame(today, 'month');
          case "YEARLY": return startDate.isSame(today, 'year');
          default: return true;
        }
      });
    }
    
    if (customStartDate && customEndDate) {
      const start = moment(customStartDate).startOf('day');
      const end = moment(customEndDate).endOf('day');
      filtered = filtered.filter(batch => {
        if (!batch.trainingStartDate) return false;
        const batchDate = parseDate(batch.trainingStartDate);
        if (!batchDate || !batchDate.isValid()) return false;
        return batchDate.isBetween(start, end, null, '[]');
      });
    }
    
    if (searchText) {
      const search = searchText.toLowerCase().trim();
      filtered = filtered.filter(batch => {
        const trainers = batchTrainersMap[batch.id] || [];
        const trainerNames = trainers.map(t => t.name || `${t.firstName || ""} ${t.lastName || ""}`).join(" ").toLowerCase();
        return (
          (batch.batchName || "").toLowerCase().includes(search) ||
          (batch.department || "").toLowerCase().includes(search) ||
          (batch.depot || "").toLowerCase().includes(search) ||
          (batch.state || "").toLowerCase().includes(search) ||
          trainerNames.includes(search) ||
          (batch.trainerNames || "").toLowerCase().includes(search) ||
          formatDateToDDMMYYYY(batch.trainingStartDate).includes(search) ||
          formatDateToDDMMYYYY(batch.trainingEndDate).includes(search)
        );
      });
    }
    
    setFilteredBatches(filtered);
  }, [allBatch, selectedState, selectedDepot, datePeriod, customStartDate, customEndDate, searchText, batchTrainersMap]);

  // ============================
  // EXPORT STUDENTS TO EXCEL
  // ============================
  const exportStudentsToExcel = (list, filename) => {
    if (!list.length) {
      toast.error(`No ${filename.replace("_", " ")} to export`);
      return;
    }
    const exportData = list.map((s, idx) => ({
      "S.No": idx + 1,
      "Student Name": s.name,
      "Mobile": s.mobile,
      "Email": s.email,
      "Aadhaar No": s.aadhaarNo,
      "Contractor Token": s.contractorToken,
      "Post-Test Score": s.postTestScore ? `${s.postTestScore}%` : "N/A",
      "Pre-Test Score": s.preTestScore ? `${s.preTestScore}%` : "N/A",
      "Status": s.status,
      "Student State": s.studentState || "N/A",
      "Student Depot": s.studentDepot || "N/A",
      "Training State": s.trainingState || "N/A",
      "Training Depot": s.trainingDepot || "N/A",
      "Batch Name": s.batchName,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, filename);
    XLSX.writeFile(wb, `${filename}_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success(`Exported ${list.length} students`);
  };

  // ============================
  // DOWNLOAD ALL BATCHES EXCEL
  // ============================
  const downloadAllBatchesExcel = () => {
    if (!filteredBatches.length) {
      toast.error("No data to export");
      return;
    }
    const data = filteredBatches.map((b, idx) => {
      const stats = passFailStatsMap[b.id] || { passed: 0, failed: 0, notAttempted: 0 };
      const trainers = batchTrainersMap[b.id] || [];
      return {
        "S.No": idx + 1,
        "Batch Name": b.batchName || "",
        "Department": b.department || "",
        "Trainer(s)": formatTrainerNames(trainers),
        "Start Date": formatDateToDDMMYYYY(b.trainingStartDate),
        "End Date": formatDateToDDMMYYYY(b.trainingEndDate),
        "State": b.state || "N/A",
        "Depot": b.depot || "N/A",
        "Total Students": batchStudentsCount[b.id] || 0,
        "Passed (≥80%)": stats.passed || 0,
        "Failed (<80%)": stats.failed || 0,
        "Not Attempted": stats.notAttempted || 0,
        "Pass %": batchStudentsCount[b.id] ? ((stats.passed / batchStudentsCount[b.id]) * 100).toFixed(2) + "%" : "0%",
      };
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Batches");
    XLSX.writeFile(wb, `All_Batches_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success("Excel file downloaded!");
  };

  // ============================
  // FETCH SINGLE BATCH REPORT
  // ============================
  const fetchSingleBatchReportDirectly = async (batch, reportTypeValue) => {
    try {
      const batchId = batch.id;
      if (!batchId) return null;
      const apiUrl = `${downloadBatchReport}${batchId}?type=${reportTypeValue}`;
      const response = await axios.get(apiUrl, { responseType: "blob", timeout: 30000 });
      return new Blob([response.data], { type: "application/zip" });
    } catch (error) {
      console.error("Error fetching report:", error);
      return null;
    }
  };

  // ============================
  // DOWNLOAD SINGLE BATCH REPORT
  // ============================
  const confirmDownloadReport = async () => {
    if (!selectedReportType) {
      toast.error("Please select a report type!");
      return;
    }
    try {
      setLoader(true);
      setReportModalShow(false);
      const reportBlob = await fetchSingleBatchReportDirectly(selectedBatch, selectedReportType.value);
      if (!reportBlob) {
        toast.error("Failed to fetch report");
        return;
      }
      const downloadUrl = window.URL.createObjectURL(reportBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `Batch_${selectedBatch.batchName}_${selectedReportType.value}_Report.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success(`${selectedReportType.label} downloaded!`);
    } catch (error) {
      toast.error("Error downloading report");
    } finally {
      setLoader(false);
    }
  };

  // ============================
  // DOWNLOAD ALL REPORTS
  // ============================
  const confirmDownloadAllReports = async () => {
    if (!selectedAllReportType) {
      toast.error("Please select a report type!");
      return;
    }
    try {
      setLoader(true);
      setAllReportsModalShow(false);
      const batches = filteredBatches;
      if (batches.length === 0) {
        toast.error("No batches to download");
        setLoader(false);
        return;
      }
      
      setFetchProgress({ 
        active: true, 
        current: 0, 
        total: batches.length, 
        message: `Downloading ${selectedAllReportType.label} for ${batches.length} batches...` 
      });
      
      const mainZip = new JSZip();
      const timestamp = new Date().toISOString().split("T")[0];
      const reportsFolder = mainZip.folder(`All_Reports_${selectedAllReportType.value}_${timestamp}`);
      let successCount = 0, failCount = 0;
      const failedBatches = [];
      
      await asyncPool(3, batches, async (batch) => {
        try {
          const reportBlob = await fetchSingleBatchReportDirectly(batch, selectedAllReportType.value);
          if (!reportBlob) {
            failedBatches.push(batch.batchName);
            failCount++;
            return;
          }
          const batchFolder = reportsFolder.folder(`${batch.batchName || batch.id}_${batch.id}`);
          batchFolder.file(`${selectedAllReportType.value}_Report.zip`, reportBlob);
          successCount++;
        } catch (error) {
          failedBatches.push(batch.batchName);
          failCount++;
        }
      });
      
      const summaryReport = [
        "DOWNLOAD SUMMARY",
        "================",
        `Report Type: ${selectedAllReportType.label}`,
        `Date: ${new Date().toLocaleString()}`,
        `Total Batches: ${batches.length}`,
        `Successfully Downloaded: ${successCount}`,
        `Failed: ${failCount}`,
        "",
        failCount > 0 ? "FAILED BATCHES:" : "All batches downloaded successfully!",
        ...failedBatches.map((name, i) => `${i + 1}. ${name}`)
      ].join("\n");
      
      reportsFolder.file("Download_Summary.txt", summaryReport);
      setFetchProgress({ active: false, current: 0, total: 0, message: "" });
      
      const finalZipBlob = await mainZip.generateAsync({ 
        type: "blob", 
        compression: "DEFLATE", 
        compressionOptions: { level: 6 } 
      });
      
      const downloadUrl = window.URL.createObjectURL(finalZipBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `All_Batches_${selectedAllReportType.value}_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      if (failCount === 0) {
        toast.success(`All ${successCount} reports downloaded successfully!`);
      } else {
        toast.warning(`${successCount} downloaded, ${failCount} failed. Check summary for details.`);
      }
    } catch (error) {
      toast.error("Error downloading reports");
    } finally {
      setLoader(false);
    }
  };

  // ============================
  // REFRESH DATA
  // ============================
  const refreshData = () => {
    CACHE.clear();
    getAllBatches();
    toast.info("Refreshing data from server...");
  };

  // ============================
  // EFFECTS
  // ============================
  useEffect(() => {
    getAllBatches();
  }, [getAllBatches]);

  useEffect(() => {
    if (allBatch.length > 0) {
      applyFilters();
    }
  }, [allBatch, selectedState, selectedDepot, datePeriod, customStartDate, customEndDate, searchText, applyFilters]);

  // ============================
  // COMPUTED VALUES
  // ============================
  const getFilteredFailedStudents = () => {
    const filteredBatchIds = new Set(filteredBatches.map(b => b.id));
    return failedStudentsList.filter(s => filteredBatchIds.has(s.batchId));
  };

  const getFilteredNotAttemptedStudents = () => {
    const filteredBatchIds = new Set(filteredBatches.map(b => b.id));
    return notAttemptedStudentsList.filter(s => filteredBatchIds.has(s.batchId));
  };

  const getFilteredPassedStudents = () => {
    const filteredBatchIds = new Set(filteredBatches.map(b => b.id));
    return passedStudentsList.filter(s => filteredBatchIds.has(s.batchId));
  };

  const totalStudents = getFilteredPassedStudents().length + getFilteredFailedStudents().length + getFilteredNotAttemptedStudents().length;
  const totalPassed = getFilteredPassedStudents().length;
  const totalFailed = getFilteredFailedStudents().length;
  const totalNotAttempted = getFilteredNotAttemptedStudents().length;
  const overallPassRate = totalStudents > 0 ? ((totalPassed / totalStudents) * 100).toFixed(1) : 0;

  const rows = filteredBatches.map((batch, idx) => {
    const stats = passFailStatsMap[batch.id] || { passed: 0, failed: 0, notAttempted: 0 };
    const trainers = batchTrainersMap[batch.id] || [];
    return {
      id: batch.id,
      serialNo: idx + 1,
      batchName: batch.batchName || "N/A",
      department: batch.department || "N/A",
      trainers: trainers.map(t => t.name || `${t.firstName || ""} ${t.lastName || ""}`.trim()),
      startDate: formatDateToDDMMYYYY(batch.trainingStartDate),
      endDate: formatDateToDDMMYYYY(batch.trainingEndDate),
      state: batch.state || "N/A",
      depot: batch.depot || "N/A",
      studentsCount: batchStudentsCount[batch.id] || 0,
      passedCount: stats.passed || 0,
      failedCount: stats.failed || 0,
      notAttemptedCount: stats.notAttempted || 0,
      passPercentage: batchStudentsCount[batch.id] ? ((stats.passed / batchStudentsCount[batch.id]) * 100).toFixed(1) : 0,
      originalBatch: batch,
    };
  });

  // ============================
  // RENDER
  // ============================
  return (
    <>
      {loader && <Loader />}
      
      {backgroundLoading && !loader && (
        <div style={{ 
          position: "fixed", bottom: "20px", right: "20px", zIndex: 9999, 
          backgroundColor: "#28a745", color: "white", padding: "8px 16px", 
          borderRadius: "20px", fontSize: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          display: "flex", alignItems: "center", gap: "8px"
        }}>
          <Spinner size="sm" style={{ color: "white" }} />
          <span>Updating data in background...</span>
        </div>
      )}
      
      {isCacheUsed && !backgroundLoading && !loader && (
        <div style={{ 
          position: "fixed", bottom: "20px", right: "20px", zIndex: 9999, 
          backgroundColor: "#17a2b8", color: "white", padding: "6px 12px", 
          borderRadius: "20px", fontSize: "11px", boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
          cursor: "pointer"
        }} 
        onClick={() => toast.info("Data loaded from cache. Click Refresh to get latest data.", { autoClose: 3000 })}>
          <i className="fas fa-database me-1"></i> Cached Data
        </div>
      )}

      {fetchProgress.active && (
        <div style={{ 
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, 
          backgroundColor: "rgba(255,255,255,0.95)", padding: "15px 20px", 
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
        }}>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-bold text-primary">
              <i className="fas fa-spinner fa-spin me-2"></i>
              {fetchProgress.message}
            </span>
            <span className="text-muted">{fetchProgress.current}/{fetchProgress.total}</span>
          </div>
          <Progress value={fetchProgress.total > 0 ? (fetchProgress.current / fetchProgress.total) * 100 : 0} />
        </div>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
            <CardBody className="text-center p-2">
              <GroupsIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} />
              <h6 className="mb-0">Filtered Batches</h6>
              <h2 className="mb-0 fw-bold">{filteredBatches.length}</h2>
            </CardBody>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)", color: "white" }}>
            <CardBody className="text-center p-2">
              <SchoolIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} />
              <h6 className="mb-0">Total Students</h6>
              <h2 className="mb-0 fw-bold">{totalStudents}</h2>
            </CardBody>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", color: "white", cursor: "pointer" }} onClick={() => setShowFailedModal(true)}>
            <CardBody className="text-center p-2">
              <ThumbDownIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} />
              <h6 className="mb-0">Failed Students</h6>
              <h2 className="mb-0 fw-bold">{totalFailed}</h2>
              <small>Click to view</small>
            </CardBody>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)", color: "white", cursor: "pointer" }} onClick={() => setShowNotAttemptedModal(true)}>
            <CardBody className="text-center p-2">
              <RemoveCircleIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} />
              <h6 className="mb-0">Not Attempted</h6>
              <h2 className="mb-0 fw-bold">{totalNotAttempted}</h2>
              <small>Click to view</small>
            </CardBody>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #48bb78 0%, #2c7a4d 100%)", color: "white", cursor: "pointer" }} onClick={() => setShowPassedModal(true)}>
            <CardBody className="text-center p-2">
              <ThumbUpIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} />
              <h6 className="mb-0">Passed Students</h6>
              <h2 className="mb-0 fw-bold">{totalPassed}</h2>
              <small>Click to view</small>
            </CardBody>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #4299e1 0%, #2b6cb0 100%)", color: "white" }}>
            <CardBody className="text-center p-2">
              <TrendingUpIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} />
              <h6 className="mb-0">Pass Rate</h6>
              <h2 className="mb-0 fw-bold">{overallPassRate}%</h2>
              <small>{totalPassed} out of {totalStudents}</small>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Card className="mt-2">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <h5 className="card-title mb-0 d-flex align-items-center">
                <GroupsIcon style={{ marginRight: "10px", color: "#1e3c72" }} />
                Batch Details Dashboard
                {isTrainer() && <Badge color="info" className="ms-2">Trainer View</Badge>}
                {isDepotManager() && <Badge color="warning" className="ms-2">Depot Manager View</Badge>}
              </h5>
              <div className="mt-2">
                <Badge color="primary" className="me-2 p-2"><span className="fw-bold">{filteredBatches.length}</span> Batches</Badge>
                <Badge color="success" className="me-2 p-2"><span className="fw-bold">{totalStudents}</span> Students</Badge>
                <Badge color="success" className="me-2 p-2"><span className="fw-bold">{totalPassed}</span> Passed</Badge>
                <Badge color="danger" className="me-2 p-2" style={{ cursor: "pointer" }} onClick={() => setShowFailedModal(true)}>
                  <span className="fw-bold">{totalFailed}</span> Failed
                </Badge>
                <Badge color="warning" className="me-2 p-2" style={{ cursor: "pointer" }} onClick={() => setShowNotAttemptedModal(true)}>
                  <span className="fw-bold">{totalNotAttempted}</span> Not Attempted
                </Badge>
                <Badge color="info" className="p-2"><span className="fw-bold">{overallPassRate}%</span> Pass Rate</Badge>
              </div>
            </div>
            <div className="d-flex gap-2 flex-wrap">
              <Input 
                type="text" 
                placeholder="🔍 Search..." 
                value={searchText} 
                onChange={(e) => setSearchText(e.target.value)} 
                style={{ width: "250px" }} 
                className="border-primary" 
              />
              <Button color="info" size="sm" onClick={downloadAllBatchesExcel}>
                <i className="fas fa-download me-1"></i> Export Excel
              </Button>
              <Button color="success" size="sm" onClick={() => setAllReportsModalShow(true)}>
                <i className="fas fa-file-archive me-1"></i> Download All
              </Button>
              <Button color="primary" size="sm" onClick={refreshData}>
                <RefreshIcon style={{ fontSize: "1rem" }} /> Refresh
              </Button>
            </div>
          </div>

          {/* Charts Section */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="shadow-sm h-100 border-0">
                <CardBody>
                  <h6 className="mb-3 d-flex align-items-center">
                    <PieChartIcon style={{ marginRight: "8px", color: "#1e3c72" }} /> 
                    Student Status Distribution
                  </h6>
                  {statusDistributionChart && (
                    <Chart options={statusDistributionChart.options} series={statusDistributionChart.series} type="donut" height={300} />
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="shadow-sm h-100 border-0">
                <CardBody>
                  <h6 className="mb-3 d-flex align-items-center">
                    <BarChartIcon style={{ marginRight: "8px", color: "#1e3c72" }} /> 
                    Top 10 Batches by Student Count
                  </h6>
                  {topBatchesChart && (
                    <Chart options={topBatchesChart.options} series={topBatchesChart.series} type="bar" height={280} />
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="shadow-sm h-100 border-0">
                <CardBody>
                  <h6 className="mb-3 d-flex align-items-center">
                    <TrendingUpIcon style={{ marginRight: "8px", color: "#1e3c72" }} /> 
                    Top 10 Batches by Pass Rate
                  </h6>
                  {batchStatsChart && (
                    <Chart options={batchStatsChart.options} series={batchStatsChart.series} type="bar" height={280} />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Batches Table */}
          <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
            <table className="table table-hover table-sm">
              <thead className="table-primary sticky-top">
                <tr>
                  <th>S.No</th><th>Batch Name</th><th>Department</th><th>Trainer(s)</th>
                  <th>Start Date</th><th>End Date</th><th>State</th><th>Depot</th>
                  <th>Students</th><th>Passed</th><th>Failed</th><th>Not Attempted</th><th>Pass %</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr onClick={() => setExpandedBatch(expandedBatch === row.id ? null : row.id)} style={{ cursor: "pointer" }}>
                      <td className="fw-bold">{row.serialNo}</td>
                      <td><span style={{ fontWeight: 600, color: "#1e3c72" }}>{row.batchName}</span></td>
                      <td>{row.department}</td>
                      <td>
                        {row.trainers[0]}
                        {row.trainers.length > 1 && (
                          <Badge color="secondary" className="ms-1" pill>+{row.trainers.length - 1}</Badge>
                        )}
                      </td>
                      <td>{row.startDate}</td>
                      <td>{row.endDate}</td>
                      <td>{row.state}</td>
                      <td>{row.depot}</td>
                      <td><span className="fw-bold text-primary">{row.studentsCount}</span></td>
                      <td><span className="text-success fw-bold">{row.passedCount}</span></td>
                      <td>
                        <span 
                          className="text-danger fw-bold" 
                          style={{ cursor: "pointer", textDecoration: "underline" }} 
                          onClick={(e) => { e.stopPropagation(); setShowFailedModal(true); }}
                        >
                          {row.failedCount}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="text-warning fw-bold" 
                          style={{ cursor: "pointer", textDecoration: "underline" }} 
                          onClick={(e) => { e.stopPropagation(); setShowNotAttemptedModal(true); }}
                        >
                          {row.notAttemptedCount}
                        </span>
                      </td>
                      <td>
                        <span className={`fw-bold ${parseFloat(row.passPercentage) >= 80 ? "text-success" : parseFloat(row.passPercentage) >= 50 ? "text-warning" : "text-danger"}`}>
                          {row.passPercentage}%
                        </span>
                      </td>
                      <td>
                        <Button size="sm" color="success" onClick={(e) => { e.stopPropagation(); setSelectedBatch(row.originalBatch); setReportModalShow(true); }}>
                          <i className="fas fa-download me-1"></i> Report
                        </Button>
                      </td>
                    </tr>
                    {expandedBatch === row.id && (
                      <tr>
                        <td colSpan="14" className="p-3 bg-light">
                          <div className="row">
                            <div className="col-md-6">
                              <h6>Batch Details</h6>
                              <table className="table table-sm table-borderless">
                                <tbody>
                                  <tr><td><strong>Batch ID:</strong></td><td>{row.id}</td></tr>
                                  <tr><td><strong>Department:</strong></td><td>{row.originalBatch.department || "N/A"}</td></tr>
                                  <tr><td><strong>Created:</strong></td><td>{row.originalBatch.entryAt ? formatDateToDDMMYYYY(row.originalBatch.entryAt) : "N/A"}</td></tr>
                                </tbody>
                              </table>
                            </div>
                            <div className="col-md-6">
                              <h6>Trainers List</h6>
                              {batchTrainersMap[row.id]?.length > 0 ? (
                                <ul className="list-unstyled">
                                  {batchTrainersMap[row.id].map((trainer, idx) => (
                                    <li key={idx}>• {trainer.name || `${trainer.firstName || ""} ${trainer.lastName || ""}`.trim()} ({trainer.email || "No email"})</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className="text-muted">No trainers assigned</span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length === 0 && (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-inbox fa-3x mb-3"></i>
              <p>No batches found</p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Failed Students Modal */}
      <Modal isOpen={showFailedModal} toggle={() => setShowFailedModal(false)} size="xl">
        <ModalHeader toggle={() => setShowFailedModal(false)} style={{ background: "#dc3545", color: "white" }}>
          <ThumbDownIcon style={{ marginRight: "10px" }} /> Failed Students ({totalFailed})
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Button color="danger" size="sm" onClick={() => exportStudentsToExcel(getFilteredFailedStudents(), "Failed_Students")}>
              <i className="fas fa-file-excel me-2"></i> Export to Excel
            </Button>
          </div>
          <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
            <table className="table table-bordered table-hover">
              <thead className="table-danger">
                <tr>
                  <th>S.No</th><th>Student Name</th><th>Mobile</th><th>Email</th>
                  <th>Post Score</th><th>Student State</th><th>Student Depot</th>
                  <th>Training State</th><th>Training Depot</th><th>Batch Name</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredFailedStudents().map((s, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td><strong>{s.name}</strong></td>
                    <td>{s.mobile || "N/A"}</td>
                    <td>{s.email || "N/A"}</td>
                    <td><Badge color="danger">{s.postTestScore ? `${s.postTestScore}%` : "N/A"}</Badge></td>
                    <td>{s.studentState || "N/A"}</td>
                    <td>{s.studentDepot || "N/A"}</td>
                    <td>{s.trainingState || "N/A"}</td>
                    <td>{s.trainingDepot || "N/A"}</td>
                    <td>{s.batchName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowFailedModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Not Attempted Students Modal */}
      <Modal isOpen={showNotAttemptedModal} toggle={() => setShowNotAttemptedModal(false)} size="xl">
        <ModalHeader toggle={() => setShowNotAttemptedModal(false)} style={{ background: "#ffc107", color: "#856404" }}>
          <RemoveCircleIcon style={{ marginRight: "10px" }} /> Not Attempted Students ({totalNotAttempted})
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Button color="warning" size="sm" onClick={() => exportStudentsToExcel(getFilteredNotAttemptedStudents(), "Not_Attempted_Students")}>
              <i className="fas fa-file-excel me-2"></i> Export to Excel
            </Button>
          </div>
          <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
            <table className="table table-bordered table-hover">
              <thead className="table-warning">
                <tr>
                  <th>S.No</th><th>Student Name</th><th>Mobile</th><th>Email</th>
                  <th>Student State</th><th>Student Depot</th>
                  <th>Training State</th><th>Training Depot</th><th>Batch Name</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredNotAttemptedStudents().map((s, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td><strong>{s.name}</strong></td>
                    <td>{s.mobile || "N/A"}</td>
                    <td>{s.email || "N/A"}</td>
                    <td>{s.studentState || "N/A"}</td>
                    <td>{s.studentDepot || "N/A"}</td>
                    <td>{s.trainingState || "N/A"}</td>
                    <td>{s.trainingDepot || "N/A"}</td>
                    <td>{s.batchName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowNotAttemptedModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Passed Students Modal */}
      <Modal isOpen={showPassedModal} toggle={() => setShowPassedModal(false)} size="xl">
        <ModalHeader toggle={() => setShowPassedModal(false)} style={{ background: "#28a745", color: "white" }}>
          <ThumbUpIcon style={{ marginRight: "10px" }} /> Passed Students ({totalPassed})
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Button color="success" size="sm" onClick={() => exportStudentsToExcel(getFilteredPassedStudents(), "Passed_Students")}>
              <i className="fas fa-file-excel me-2"></i> Export to Excel
            </Button>
          </div>
          <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
            <table className="table table-bordered table-hover">
              <thead className="table-success">
                <tr>
                  <th>S.No</th><th>Student Name</th><th>Mobile</th><th>Email</th>
                  <th>Post Score</th><th>Student State</th><th>Student Depot</th>
                  <th>Training State</th><th>Training Depot</th><th>Batch Name</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredPassedStudents().map((s, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td><strong>{s.name}</strong></td>
                    <td>{s.mobile || "N/A"}</td>
                    <td>{s.email || "N/A"}</td>
                    <td><Badge color="success">{s.postTestScore ? `${s.postTestScore}%` : "N/A"}</Badge></td>
                    <td>{s.studentState || "N/A"}</td>
                    <td>{s.studentDepot || "N/A"}</td>
                    <td>{s.trainingState || "N/A"}</td>
                    <td>{s.trainingDepot || "N/A"}</td>
                    <td>{s.batchName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowPassedModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Single Batch Report Modal */}
      <Modal isOpen={reportModalShow} centered toggle={() => setReportModalShow(false)}>
        <ModalHeader toggle={() => setReportModalShow(false)}>Download Batch Report</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <div className="d-flex align-items-center mb-3 p-3 bg-light rounded">
              <SchoolIcon className="me-2 text-primary" style={{ fontSize: "1.5rem" }} />
              <div>
                <small className="text-muted d-block">Batch Name</small>
                <strong>{selectedBatch?.batchName || "N/A"}</strong>
              </div>
            </div>
            <div className="mb-3">
              <label className="mb-2 fw-bold">Select Report Type:</label>
              <Select 
                options={reportOptions} 
                value={selectedReportType} 
                onChange={setSelectedReportType} 
                placeholder="Choose report type..." 
                isSearchable={false} 
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setReportModalShow(false)}>Cancel</Button>
          <Button color="success" onClick={confirmDownloadReport} disabled={!selectedReportType}>
            <i className="fas fa-download me-2"></i> Download
          </Button>
        </ModalFooter>
      </Modal>

      {/* All Batches Report Modal */}
      <Modal isOpen={allReportsModalShow} centered toggle={() => setAllReportsModalShow(false)} size="lg">
        <ModalHeader toggle={() => setAllReportsModalShow(false)}>Download All Reports</ModalHeader>
        <ModalBody>
          <div className="d-flex gap-3 mb-3 p-3 bg-primary bg-opacity-10 rounded flex-wrap">
            <div><small className="text-muted">Batches</small><div className="fw-bold fs-4">{filteredBatches.length}</div></div>
            <div><small className="text-muted">Students</small><div className="fw-bold fs-4">{totalStudents}</div></div>
            <div><small className="text-muted">Pass Rate</small><div className="fw-bold fs-4">{overallPassRate}%</div></div>
          </div>
          <label className="mb-2 fw-bold">Report Type:</label>
          <Select 
            options={reportOptions} 
            value={selectedAllReportType} 
            onChange={setSelectedAllReportType} 
            placeholder="Choose..." 
            isSearchable={false} 
          />
          {filteredBatches.length > 5 && (
            <div className="mt-3 p-2 bg-warning bg-opacity-10 rounded">
              <small><i className="fas fa-exclamation-triangle text-warning me-1"></i>
                Processing {filteredBatches.length} batches may take a few minutes.
              </small>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setAllReportsModalShow(false)}>Cancel</Button>
          <Button color="success" onClick={confirmDownloadAllReports} disabled={!selectedAllReportType}>
            <i className="fas fa-download me-2"></i> Download All
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

// ============================
// PDF REPORT COMPONENT
// ============================
const TrainingReportPDF = ({ data, dateRange, trainingDistribution, stats, filters }) => {
  const tableData = trainingDistribution
    .filter(item => item.count > 0 && item.depot !== "Unknown Depot" && item.state !== "Unknown State")
    .slice(0, 30);
  const totalCandidates = stats.totalCandidates || 0;
  const nominated = stats.nominated || 0;
  const attempted = stats.attempted || 0;
  const passed = stats.passed || 0;
  const nominationRate = totalCandidates > 0 ? ((nominated / totalCandidates) * 100).toFixed(1) : 0;
  const attemptRate = nominated > 0 ? ((attempted / nominated) * 100).toFixed(1) : 0;
  const passRate = attempted > 0 ? ((passed / attempted) * 100).toFixed(1) : 0;
  const completionRate = data?.batches?.planned > 0 ? ((data.batches.completed / data.batches.planned) * 100).toFixed(1) : 0;

  const getFilterText = () => {
    const parts = [];
    if (filters.selectedState && filters.selectedState.value !== "all") parts.push(`State: ${filters.selectedState.label}`);
    if (filters.selectedDivision && filters.selectedDivision.value !== "all") parts.push(`Division: ${filters.selectedDivision.label}`);
    if (filters.selectedDepot && filters.selectedDepot.value !== "all") parts.push(`Depot: ${filters.selectedDepot.label}`);
    return parts.length > 0 ? parts.join(" • ") : "All Locations";
  };

  return (
    <Document>
      <Page style={pdfStyles.page}>
        <View style={pdfStyles.header}>
          <Text style={pdfStyles.companyName}>TML SMART CITY MOBILITY SOLUTIONS LIMITED</Text>
        </View>
        <Text style={pdfStyles.title}>TRAINING REPORT</Text>
        
        <View style={pdfStyles.infoTable}>
          <View style={pdfStyles.infoRow}>
            <Text style={pdfStyles.infoLabel}>Course Name:</Text>
            <Text style={pdfStyles.infoValue}>{data?.courseName || "Driver/Technical/IT/Safety Training"}</Text>
          </View>
          <View style={pdfStyles.infoRow}>
            <Text style={pdfStyles.infoLabel}>Training Duration:</Text>
            <Text style={pdfStyles.infoValue}>
              From {dateRange.fromDate ? format(new Date(dateRange.fromDate), "dd-MM-yyyy") : "00-00-0000"} 
              To {dateRange.toDate ? format(new Date(dateRange.toDate), "dd-MM-yyyy") : "00-00-0000"}
            </Text>
          </View>
          <View style={pdfStyles.infoRow}>
            <Text style={pdfStyles.infoLabel}>Filter Applied:</Text>
            <Text style={[pdfStyles.infoValue, { fontWeight: "bold", color: "#1e3c72" }]}>{getFilterText()}</Text>
          </View>
          <View style={pdfStyles.infoRow}>
            <Text style={pdfStyles.infoLabel}>Report Generated:</Text>
            <Text style={pdfStyles.infoValue}>{format(new Date(), "dd-MM-yyyy HH:mm")}</Text>
          </View>
        </View>

        <Text style={pdfStyles.sectionTitle}>TRAINING SUMMARY STATISTICS</Text>
        <View style={pdfStyles.statsCard}>
          <View style={pdfStyles.statRow}><Text style={pdfStyles.statLabel}>Total Candidates:</Text><Text style={pdfStyles.statValue}>{totalCandidates.toLocaleString()}</Text></View>
          <View style={pdfStyles.statRow}><Text style={pdfStyles.statLabel}>Nominated in Batches:</Text><Text style={pdfStyles.statValue}>{nominated.toLocaleString()} ({nominationRate}%)</Text></View>
          <View style={pdfStyles.statRow}><Text style={pdfStyles.statLabel}>Actually Attempted Exams:</Text><Text style={pdfStyles.statValue}>{attempted.toLocaleString()} ({attemptRate}%)</Text></View>
          <View style={pdfStyles.statRow}><Text style={pdfStyles.statLabel}>Actually Passed (≥80%):</Text><Text style={pdfStyles.statValue}>{passed.toLocaleString()} ({passRate}%)</Text></View>
          <View style={pdfStyles.statRow}><Text style={pdfStyles.statLabel}>Overall Pass Rate:</Text><Text style={[pdfStyles.statValue, { color: "#2c5282", fontSize: 11 }]}>{passRate}%</Text></View>
        </View>

        <Text style={pdfStyles.subsectionTitle}>Batch Completion Status</Text>
        <View style={pdfStyles.statsCard}>
          <View style={pdfStyles.statRow}><Text style={pdfStyles.statLabel}>Planned Batches:</Text><Text style={pdfStyles.statValue}>{data?.batches?.planned || 0}</Text></View>
          <View style={pdfStyles.statRow}><Text style={pdfStyles.statLabel}>Completed Batches:</Text><Text style={pdfStyles.statValue}>{data?.batches?.completed || 0}</Text></View>
          <View style={pdfStyles.statRow}><Text style={pdfStyles.statLabel}>Ongoing Batches:</Text><Text style={pdfStyles.statValue}>{data?.batches?.ongoing || 0}</Text></View>
          <View style={pdfStyles.statRow}><Text style={pdfStyles.statLabel}>Batch Completion Rate:</Text><Text style={pdfStyles.statValue}>{completionRate}%</Text></View>
        </View>

        <View style={pdfStyles.footer}>
          <Text style={{ fontSize: 8, color: "#666" }}>This is a system-generated report</Text>
          <Text style={{ fontSize: 8, color: "#666" }}>Page 1 of 2</Text>
        </View>
      </Page>

      <Page style={pdfStyles.page}>
        <Text style={pdfStyles.sectionTitle}>TRAINING DISTRIBUTION BY LOCATION</Text>
        {tableData.length > 0 ? (
          <>
            <View style={pdfStyles.distributionTable}>
              <View style={pdfStyles.distHeader}>
                <Text style={[pdfStyles.tableHeaderCell, pdfStyles.colDistSn]}>S.No.</Text>
                <Text style={[pdfStyles.tableHeaderCell, pdfStyles.colDistState]}>State</Text>
                <Text style={[pdfStyles.tableHeaderCell, pdfStyles.colDistDepot]}>Depot</Text>
                <Text style={[pdfStyles.tableHeaderCell, pdfStyles.colDistCategory]}>Training Category</Text>
                <Text style={[pdfStyles.tableHeaderCell, pdfStyles.colDistCount]}>Count</Text>
              </View>
              {tableData.map((item, index) => (
                <View key={index} style={pdfStyles.distRow}>
                  <Text style={[pdfStyles.tableCell, pdfStyles.colDistSn]}>{index + 1}</Text>
                  <Text style={[pdfStyles.tableCell, pdfStyles.colDistState]}>{item.state || "N/A"}</Text>
                  <Text style={[pdfStyles.tableCell, pdfStyles.colDistDepot]}>{item.depot || "N/A"}</Text>
                  <Text style={[pdfStyles.tableCell, pdfStyles.colDistCategory]}>{item.category || "General Training"}</Text>
                  <Text style={[pdfStyles.tableCell, pdfStyles.colDistCount, { fontWeight: "bold" }]}>{item.count}</Text>
                </View>
              ))}
            </View>
          </>
        ) : (
          <View style={{ padding: 30, backgroundColor: "#f8f9fa", borderWidth: 1, borderColor: "#dee2e6", marginBottom: 20 }}>
            <Text style={{ fontSize: 11, textAlign: "center" }}>No training distribution data available</Text>
          </View>
        )}

        <Text style={[pdfStyles.sectionTitle, { marginTop: 20 }]}>CERTIFICATION</Text>
        <View style={[pdfStyles.statsCard, { backgroundColor: "#f0f7ff" }]}>
          <Text style={{ fontSize: 10, marginBottom: 10 }}>
            This is to certify that <Text style={{ fontWeight: "bold" }}>{passed.toLocaleString()}</Text> participants 
            have successfully completed the training program conducted by TML Smart City Mobility Solutions Limited 
            during {dateRange.fromDate ? ` ${format(new Date(dateRange.fromDate), "dd MMM yyyy")}` : ""} 
            to {dateRange.toDate ? format(new Date(dateRange.toDate), "dd MMM yyyy") : ""}.
          </Text>
          <Text style={{ fontSize: 9, marginTop: 10 }}>
            <Text style={{ fontWeight: "bold" }}>Overall Pass Rate: {passRate}%</Text>
          </Text>
        </View>

        <View style={pdfStyles.footer}>
          <View style={pdfStyles.signatureBox}>
            <Text style={{ fontSize: 9 }}>Training Manager: ______________________</Text>
            <Text style={pdfStyles.signatureLine}>Authorized Signatory</Text>
          </View>
          <View style={pdfStyles.signatureBox}>
            <Text style={{ fontSize: 9 }}>Date: {format(new Date(), "dd/MM/yyyy")}</Text>
            <Text style={{ fontSize: 9, marginTop: 25 }}>(System Generated Report)</Text>
          </View>
        </View>
        <View style={pdfStyles.pageNumber}><Text>Page 2 of 2</Text></View>
      </Page>
    </Document>
  );
};

// ============================
// MAIN DASHBOARD COMPONENT
// ============================
const PowerBIDashboard = () => {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [divisionText, setDivisionText] = useState("");
  const [selectedState, setSelectedState] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedDepot, setSelectedDepot] = useState(null);
  const [datePeriod, setDatePeriod] = useState("ALL");
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateRangeActive, setIsCustomDateRangeActive] = useState(false);
  const [showDateRangeModal, setShowDateRangeModal] = useState(false);
  const [stateOptions, setStateOptions] = useState([{ value: "all", label: "All States" }]);
  const [divisionOptions, setDivisionOptions] = useState([{ value: "all", label: "All Divisions" }]);
  const [depotOptions, setDepotOptions] = useState([{ value: "all", label: "All Depots" }]);
  const [allStatesList, setAllStatesList] = useState([]);
  const [allDivisionsList, setAllDivisionsList] = useState([]);
  const [allDepotsByState, setAllDepotsByState] = useState({});
  const [allDepotsByDivision, setAllDepotsByDivision] = useState({});
  const [totalDepotsCount, setTotalDepotsCount] = useState(0);
  const [loader, setLoader] = useState(true);
  const [chartsData, setChartsData] = useState([]);
  const [batchChartSeries, setBatchChartSeries] = useState([]);
  const [batchChartOptions, setBatchChartOptions] = useState({});
  const [liveData, setLiveData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [trainingDistribution, setTrainingDistribution] = useState([]);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const [allTrainings, setAllTrainings] = useState([]);
  const [authChecked, setAuthChecked] = useState(false);
  const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("analytics");
  const [stats, setStats] = useState({ totalCandidates: 0, nominated: 0, attempted: 0, passed: 0 });
  const [isCacheUsed, setIsCacheUsed] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const isAuthenticated = useRef(false);
  const initialFetchDone = useRef(false);
  const locationDataCache = useRef(null);
  const trainingDataCache = useRef(null);

  // Get user role
  const userRole = getUserRole();
  const currentUser = getCurrentUser();
  const userDepot = currentUser?.depot || currentUser?.depotName || "";

  const periodOptions = [
    { value: "ALL", label: "All Time" },
    { value: "TODAY", label: "Today" },
    { value: "WEEKLY", label: "This Week" },
    { value: "MONTHLY", label: "This Month" },
    { value: "YEARLY", label: "This Year" },
  ];

  // ============================
  // CHECK AUTHENTICATION
  // ============================
  useEffect(() => {
    const checkAuth = () => {
      const authUser = localStorage.getItem("authUser");
      isAuthenticated.current = !!authUser;
      setAuthChecked(true);
    };
    checkAuth();
    window.addEventListener("storage", checkAuth);
    return () => window.removeEventListener("storage", checkAuth);
  }, []);

  // ============================
  // FETCH STUDENT DATA AND EXTRACT FILTERS
  // ============================
  const fetchStudentDataAndExtractFilters = useCallback(async (forceRefresh = false) => {
    const authUser = localStorage.getItem("authUser");
    if (!authUser) return null;
    
    // Check cache
    if (!forceRefresh && locationDataCache.current) return locationDataCache.current;
    
    try {
      setDistributionLoading(true);
      
      // Check localStorage cache
      const cachedLocationData = CACHE.get(CACHE.LOCATION_DATA);
      if (cachedLocationData && !forceRefresh) {
        locationDataCache.current = cachedLocationData;
        return cachedLocationData;
      }
      
      const response = await getAllStudentsForTata();
      let students = response?.success ? (response?.result || response?.data || []) : [];
      
      // Filter students based on role
      if (isDepotManager() && userDepot) {
        students = students.filter(s => {
          const depot = s.tataStudentDetailsDto?.depot || s.depot || s.branch;
          return depot === userDepot;
        });
      }
      
      const locationFilters = extractLocationFilters(students);
      locationDataCache.current = locationFilters;
      
      // Save to cache
      CACHE.set(CACHE.LOCATION_DATA, locationFilters);
      
      return locationFilters;
    } catch (error) {
      console.error("Error fetching student data:", error);
      if (error?.response?.status === 401) {
        localStorage.removeItem("authUser");
        isAuthenticated.current = false;
        navigate("/login");
      }
      return null;
    } finally {
      setDistributionLoading(false);
    }
  }, [navigate, userDepot]);

  // ============================
  // FETCH TRAINING DATA
  // ============================
  const fetchTrainingData = useCallback(async (forceRefresh = false) => {
    const authUser = localStorage.getItem("authUser");
    if (!authUser) return [];
    
    // Check cache
    if (!forceRefresh && trainingDataCache.current) return trainingDataCache.current;
    
    try {
      // Check localStorage cache
      const cachedTrainingData = CACHE.get(CACHE.TRAINING_DATA);
      if (cachedTrainingData && !forceRefresh) {
        trainingDataCache.current = cachedTrainingData;
        return cachedTrainingData;
      }
      
      const response = await fetchAllTraining();
      if (response?.success) {
        let trainings = response?.result || response?.data || [];
        
        // Filter trainings based on role
        if (isDepotManager() && userDepot) {
          trainings = trainings.filter(t => t.depot === userDepot);
        }
        
        trainingDataCache.current = trainings;
        CACHE.set(CACHE.TRAINING_DATA, trainings);
        return trainings;
      }
      return [];
    } catch (error) {
      console.error("Error fetching training data:", error);
      if (error?.response?.status === 401) {
        localStorage.removeItem("authUser");
        isAuthenticated.current = false;
        navigate("/login");
      }
      return [];
    }
  }, [navigate, userDepot]);

  // ============================
  // UPDATE DEPOT OPTIONS
  // ============================
  const updateDepotOptions = useCallback(() => {
    let depotOpts = [];
    if (selectedState && selectedState.value !== "all" && allDepotsByState[selectedState.label]) {
      depotOpts = [{ value: "all", label: "All Depots" }, ...allDepotsByState[selectedState.label].map(d => ({ value: d.toLowerCase().replace(/\s+/g, "_"), label: d }))];
    } else if (selectedDivision && selectedDivision.value !== "all" && allDepotsByDivision[selectedDivision.label]) {
      depotOpts = [{ value: "all", label: "All Depots" }, ...allDepotsByDivision[selectedDivision.label].map(d => ({ value: d.toLowerCase().replace(/\s+/g, "_"), label: d }))];
    } else {
      const allDepots = [...new Set(Object.values(allDepotsByState).flat().sort())];
      depotOpts = [{ value: "all", label: "All Depots" }, ...allDepots.map(d => ({ value: d.toLowerCase().replace(/\s+/g, "_"), label: d }))];
    }
    setDepotOptions(depotOpts);
  }, [selectedState, selectedDivision, allDepotsByState, allDepotsByDivision]);

  useEffect(() => {
    updateDepotOptions();
  }, [selectedState, selectedDivision, updateDepotOptions]);

  // ============================
  // INITIALIZE LOCATION FILTERS
  // ============================
  const initializeLocationFilters = useCallback(async (forceRefresh = false) => {
    const locationFilters = await fetchStudentDataAndExtractFilters(forceRefresh);
    if (locationFilters) {
      setAllStatesList(locationFilters.statesList);
      setAllDivisionsList(locationFilters.divisionsList);
      setAllDepotsByState(locationFilters.depotsByState);
      setAllDepotsByDivision(locationFilters.depotsByDivision);
      setTotalDepotsCount(locationFilters.totalDepotsCount);
      setStateOptions([{ value: "all", label: "All States" }, ...locationFilters.statesList.map(s => ({ value: s.toLowerCase().replace(/\s+/g, "_"), label: s }))]);
      setDivisionOptions([{ value: "all", label: "All Divisions" }, ...locationFilters.divisionsList.map(d => ({ value: d.toLowerCase().replace(/\s+/g, "_"), label: d }))]);
      updateDepotOptions();
    }
  }, [fetchStudentDataAndExtractFilters, updateDepotOptions]);

  // ============================
  // LOAD DASHBOARD DATA
  // ============================
  const loadDashboardData = useCallback(async (forceRefresh = false) => {
    if (!isAuthenticated.current) {
      setLoader(false);
      return;
    }
    
    try {
      setLoader(true);
      setRefreshing(forceRefresh);
      
      // Check cache for dashboard data
      if (!forceRefresh) {
        const cachedDashboard = CACHE.get(CACHE.DASHBOARD_DATA);
        if (cachedDashboard) {
          setLiveData(cachedDashboard.liveData);
          setStats(cachedDashboard.stats);
          setChartsData(cachedDashboard.chartsData);
          setBatchChartSeries(cachedDashboard.batchChartSeries);
          setBatchChartOptions(cachedDashboard.batchChartOptions);
          setIsCacheUsed(true);
          setLastRefreshTime(cachedDashboard.timestamp);
          await initializeLocationFilters(false);
          const trainings = await fetchTrainingData(false);
          setAllTrainings(trainings);
          const distribution = processTrainingDistribution(trainings, selectedState, selectedDivision, selectedDepot);
          setTrainingDistribution(distribution);
          setLoader(false);
          return;
        }
      }
      
      await initializeLocationFilters(forceRefresh);
      const trainings = await fetchTrainingData(forceRefresh);
      setAllTrainings(trainings);
      const distribution = processTrainingDistribution(trainings, selectedState, selectedDivision, selectedDepot);
      setTrainingDistribution(distribution);
      
      // Build params for API
      const params = {
        division: divisionText || (selectedDivision && selectedDivision.value !== "all" ? selectedDivision.label : undefined),
        state: selectedState && selectedState.value !== "all" ? selectedState.label : undefined,
        depot: selectedDepot && selectedDepot.value !== "all" ? selectedDepot.label : undefined,
      };
      
      // For Depot Manager, always include depot
      if (isDepotManager() && userDepot) {
        params.depot = userDepot;
      }
      
      const data = await getTataDashboardData(params.division, params.state, params.depot);
      
      if (data?.success) {
        const result = data?.tataResult || data?.result || {};
        const totalStudent = result?.totalStudent || 0;
        const nominatedStudent = result?.nominatedStudent || 0;
        const completedBatch = result?.completedBatch || 0;
        const totalBatch = result?.totalBatch || 0;
        const attemptedStudents = result?.attemptedStudent || 0;
        const passedStudents = result?.passedStudent || 0;
        const totalCandidatesFromDistribution = distribution.reduce((sum, item) => sum + item.count, 0);
        const finalTotalCandidates = totalStudent > 0 ? totalStudent : totalCandidatesFromDistribution;
        
        const newStats = {
          totalCandidates: finalTotalCandidates,
          nominated: nominatedStudent,
          attempted: attemptedStudents,
          passed: passedStudents
        };
        setStats(newStats);
        
        const newLiveData = {
          courseName: result?.courseName || "Driver/Technical/IT/Safety Training",
          project: result?.project || "Smart City Mobility",
          batches: { planned: totalBatch, completed: completedBatch, ongoing: totalBatch - completedBatch },
          stats: { totalCandidates: finalTotalCandidates, nominated: nominatedStudent, attempted: attemptedStudents, passed: passedStudents, passPercentage: attemptedStudents > 0 ? ((passedStudents / attemptedStudents) * 100).toFixed(1) : 0 }
        };
        setLiveData(newLiveData);
        
        const newChartsData = [
          { ...makeChart("Total Candidates vs Nominated", ["Total", "Nominated"], [finalTotalCandidates || 0, nominatedStudent || 0], "bar", ["#4299e1", "#48bb78"]), series: [{ name: "Count", data: [finalTotalCandidates || 0, nominatedStudent || 0] }] },
          { ...makeChart("Nominated vs Attempted", ["Nominated", "Attempted"], [nominatedStudent || 0, attemptedStudents || 0], "bar", ["#ed8936", "#ecc94b"]), series: [{ name: "Count", data: [nominatedStudent || 0, attemptedStudents || 0] }] },
          { ...makeChart("Attempted vs Passed", ["Attempted", "Passed"], [attemptedStudents || 0, passedStudents || 0], "bar", ["#ecc94b", "#48bb78"]), series: [{ name: "Count", data: [attemptedStudents || 0, passedStudents || 0] }] },
          { ...makeChart("Pass Rate Analysis", ["Pass Rate"], [attemptedStudents > 0 ? (passedStudents / attemptedStudents * 100) : 0], "bar", ["#48bb78"]), series: [{ name: "Pass Rate %", data: [attemptedStudents > 0 ? (passedStudents / attemptedStudents * 100) : 0] }] }
        ];
        setChartsData(newChartsData);
        
        const batchSeries = [{ name: "Batches", data: [totalBatch || 0, completedBatch || 0, totalBatch - completedBatch || 0] }];
        setBatchChartSeries(batchSeries);
        setBatchChartOptions({
          ...makeChart("Batch Completion Status", ["Planned", "Completed", "Ongoing"], [totalBatch || 0, completedBatch || 0, totalBatch - completedBatch || 0], "bar", ["#4299e1", "#48bb78", "#ecc94b"]),
          chart: { type: "bar" }
        });
        
        // Save to cache
        CACHE.set(CACHE.DASHBOARD_DATA, {
          liveData: newLiveData,
          stats: newStats,
          chartsData: newChartsData,
          batchChartSeries: batchSeries,
          batchChartOptions: {
            ...makeChart("Batch Completion Status", ["Planned", "Completed", "Ongoing"], [totalBatch || 0, completedBatch || 0, totalBatch - completedBatch || 0], "bar", ["#4299e1", "#48bb78", "#ecc94b"]),
            chart: { type: "bar" }
          },
          timestamp: Date.now()
        });
        
        setLastRefreshTime(Date.now());
        setIsCacheUsed(false);
      } else {
        setLiveData({
          courseName: "Driver/Technical/IT/Safety Training",
          project: "Smart City Mobility",
          batches: { planned: 0, completed: 0, ongoing: 0 },
          stats: { totalCandidates: 0, nominated: 0, attempted: 0, passed: 0, passPercentage: 0 }
        });
      }
    } catch (error) {
      console.error("Dashboard error:", error);
      if (error?.response?.status === 401) {
        localStorage.removeItem("authUser");
        isAuthenticated.current = false;
        navigate("/login");
      }
      toast.error("Something went wrong loading data.");
    } finally {
      setLoader(false);
      setRefreshing(false);
    }
  }, [
    divisionText, selectedState, selectedDivision, selectedDepot, 
    initializeLocationFilters, fetchTrainingData, navigate, userDepot
  ]);

  // ============================
  // HANDLE PERIOD CHANGE
  // ============================
  const handlePeriodChange = (option) => {
    setDatePeriod(option.value);
    if (option.value === "CUSTOM") {
      setShowDateRangeModal(true);
    } else {
      setIsCustomDateRangeActive(false);
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  // ============================
  // APPLY CUSTOM DATE RANGE
  // ============================
  const applyCustomDateRange = () => {
    if (!customStartDate || !customEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    if (new Date(customStartDate) > new Date(customEndDate)) {
      toast.error("Start date cannot be after end date");
      return;
    }
    setIsCustomDateRangeActive(true);
    setShowDateRangeModal(false);
    setDatePeriod("CUSTOM");
    toast.success("Custom date range applied");
  };

  // ============================
  // CLEAR CUSTOM DATE RANGE
  // ============================
  const clearCustomDateRange = () => {
    setCustomStartDate(null);
    setCustomEndDate(null);
    setIsCustomDateRangeActive(false);
    setDatePeriod("ALL");
  };

  // ============================
  // HANDLE FILTER
  // ============================
  const handleFilter = () => {
    setRefreshing(true);
    loadDashboardData(true);
  };

  // ============================
  // HANDLE REFRESH
  // ============================
  const handleRefresh = () => {
    setRefreshing(true);
    CACHE.clear();
    loadDashboardData(true);
    toast.info("Refreshing all data from server...");
  };

  // ============================
  // RESET FILTERS
  // ============================
  const resetFilters = () => {
    setSelectedState(null);
    setSelectedDivision(null);
    setSelectedDepot(null);
    setDivisionText("");
    setDatePeriod("ALL");
    setCustomStartDate(null);
    setCustomEndDate(null);
    setIsCustomDateRangeActive(false);
    setFromDate(format(subDays(new Date(), 30), "yyyy-MM-dd"));
    setToDate(format(new Date(), "yyyy-MM-dd"));
    loadDashboardData(true);
  };

  // ============================
  // HANDLE PRINT PREVIEW
  // ============================
  const handlePrintPreview = () => setPrintPreviewOpen(true);

  // ============================
  // EXPORT TO EXCEL
  // ============================
  const exportToExcel = () => {
    setExportLoading(true);
    try {
      const filteredDist = trainingDistribution.filter(t => {
        if (selectedState && selectedState.value !== "all" && t.state !== selectedState.label) return false;
        if (selectedDivision && selectedDivision.value !== "all" && t.division !== selectedDivision.label) return false;
        if (selectedDepot && selectedDepot.value !== "all" && t.depot !== selectedDepot.label) return false;
        return true;
      });
      
      const wb = XLSX.utils.book_new();
      const summaryData = [
        ["Training Analytics Report"],
        [`Generated: ${new Date().toLocaleString()}`],
        [`Date Period: ${datePeriod}${isCustomDateRangeActive && customStartDate ? ` (${formatDateToDDMMYYYY(customStartDate)} to ${formatDateToDDMMYYYY(customEndDate)})` : ""}`],
        [`State: ${selectedState?.label || "All"}`],
        [`Division: ${selectedDivision?.label || "All"}`],
        [`Depot: ${selectedDepot?.label || "All"}`],
        [],
        ["Candidate Statistics"],
        ["Metric", "Count", "Percentage"],
        ["Total Candidates", stats.totalCandidates, "100%"],
        ["Nominated", stats.nominated, stats.totalCandidates ? `${((stats.nominated / stats.totalCandidates) * 100).toFixed(1)}%` : "0%"],
        ["Attempted", stats.attempted, stats.nominated ? `${((stats.attempted / stats.nominated) * 100).toFixed(1)}%` : "0%"],
        ["Passed (≥80%)", stats.passed, stats.attempted ? `${((stats.passed / stats.attempted) * 100).toFixed(1)}%` : "0%"],
        [],
        ["Training Distribution"],
        ["S.No", "State", "Depot", "Category", "Count"],
        ...filteredDist.map((t, i) => [i + 1, t.state, t.depot, t.category, t.count]),
      ];
      
      const ws = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, ws, "Training Report");
      XLSX.writeFile(wb, `Training_Report_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
      toast.success("Excel exported!");
    } catch (error) {
      toast.error("Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  // ============================
  // EFFECTS
  // ============================
  useEffect(() => {
    if (authChecked && isAuthenticated.current && !initialFetchDone.current) {
      initialFetchDone.current = true;
      loadDashboardData();
    }
  }, [authChecked, loadDashboardData]);

  // ============================
  // RENDER
  // ============================
  if (loader && !refreshing) return <Loader />;
  
  if (!isAuthenticated.current && authChecked) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
        <Card style={{ width: "400px" }}>
          <CardBody className="text-center p-4">
            <h4 className="mb-3">Authentication Required</h4>
            <p className="text-muted mb-4">Please log in to view the dashboard.</p>
            <Button color="primary" onClick={() => navigate("/login")}>Go to Login</Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Get role badge
  const getRoleBadge = () => {
    if (isTrainer()) return <Badge color="info" className="ms-2">Trainer View</Badge>;
    if (isDepotManager()) return <Badge color="warning" className="ms-2">Depot Manager: {userDepot}</Badge>;
    return null;
  };

  return (
    <Row>
      <Col xl="12">
        <Card>
          <CardBody>
            {/* Tab Navigation */}
            <div className="d-flex border-bottom mb-4">
              <button 
                className={`btn btn-link ${activeTab === "analytics" ? "active" : ""}`} 
                onClick={() => setActiveTab("analytics")} 
                style={{ 
                  textDecoration: "none", 
                  fontWeight: activeTab === "analytics" ? "bold" : "normal", 
                  borderBottom: activeTab === "analytics" ? "2px solid #1e3c72" : "none", 
                  color: activeTab === "analytics" ? "#1e3c72" : "#6c757d" 
                }}
              >
                <BarChartIcon style={{ fontSize: "1rem", marginRight: "8px", verticalAlign: "middle" }} /> 
                Training Analytics
              </button>
              <button 
                className={`btn btn-link ${activeTab === "batchDetails" ? "active" : ""}`} 
                onClick={() => setActiveTab("batchDetails")} 
                style={{ 
                  textDecoration: "none", 
                  fontWeight: activeTab === "batchDetails" ? "bold" : "normal", 
                  borderBottom: activeTab === "batchDetails" ? "2px solid #1e3c72" : "none", 
                  color: activeTab === "batchDetails" ? "#1e3c72" : "#6c757d" 
                }}
              >
                <GroupsIcon style={{ fontSize: "1rem", marginRight: "8px", verticalAlign: "middle" }} /> 
                Batch Details
              </button>
            </div>

            {/* Training Analytics Tab */}
            {activeTab === "analytics" && (
              <>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="card-title mb-0">
                      Training Analytics Dashboard
                      {getRoleBadge()}
                    </h4>
                    <small className="text-muted">
                      <WarehouseIcon style={{ fontSize: "1rem", marginRight: "4px", verticalAlign: "middle" }} /> 
                      Database: {allStatesList.length} States • {allDivisionsList.length} Divisions • {totalDepotsCount} Depots
                      {isCacheUsed && (
                        <span className="ms-2 text-primary">
                          <i className="fas fa-database me-1"></i> Cached
                        </span>
                      )}
                      {lastRefreshTime && (
                        <span className="ms-2">
                          <i className="fas fa-clock me-1"></i> 
                          Updated: {new Date(lastRefreshTime).toLocaleTimeString()}
                        </span>
                      )}
                    </small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button color="light" onClick={handleRefresh} disabled={refreshing}>
                      {refreshing ? (
                        <><Spinner size="sm" className="me-2" /> Refreshing...</>
                      ) : (
                        <><RefreshIcon style={{ fontSize: "1rem", marginRight: "8px" }} /> Refresh</>
                      )}
                    </Button>
                  </div>
                </div>

                {stats.totalCandidates > 0 ? (
                  <Alert color="success" className="mb-4">
                    <i className="mdi mdi-check-circle me-2"></i>
                    <strong>Live Data Loaded</strong> • {stats.nominated} nominated • {stats.attempted} attempted • {stats.passed} passed • 
                    {stats.attempted > 0 ? `${((stats.passed / stats.attempted) * 100).toFixed(1)}%` : "0%"} pass rate
                    {isCacheUsed && <span className="ms-2 text-muted">(from cache)</span>}
                  </Alert>
                ) : (
                  <Alert color="warning" className="mb-4">
                    <i className="mdi mdi-alert-circle me-2"></i>
                    <strong>No Data Available</strong> - Database: {allStatesList.length} states, {allDivisionsList.length} divisions, {totalDepotsCount} depots
                  </Alert>
                )}

                <Row className="mb-4 align-items-end">
                  <Col md={2}>
                    <Label>From Date</Label>
                    <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={toDate} />
                  </Col>
                  <Col md={2}>
                    <Label>To Date</Label>
                    <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} min={fromDate} max={format(new Date(), "yyyy-MM-dd")} />
                  </Col>
                  <Col md={2}>
                    <Label>Division (Text)</Label>
                    <Input type="text" placeholder="Enter Division" value={divisionText} onChange={(e) => setDivisionText(e.target.value)} />
                  </Col>
                  <Col md={2}>
                    <Label><LocationOnIcon style={{ fontSize: "1rem", marginRight: "4px", verticalAlign: "middle" }} /> State ({allStatesList.length})</Label>
                    <Select 
                      options={stateOptions} 
                      value={selectedState} 
                      onChange={setSelectedState} 
                      placeholder="Select State" 
                      isClearable 
                      isLoading={distributionLoading} 
                    />
                  </Col>
                  <Col md={2}>
                    <Label><FilterListIcon style={{ fontSize: "1rem", marginRight: "4px", verticalAlign: "middle" }} /> Division ({allDivisionsList.length})</Label>
                    <Select 
                      options={divisionOptions} 
                      value={selectedDivision} 
                      onChange={setSelectedDivision} 
                      placeholder="Select Division" 
                      isClearable 
                      isLoading={distributionLoading} 
                    />
                  </Col>
                  <Col md={2}>
                    <div className="d-flex gap-2 mt-4">
                      <Button color="primary" onClick={handleFilter} disabled={refreshing || !fromDate || !toDate} style={{ flex: 1 }}>
                        {refreshing ? <Spinner size="sm" /> : "Apply"}
                      </Button>
                      <Button color="secondary" onClick={resetFilters} disabled={refreshing}>
                        <i className="mdi mdi-refresh"></i>
                      </Button>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <Label>
                      <WarehouseIcon style={{ fontSize: "1rem", marginRight: "4px", verticalAlign: "middle" }} /> Depot
                      <small className="text-muted ms-1">
                        {selectedState?.value !== "all" && selectedState ? 
                          `${allDepotsByState[selectedState.label]?.length || 0} in ${selectedState.label}` : 
                          selectedDivision?.value !== "all" && selectedDivision ? 
                          `${allDepotsByDivision[selectedDivision.label]?.length || 0} in ${selectedDivision.label}` : 
                          `${totalDepotsCount} total`}
                      </small>
                    </Label>
                    <Select 
                      options={depotOptions} 
                      value={selectedDepot} 
                      onChange={setSelectedDepot} 
                      placeholder="Select Depot" 
                      isClearable 
                      isLoading={distributionLoading} 
                    />
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={3}>
                    <Card className="border">
                      <CardBody className="p-3 text-center">
                        <h6 className="text-muted mb-1">Total Candidates</h6>
                        <h3 className="mb-0">{stats.totalCandidates.toLocaleString()}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="border">
                      <CardBody className="p-3 text-center">
                        <h6 className="text-muted mb-1">Nominated</h6>
                        <h3 className="mb-0 text-success">{stats.nominated.toLocaleString()}</h3>
                        <small className="text-muted">{stats.totalCandidates ? `${((stats.nominated / stats.totalCandidates) * 100).toFixed(1)}%` : "0%"}</small>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="border">
                      <CardBody className="p-3 text-center">
                        <h6 className="text-muted mb-1">Attempted</h6>
                        <h3 className="mb-0 text-warning">{stats.attempted.toLocaleString()}</h3>
                        <small className="text-muted">{stats.nominated ? `${((stats.attempted / stats.nominated) * 100).toFixed(1)}%` : "0%"}</small>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="border">
                      <CardBody className="p-3 text-center">
                        <h6 className="text-muted mb-1">Passed (≥80%)</h6>
                        <h3 className="mb-0 text-primary">{stats.passed.toLocaleString()}</h3>
                        <small className="text-muted">{stats.attempted ? `${((stats.passed / stats.attempted) * 100).toFixed(1)}%` : "0%"} pass rate</small>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <h5 className="mt-4">Batch Completion Status</h5>
                    <div className="apex-charts" dir="ltr">
                      {batchChartSeries.length > 0 ? (
                        <Chart options={batchChartOptions} series={batchChartSeries} type="bar" height={320} />
                      ) : (
                        <div className="text-center py-5"><Spinner color="primary" /></div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mt-4">Training Distribution</h5>
                      <small className="text-muted">{trainingDistribution.length} locations</small>
                    </div>
                    <div className="table-responsive" style={{ maxHeight: "350px", overflowY: "auto" }}>
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr><th>Depot</th><th>State</th><th>Category</th><th className="text-end">Count</th></tr>
                        </thead>
                        <tbody>
                          {distributionLoading ? (
                            <tr><td colSpan="4" className="text-center py-4"><Spinner size="sm" /> Loading...</td></tr>
                          ) : trainingDistribution.length > 0 ? (
                            trainingDistribution.slice(0, 10).map((t, i) => (
                              <tr key={i}>
                                <td><i className="mdi mdi-warehouse me-1 text-primary"></i>{t.depot}</td>
                                <td><i className="mdi mdi-map-marker me-1 text-success"></i>{t.state}</td>
                                <td><span className="badge bg-light text-dark"><i className="mdi mdi-tag me-1"></i>{t.category}</span></td>
                                <td className="text-end"><strong>{t.count.toLocaleString()}</strong></td>
                              </tr>
                            ))
                          ) : (
                            <tr><td colSpan="4" className="text-center text-muted py-4">No data available</td></tr>
                          )}
                        </tbody>
                        {trainingDistribution.length > 0 && (
                          <tfoot>
                            <tr className="table-light">
                              <th colSpan="3">Total</th>
                              <th className="text-end">{trainingDistribution.reduce((sum, t) => sum + (t.count || 0), 0).toLocaleString()}</th>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </Col>
                </Row>

                <Row className="mt-4">
                  {chartsData.map((chart, i) => (
                    <Col md={6} key={i}>
                      <Card><CardBody><Chart options={chart.options || chart} series={chart.series} type="bar" height={280} /></CardBody></Card>
                    </Col>
                  ))}
                </Row>

                <Row className="mt-3">
                  <Col xl="12">
                    <Card>
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                          <h4 className="card-title mb-0">Reports & Exports</h4>
                          <div className="d-flex gap-2">
                            <Button color="info" onClick={handlePrintPreview} disabled={!stats.totalCandidates}>
                              <PrintIcon style={{ fontSize: "1.1rem", marginRight: "8px" }} /> Print Report
                            </Button>
                            <Button color="success" onClick={exportToExcel} disabled={exportLoading || !stats.totalCandidates}>
                              {exportLoading ? <Spinner size="sm" className="me-2" /> : <i className="mdi mdi-file-excel me-2"></i>}
                              Export Excel
                            </Button>
                            <PDFDownloadLink 
                              document={<TrainingReportPDF 
                                data={liveData} 
                                dateRange={{ fromDate, toDate }} 
                                trainingDistribution={trainingDistribution} 
                                stats={stats} 
                                filters={{ 
                                  division: divisionText, 
                                  selectedState, 
                                  selectedDivision, 
                                  selectedDepot, 
                                  allStatesList, 
                                  allDivisionsList, 
                                  depotsByState: allDepotsByState, 
                                  depotsByDivision: allDepotsByDivision, 
                                  totalDepotsCount 
                                }} 
                              />} 
                              fileName={`TML_Report_${format(new Date(), "yyyy-MM-dd")}.pdf`}
                            >
                              {({ loading }) => (
                                <Button color="danger" disabled={loading || !stats.totalCandidates}>
                                  {loading ? <Spinner size="sm" className="me-2" /> : <PictureAsPdfIcon style={{ fontSize: "1.1rem", marginRight: "8px" }} />}
                                  {loading ? "Generating..." : "Export PDF"}
                                </Button>
                              )}
                            </PDFDownloadLink>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </>
            )}

            {/* Batch Details Tab */}
            {activeTab === "batchDetails" && (
              <BatchDetailsTab 
                datePeriod={datePeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                selectedState={selectedState?.label}
                selectedDepot={selectedDepot?.label}
                selectedDivision={selectedDivision?.label}
              />
            )}
          </CardBody>
        </Card>
      </Col>

      {/* Custom Date Range Modal */}
      <Modal isOpen={showDateRangeModal} centered toggle={() => setShowDateRangeModal(false)}>
        <ModalHeader toggle={() => setShowDateRangeModal(false)}>Select Custom Date Range</ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <Label>Start Date</Label>
              <DatePicker 
                selected={customStartDate} 
                onChange={(date) => setCustomStartDate(date)} 
                className="form-control" 
                dateFormat="dd/MM/yyyy" 
                placeholderText="Select start date" 
              />
            </Col>
            <Col md={6}>
              <Label>End Date</Label>
              <DatePicker 
                selected={customEndDate} 
                onChange={(date) => setCustomEndDate(date)} 
                className="form-control" 
                dateFormat="dd/MM/yyyy" 
                placeholderText="Select end date" 
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowDateRangeModal(false)}>Cancel</Button>
          <Button color="primary" onClick={applyCustomDateRange}>Apply</Button>
        </ModalFooter>
      </Modal>

      {/* Print Preview Modal */}
      <PrintPreviewModal 
        isOpen={printPreviewOpen} 
        toggle={() => setPrintPreviewOpen(false)} 
        data={liveData} 
        dateRange={{ fromDate, toDate }} 
        trainingDistribution={trainingDistribution} 
        stats={stats} 
        filters={{ 
          division: divisionText, 
          selectedState, 
          selectedDivision, 
          selectedDepot, 
          allStatesList, 
          allDivisionsList, 
          depotsByState: allDepotsByState, 
          depotsByDivision: allDepotsByDivision, 
          totalDepotsCount 
        }} 
        chartsData={chartsData} 
      />
    </Row>
  );
};

export default PowerBIDashboard;













// import React, { useEffect, useState, useRef, useCallback } from "react";
// import {
//   Button,
//   Card,
//   CardBody,
//   Col,
//   Input,
//   Label,
//   Row,
//   Spinner,
//   Alert,
//   Badge,
//   Modal,
//   ModalHeader,
//   ModalBody,
//   ModalFooter,
//   Progress,
// } from "reactstrap";
// import {
//   PDFDownloadLink,
//   Document,
//   Page,
//   Text,
//   View,
//   StyleSheet,
//   Font,
// } from "@react-pdf/renderer";
// import Chart from "react-apexcharts";
// import { toast } from "react-toastify";
// import { getTataDashboardData } from "helpers/dashboard_helper";
// import { fetchAllTraining } from "helpers/training_helper";
// import { getAllStudentsForTata } from "helpers/student_helper";
// import Loader from "components/Loader/Loader";
// import { format, subDays } from "date-fns";
// import { saveAs } from "file-saver";
// import * as XLSX from "xlsx";
// import Select from "react-select";
// import DatePicker from "react-datepicker";
// import "react-datepicker/dist/react-datepicker.css";
// import WarehouseIcon from "@mui/icons-material/Warehouse";
// import FilterListIcon from "@mui/icons-material/FilterList";
// import LocationOnIcon from "@mui/icons-material/LocationOn";
// import PrintIcon from "@mui/icons-material/Print";
// import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
// import BarChartIcon from "@mui/icons-material/BarChart";
// import SchoolIcon from "@mui/icons-material/School";
// import GroupsIcon from "@mui/icons-material/Groups";
// import TrendingUpIcon from "@mui/icons-material/TrendingUp";
// import RefreshIcon from "@mui/icons-material/Refresh";
// import ThumbUpIcon from "@mui/icons-material/ThumbUp";
// import ThumbDownIcon from "@mui/icons-material/ThumbDown";
// import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
// import AssessmentIcon from "@mui/icons-material/Assessment";
// import CloseIcon from "@mui/icons-material/Close";
// import PieChartIcon from "@mui/icons-material/PieChart";
// import { useNavigate } from "react-router-dom";
// import moment from "moment";

// // Import batch-related helpers
// import {
//   fetchAllBatch,
//   fetchAllBatchOfTrainer,
//   getAllTrainerOfBatch,
//   getAllStudentOfBatch,
// } from "helpers/batch_helper";
// import { getResult } from "helpers/exam_helper";
// import { downloadBatchReport, url } from "helpers/url_helper";
// import axios from "axios";
// import JSZip from "jszip";

// // Register fonts
// Font.register({
//   family: "Helvetica",
//   fonts: [
//     { src: "https://fonts.gstatic.com/s/helvetica/v23/Helvetica.ttf" },
//     {
//       src: "https://fonts.gstatic.com/s/helvetica/v23/Helvetica-Bold.ttf",
//       fontWeight: "bold",
//     },
//   ],
// });

// // PDF styles for Batch Details Report
// const batchReportStyles = StyleSheet.create({
//   page: { padding: 40, fontFamily: "Helvetica", fontSize: 10 },
//   header: {
//     marginBottom: 20,
//     borderBottomWidth: 2,
//     borderBottomColor: "#1e3c72",
//     borderBottomStyle: "solid",
//     paddingBottom: 10,
//   },
//   companyName: {
//     fontSize: 18,
//     fontWeight: "bold",
//     color: "#1e3c72",
//     textAlign: "center",
//   },
//   mission: {
//     fontSize: 11,
//     fontStyle: "italic",
//     textAlign: "center",
//     color: "#555",
//     marginTop: 5,
//   },
//   title: {
//     fontSize: 20,
//     fontWeight: "bold",
//     textAlign: "center",
//     marginVertical: 15,
//     color: "#1e3c72",
//   },
//   sectionTitle: {
//     fontSize: 14,
//     fontWeight: "bold",
//     backgroundColor: "#e6eef9",
//     padding: 8,
//     marginTop: 20,
//     marginBottom: 15,
//     borderLeftWidth: 4,
//     borderLeftColor: "#1e3c72",
//     borderLeftStyle: "solid",
//   },
//   subsectionTitle: {
//     fontSize: 12,
//     fontWeight: "bold",
//     marginTop: 15,
//     marginBottom: 10,
//     color: "#2c3e50",
//   },
//   infoTable: {
//     width: "100%",
//     marginVertical: 15,
//     borderWidth: 1,
//     borderColor: "#ddd",
//     borderStyle: "solid",
//   },
//   infoRow: {
//     flexDirection: "row",
//     borderBottomWidth: 1,
//     borderBottomColor: "#ddd",
//     borderBottomStyle: "solid",
//     minHeight: 30,
//   },
//   infoLabel: {
//     width: "30%",
//     backgroundColor: "#f5f5f5",
//     padding: 8,
//     fontWeight: "bold",
//     fontSize: 10,
//   },
//   infoValue: { width: "70%", padding: 8, fontSize: 10 },
//   statsCard: {
//     marginBottom: 15,
//     padding: 15,
//     borderWidth: 1,
//     borderColor: "#e2e8f0",
//     borderStyle: "solid",
//     borderRadius: 5,
//     backgroundColor: "#fafafa",
//   },
//   statRow: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     marginBottom: 8,
//     paddingVertical: 3,
//     borderBottomWidth: 1,
//     borderBottomColor: "#edf2f7",
//     borderBottomStyle: "solid",
//   },
//   statLabel: { fontSize: 10, color: "#4a5568" },
//   statValue: { fontSize: 10, fontWeight: "bold", color: "#2d3748" },
//   distributionTable: {
//     width: "100%",
//     marginTop: 10,
//     marginBottom: 20,
//     borderWidth: 1,
//     borderColor: "#ddd",
//     borderStyle: "solid",
//   },
//   distHeader: {
//     flexDirection: "row",
//     backgroundColor: "#2c5282",
//     color: "white",
//     minHeight: 35,
//     alignItems: "center",
//   },
//   distRow: {
//     flexDirection: "row",
//     borderBottomWidth: 1,
//     borderBottomColor: "#eee",
//     borderBottomStyle: "solid",
//     minHeight: 28,
//     alignItems: "center",
//   },
//   colDistSn: { width: "8%" },
//   colDistState: { width: "20%" },
//   colDistDepot: { width: "25%" },
//   colDistCategory: { width: "32%" },
//   colDistCount: { width: "15%" },
//   tableHeaderCell: { padding: 6, fontSize: 9, fontWeight: "bold", color: "#fff" },
//   tableCell: { padding: 6, fontSize: 9 },
//   filterBadge: { fontSize: 9, backgroundColor: "#e6eef9", padding: 4, marginBottom: 10, textAlign: "center" },
//   footer: {
//     marginTop: 40,
//     paddingTop: 20,
//     borderTopWidth: 1,
//     borderTopColor: "#ddd",
//     borderTopStyle: "solid",
//     flexDirection: "row",
//     justifyContent: "space-between",
//   },
//   signatureBox: { width: "45%" },
//   signatureLine: {
//     marginTop: 30,
//     borderTopWidth: 1,
//     borderTopColor: "#000",
//     borderTopStyle: "solid",
//     paddingTop: 5,
//     textAlign: "center",
//     fontSize: 9,
//   },
//   pageNumber: { position: "absolute", bottom: 30, right: 40, fontSize: 8, color: "#666" },
// });

// // PDF styles for main report
// const styles = StyleSheet.create({
//   page: { padding: 40, fontFamily: "Helvetica", fontSize: 10 },
//   header: {
//     marginBottom: 20,
//     borderBottomWidth: 2,
//     borderBottomColor: "#1e3c72",
//     borderBottomStyle: "solid",
//     paddingBottom: 10,
//   },
//   companyName: {
//     fontSize: 18,
//     fontWeight: "bold",
//     color: "#1e3c72",
//     textAlign: "center",
//   },
//   mission: {
//     fontSize: 11,
//     fontStyle: "italic",
//     textAlign: "center",
//     color: "#555",
//     marginTop: 5,
//   },
//   title: {
//     fontSize: 20,
//     fontWeight: "bold",
//     textAlign: "center",
//     marginVertical: 15,
//     color: "#1e3c72",
//   },
//   sectionTitle: {
//     fontSize: 14,
//     fontWeight: "bold",
//     backgroundColor: "#e6eef9",
//     padding: 8,
//     marginTop: 20,
//     marginBottom: 15,
//     borderLeftWidth: 4,
//     borderLeftColor: "#1e3c72",
//     borderLeftStyle: "solid",
//   },
//   subsectionTitle: {
//     fontSize: 12,
//     fontWeight: "bold",
//     marginTop: 15,
//     marginBottom: 10,
//     color: "#2c3e50",
//   },
//   infoTable: {
//     width: "100%",
//     marginVertical: 15,
//     borderWidth: 1,
//     borderColor: "#ddd",
//     borderStyle: "solid",
//   },
//   infoRow: {
//     flexDirection: "row",
//     borderBottomWidth: 1,
//     borderBottomColor: "#ddd",
//     borderBottomStyle: "solid",
//     minHeight: 30,
//   },
//   infoLabel: {
//     width: "30%",
//     backgroundColor: "#f5f5f5",
//     padding: 8,
//     fontWeight: "bold",
//     fontSize: 10,
//   },
//   infoValue: { width: "70%", padding: 8, fontSize: 10 },
//   statsCard: {
//     marginBottom: 15,
//     padding: 15,
//     borderWidth: 1,
//     borderColor: "#e2e8f0",
//     borderStyle: "solid",
//     borderRadius: 5,
//     backgroundColor: "#fafafa",
//   },
//   statRow: {
//     flexDirection: "row",
//     justifyContent: "space-between",
//     marginBottom: 8,
//     paddingVertical: 3,
//     borderBottomWidth: 1,
//     borderBottomColor: "#edf2f7",
//     borderBottomStyle: "solid",
//   },
//   statLabel: { fontSize: 10, color: "#4a5568" },
//   statValue: { fontSize: 10, fontWeight: "bold", color: "#2d3748" },
//   distributionTable: {
//     width: "100%",
//     marginTop: 10,
//     marginBottom: 20,
//     borderWidth: 1,
//     borderColor: "#ddd",
//     borderStyle: "solid",
//   },
//   distHeader: {
//     flexDirection: "row",
//     backgroundColor: "#2c5282",
//     color: "white",
//     minHeight: 35,
//     alignItems: "center",
//   },
//   distRow: {
//     flexDirection: "row",
//     borderBottomWidth: 1,
//     borderBottomColor: "#eee",
//     borderBottomStyle: "solid",
//     minHeight: 28,
//     alignItems: "center",
//   },
//   colDistSn: { width: "8%" },
//   colDistState: { width: "20%" },
//   colDistDepot: { width: "25%" },
//   colDistCategory: { width: "32%" },
//   colDistCount: { width: "15%" },
//   tableHeaderCell: { padding: 6, fontSize: 9, fontWeight: "bold", color: "#fff" },
//   tableCell: { padding: 6, fontSize: 9 },
//   filterBadge: { fontSize: 9, backgroundColor: "#e6eef9", padding: 4, marginBottom: 10, textAlign: "center" },
//   footer: {
//     marginTop: 40,
//     paddingTop: 20,
//     borderTopWidth: 1,
//     borderTopColor: "#ddd",
//     borderTopStyle: "solid",
//     flexDirection: "row",
//     justifyContent: "space-between",
//   },
//   signatureBox: { width: "45%" },
//   signatureLine: {
//     marginTop: 30,
//     borderTopWidth: 1,
//     borderTopColor: "#000",
//     borderTopStyle: "solid",
//     paddingTop: 5,
//     textAlign: "center",
//     fontSize: 9,
//   },
//   pageNumber: { position: "absolute", bottom: 30, right: 40, fontSize: 8, color: "#666" },
// });

// // Enhanced Print Styles
// const printStyles = `
//   @media print {
//     @page { size: A4 portrait; margin: 0.4in; }
//     body { font-family: 'Arial', 'Helvetica', sans-serif; background: white; color: #1a202c; line-height: 1.4; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
//     .no-print { display: none !important; }
//     .print-report { max-width: 100%; margin: 0 auto; padding: 10px; }
//     .print-header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #1e3c72; padding-bottom: 10px; }
//     .print-title { font-size: 22px; font-weight: bold; color: #1e3c72; margin-bottom: 3px; }
//     .print-subtitle { font-size: 12px; color: #4a5568; font-style: italic; margin: 2px 0; }
//     .print-meta { background: linear-gradient(to right, #f8fafc, #edf2f7); border: 1px solid #e2e8f0; border-radius: 6px; padding: 10px; margin-bottom: 15px; display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
//     .print-meta-item { display: flex; align-items: center; font-size: 11px; }
//     .print-meta-label { font-weight: bold; color: #2d3748; min-width: 100px; }
//     .print-meta-value { color: #1e3c72; font-weight: 600; }
//     .print-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-bottom: 15px; }
//     .print-stat-card { background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 8px; text-align: center; border-top: 3px solid #1e3c72; }
//     .print-stat-label { font-size: 11px; color: #718096; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.3px; }
//     .print-stat-value { font-size: 20px; font-weight: bold; color: #1e3c72; margin-bottom: 3px; }
//     .print-stat-percent { font-size: 10px; color: #48bb78; font-weight: 600; }
//     .print-section { margin-bottom: 15px; page-break-inside: avoid; }
//     .print-section-header { display: flex; align-items: center; margin-bottom: 10px; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px; }
//     .print-section-icon { background: #1e3c72; color: white; width: 26px; height: 26px; border-radius: 6px; display: flex; align-items: center; justify-content: center; margin-right: 10px; font-size: 14px; }
//     .print-section-title { font-size: 16px; font-weight: bold; color: #1e3c72; margin: 0; }
//     .print-charts-grid { display: flex; flex-direction: column; gap: 15px; margin-bottom: 15px; }
//     .print-chart-row { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; page-break-inside: avoid; }
//     .print-chart-card { background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px; page-break-inside: avoid; }
//     .print-chart-header { display: flex; align-items: center; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px solid #edf2f7; }
//     .print-chart-icon { background: #f7fafc; width: 30px; height: 30px; border-radius: 6px; display: flex; align-items: center; justify-content: center; margin-right: 10px; }
//     .print-chart-title { font-size: 13px; font-weight: bold; color: #2d3748; }
//     .print-chart-container { width: 100%; height: 180px; display: flex; justify-content: center; align-items: center; }
//     .print-chart-svg { width: 100%; height: 100%; max-height: 180px; }
//     .print-chart-full { grid-column: span 2; }
//     .print-table-container { border: 1px solid #e2e8f0; border-radius: 6px; overflow: hidden; margin-bottom: 15px; }
//     .print-table { width: 100%; border-collapse: collapse; font-size: 10px; }
//     .print-table th { background: #1e3c72; color: white; padding: 8px 6px; font-weight: 600; text-align: left; }
//     .print-table td { padding: 6px; border-bottom: 1px solid #e2e8f0; }
//     .print-table tr:last-child td { border-bottom: none; }
//     .print-table tr:nth-child(even) { background-color: #f8fafc; }
//     .print-performance-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 15px; }
//     .print-performance-card { background: white; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; }
//     .print-performance-item { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; border-bottom: 1px dashed #e2e8f0; font-size: 11px; }
//     .print-certification { background: linear-gradient(135deg, #f0f7ff 0%, #e6f3ff 100%); border: 1px solid #b8daff; border-radius: 8px; padding: 15px; margin-bottom: 15px; }
//     .print-certification-title { font-size: 14px; font-weight: bold; color: #1e3c72; margin-bottom: 10px; }
//     .print-certification-text { font-size: 11px; line-height: 1.4; }
//     .print-signature { display: flex; justify-content: space-between; margin-top: 20px; padding-top: 15px; border-top: 1px solid #e2e8f0; }
//     .print-signature-box { width: 45%; }
//     .print-signature-line { margin-top: 25px; border-top: 1px solid #000; padding-top: 5px; font-size: 10px; text-align: center; }
//     .print-footer { margin-top: 15px; padding-top: 10px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 9px; color: #718096; }
//     .print-badge { display: inline-block; padding: 3px 8px; border-radius: 16px; font-size: 9px; font-weight: 600; }
//     .print-badge-info { background: #bee3f8; color: #2c5282; }
//   }
// `;

// // ============================
// // CONCURRENCY POOL FOR PARALLEL REQUESTS
// // ============================
// const asyncPool = async (concurrency, items, iteratorFn) => {
//   const results = [];
//   const executing = new Set();
//   for (const [index, item] of items.entries()) {
//     const promise = Promise.resolve().then(() => iteratorFn(item, index));
//     results.push(promise);
//     executing.add(promise);
//     const clean = () => executing.delete(promise);
//     promise.then(clean, clean);
//     if (executing.size >= concurrency) await Promise.race(executing);
//   }
//   return Promise.all(results);
// };

// // ============================
// // HELPER FUNCTIONS
// // ============================
// const parseDate = (dateStr) => {
//   if (!dateStr) return null;
//   if (moment(dateStr, moment.ISO_8601, true).isValid()) return moment(dateStr);
//   if (moment(dateStr, "DD/MM/YYYY", true).isValid()) return moment(dateStr, "DD/MM/YYYY");
//   if (moment(dateStr, "DD-MM-YYYY", true).isValid()) return moment(dateStr, "DD-MM-YYYY");
//   if (moment(dateStr, "YYYY-MM-DD", true).isValid()) return moment(dateStr, "YYYY-MM-DD");
//   const parsed = moment(dateStr);
//   return parsed.isValid() ? parsed : null;
// };

// const formatDateToDDMMYYYY = (dateStr) => {
//   if (!dateStr) return "N/A";
//   const date = parseDate(dateStr);
//   if (date && date.isValid()) return date.format("DD/MM/YYYY");
//   try {
//     if (typeof dateStr === "string") {
//       if (dateStr.includes("-")) {
//         const [year, month, day] = dateStr.split("T")[0].split("-");
//         return `${day}/${month}/${year}`;
//       }
//       if (dateStr.includes("/")) return dateStr;
//     }
//     if (dateStr instanceof Date) {
//       const day = dateStr.getDate().toString().padStart(2, "0");
//       const month = (dateStr.getMonth() + 1).toString().padStart(2, "0");
//       const year = dateStr.getFullYear();
//       return `${day}/${month}/${year}`;
//     }
//     return String(dateStr);
//   } catch {
//     return String(dateStr);
//   }
// };

// const formatTrainerNames = (trainers) => {
//   if (!trainers || trainers.length === 0) return "No Trainer Assigned";
//   const names = trainers.map((t) => {
//     if (t.name) return t.name;
//     if (t.firstName && t.lastName) return `${t.firstName} ${t.lastName}`;
//     if (t.firstName) return t.firstName;
//     return `Trainer ${t.id}`;
//   });
//   return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
// };

// // ============================
// // PDF Report Component
// // ============================
// const TrainingReportPDF = ({ data, dateRange, trainingDistribution, stats, filters }) => {
//   const tableData = trainingDistribution
//     .filter(item => item.count > 0 && item.depot !== "Unknown Depot" && item.state !== "Unknown State")
//     .slice(0, 30);
//   const totalCandidates = stats.totalCandidates || 0;
//   const nominated = stats.nominated || 0;
//   const attempted = stats.attempted || 0;
//   const passed = stats.passed || 0;
//   const nominationRate = totalCandidates > 0 ? ((nominated / totalCandidates) * 100).toFixed(1) : 0;
//   const attemptRate = nominated > 0 ? ((attempted / nominated) * 100).toFixed(1) : 0;
//   const passRate = attempted > 0 ? ((passed / attempted) * 100).toFixed(1) : 0;
//   const completionRate = data.batches?.planned > 0 ? ((data.batches.completed / data.batches.planned) * 100).toFixed(1) : 0;

//   const getFilterText = () => {
//     const parts = [];
//     if (filters.selectedState && filters.selectedState.value !== "all") parts.push(`State: ${filters.selectedState.label}`);
//     if (filters.selectedDivision && filters.selectedDivision.value !== "all") parts.push(`Division: ${filters.selectedDivision.label}`);
//     if (filters.selectedDepot && filters.selectedDepot.value !== "all") parts.push(`Depot: ${filters.selectedDepot.label}`);
//     if (filters.division) parts.push(`Division Text: ${filters.division}`);
//     return parts.length > 0 ? parts.join(" • ") : "All Locations";
//   };

//   return (
//     <Document>
//       <Page style={styles.page}>
//         <View style={styles.header}>
//           <Text style={styles.companyName}>TML SMART CITY MOBILITY SOLUTIONS LIMITED</Text>
//           <Text style={styles.mission}>We will be the most admired public transportation company.</Text>
//         </View>
//         <Text style={styles.title}>TRAINING REPORT</Text>
//         <View style={styles.infoTable}>
//           <View style={styles.infoRow}><Text style={styles.infoLabel}>Course Name:</Text><Text style={styles.infoValue}>{data.courseName || "Driver/Technical/IT/Safety Training"}</Text></View>
//           <View style={styles.infoRow}><Text style={styles.infoLabel}>Training Duration:</Text><Text style={styles.infoValue}>From {dateRange.fromDate ? format(new Date(dateRange.fromDate), "dd-MM-yyyy") : "00-00-0000"} To {dateRange.toDate ? format(new Date(dateRange.toDate), "dd-MM-yyyy") : "00-00-0000"}</Text></View>
//           <View style={styles.infoRow}><Text style={styles.infoLabel}>Project:</Text><Text style={styles.infoValue}>{data.project || "Smart City Mobility"}</Text></View>
//           <View style={styles.infoRow}><Text style={styles.infoLabel}>Filter Applied:</Text><Text style={[styles.infoValue, { fontWeight: "bold", color: "#1e3c72" }]}>{getFilterText()}</Text></View>
//           <View style={styles.infoRow}><Text style={styles.infoLabel}>Report Generated:</Text><Text style={styles.infoValue}>{format(new Date(), "dd-MM-yyyy HH:mm")}</Text></View>
//         </View>
//         <Text style={styles.sectionTitle}>TRAINING SUMMARY STATISTICS</Text>
//         <View style={styles.statsCard}>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Total Candidates:</Text><Text style={styles.statValue}>{totalCandidates.toLocaleString()}</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Nominated in Batches:</Text><Text style={styles.statValue}>{nominated.toLocaleString()} ({nominationRate}%)</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Actually Attempted Exams:</Text><Text style={styles.statValue}>{attempted.toLocaleString()} ({attemptRate}%)</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Actually Passed (≥80%):</Text><Text style={styles.statValue}>{passed.toLocaleString()} ({passRate}%)</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Overall Pass Rate:</Text><Text style={[styles.statValue, { color: "#2c5282", fontSize: 11 }]}>{passRate}%</Text></View>
//         </View>
//         <Text style={styles.subsectionTitle}>Batch Completion Status</Text>
//         <View style={styles.statsCard}>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Planned Batches:</Text><Text style={styles.statValue}>{data.batches.planned || 0}</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Completed Batches:</Text><Text style={styles.statValue}>{data.batches.completed || 0}</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Ongoing Batches:</Text><Text style={styles.statValue}>{data.batches.ongoing || 0}</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Batch Completion Rate:</Text><Text style={styles.statValue}>{completionRate}%</Text></View>
//         </View>
//         <View style={styles.footer}>
//           <Text style={{ fontSize: 8, color: "#666" }}>This is a system-generated report</Text>
//           <Text style={{ fontSize: 8, color: "#666" }}>Page 1 of 2</Text>
//         </View>
//       </Page>
//       <Page style={styles.page}>
//         <Text style={styles.sectionTitle}>TRAINING DISTRIBUTION BY LOCATION</Text>
//         <Text style={styles.filterBadge}>Filter: {getFilterText()}</Text>
//         {tableData.length > 0 ? (
//           <>
//             <Text style={{ fontSize: 9, color: "#666", marginBottom: 10, marginTop: 5 }}>Showing {tableData.length} locations with actual training data</Text>
//             <View style={styles.distributionTable}>
//               <View style={styles.distHeader}>
//                 <Text style={[styles.tableHeaderCell, styles.colDistSn]}>S.No.</Text>
//                 <Text style={[styles.tableHeaderCell, styles.colDistState]}>State</Text>
//                 <Text style={[styles.tableHeaderCell, styles.colDistDepot]}>Depot</Text>
//                 <Text style={[styles.tableHeaderCell, styles.colDistCategory]}>Training Category</Text>
//                 <Text style={[styles.tableHeaderCell, styles.colDistCount]}>Count</Text>
//               </View>
//               {tableData.map((item, index) => (
//                 <View key={index} style={styles.distRow}>
//                   <Text style={[styles.tableCell, styles.colDistSn]}>{index + 1}</Text>
//                   <Text style={[styles.tableCell, styles.colDistState]}>{item.state || "N/A"}</Text>
//                   <Text style={[styles.tableCell, styles.colDistDepot]}>{item.depot || "N/A"}</Text>
//                   <Text style={[styles.tableCell, styles.colDistCategory]}>{item.category || "General Training"}</Text>
//                   <Text style={[styles.tableCell, styles.colDistCount, { fontWeight: "bold" }]}>{item.count}</Text>
//                 </View>
//               ))}
//               <View style={[styles.distRow, { backgroundColor: "#f0f4f8", borderTopWidth: 2, borderTopColor: "#1e3c72" }]}>
//                 <Text style={[styles.tableCell, styles.colDistSn, { fontWeight: "bold" }]}>Total</Text>
//                 <Text style={[styles.tableCell, styles.colDistState]}></Text>
//                 <Text style={[styles.tableCell, styles.colDistDepot]}></Text>
//                 <Text style={[styles.tableCell, styles.colDistCategory, { fontWeight: "bold" }]}>All Locations</Text>
//                 <Text style={[styles.tableCell, styles.colDistCount, { fontWeight: "bold" }]}>{tableData.reduce((sum, t) => sum + (t.count || 0), 0)}</Text>
//               </View>
//             </View>
//           </>
//         ) : (
//           <View style={{ padding: 30, backgroundColor: "#f8f9fa", borderWidth: 1, borderColor: "#dee2e6", marginBottom: 20 }}>
//             <Text style={{ fontSize: 11, textAlign: "center" }}>No training distribution data available</Text>
//           </View>
//         )}
//         <Text style={[styles.sectionTitle, { marginTop: 20 }]}>PERFORMANCE SUMMARY</Text>
//         <View style={styles.statsCard}>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Training Coverage:</Text><Text style={styles.statValue}>{nominationRate}% of total candidates</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Exam Attempt Rate:</Text><Text style={styles.statValue}>{attemptRate}% of nominated candidates</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Success Rate (≥80%):</Text><Text style={[styles.statValue, { color: "#22543d", fontSize: 11, fontWeight: "bold" }]}>{passRate}%</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Total Participants Trained:</Text><Text style={styles.statValue}>{nominated.toLocaleString()}</Text></View>
//           <View style={styles.statRow}><Text style={styles.statLabel}>Successful Completions:</Text><Text style={styles.statValue}>{passed.toLocaleString()}</Text></View>
//         </View>
//         <Text style={styles.subsectionTitle}>CERTIFICATION</Text>
//         <View style={[styles.statsCard, { backgroundColor: "#f0f7ff" }]}>
//           <Text style={{ fontSize: 10, marginBottom: 10 }}>This is to certify that <Text style={{ fontWeight: "bold" }}>{passed.toLocaleString()}</Text> participants have successfully completed the training program conducted by TML Smart City Mobility Solutions Limited during {dateRange.fromDate ? ` ${format(new Date(dateRange.fromDate), "dd MMM yyyy")}` : ""} to {dateRange.toDate ? format(new Date(dateRange.toDate), "dd MMM yyyy") : ""}.</Text>
//           <Text style={{ fontSize: 9, marginTop: 10 }}><Text style={{ fontWeight: "bold" }}>Overall Pass Rate: {passRate}%</Text></Text>
//         </View>
//         <View style={styles.footer}>
//           <View style={styles.signatureBox}>
//             <Text style={{ fontSize: 9 }}>Training Manager: ______________________</Text>
//             <Text style={[styles.signatureLine, { width: "80%" }]}>Authorized Signatory</Text>
//           </View>
//           <View style={styles.signatureBox}>
//             <Text style={{ fontSize: 9 }}>Date: {format(new Date(), "dd/MM/yyyy")}</Text>
//             <Text style={{ fontSize: 9, marginTop: 25 }}>(System Generated Report)</Text>
//           </View>
//         </View>
//         <View style={styles.pageNumber}><Text>Page 2 of 2</Text></View>
//       </Page>
//     </Document>
//   );
// };

// // Process student data to extract states, divisions, and depots
// const extractLocationFilters = (students) => {
//   if (!students || students.length === 0) {
//     return { states: [], divisions: [], depots: [], depotsByState: {}, depotsByDivision: {}, statesList: [], divisionsList: [], totalDepotsCount: 0 };
//   }
//   const stateSet = new Set();
//   const divisionSet = new Set();
//   const depotMap = new Map();
//   const depotByStateMap = new Map();
//   const depotByDivisionMap = new Map();
//   students.forEach((student) => {
//     const state = student.tataStudentDetailsDto?.state || student.state || student.studentAddressInfo?.state;
//     const division = student.tataStudentDetailsDto?.division || student.division;
//     const depot = student.tataStudentDetailsDto?.depot || student.depot || student.branch;
//     if (state && state !== "Unknown State" && !state.includes("Unknown") && state.trim() !== "") stateSet.add(state.trim());
//     if (division && division !== "Unknown Division" && !division.includes("Unknown") && division.trim() !== "") divisionSet.add(division.trim());
//     if (state && depot && state !== "Unknown State" && depot !== "Unknown Depot" && !state.includes("Unknown") && !depot.includes("Unknown") && state.trim() !== "" && depot.trim() !== "") {
//       const key = `${state.trim()}-${depot.trim()}`;
//       if (!depotMap.has(key)) depotMap.set(key, { state: state.trim(), depot: depot.trim(), division: division?.trim() || "N/A" });
//       if (!depotByStateMap.has(state.trim())) depotByStateMap.set(state.trim(), new Set());
//       depotByStateMap.get(state.trim()).add(depot.trim());
//     }
//     if (division && depot && division !== "Unknown Division" && depot !== "Unknown Depot" && !division.includes("Unknown") && !depot.includes("Unknown") && division.trim() !== "" && depot.trim() !== "") {
//       if (!depotByDivisionMap.has(division.trim())) depotByDivisionMap.set(division.trim(), new Set());
//       depotByDivisionMap.get(division.trim()).add(depot.trim());
//     }
//   });
//   const states = Array.from(stateSet).sort();
//   const divisions = Array.from(divisionSet).sort();
//   const depotsByState = {};
//   depotByStateMap.forEach((depots, state) => { depotsByState[state] = Array.from(depots).sort(); });
//   const depotsByDivision = {};
//   depotByDivisionMap.forEach((depots, division) => { depotsByDivision[division] = Array.from(depots).sort(); });
//   return { states, divisions, depots: Array.from(depotMap.values()), depotsByState, depotsByDivision, statesList: states, divisionsList: divisions, totalDepotsCount: depotMap.size };
// };

// // Process training data for distribution
// const processTrainingDistribution = (trainings, selectedState, selectedDivision, selectedDepot) => {
//   const distributionMap = {};
//   if (!Array.isArray(trainings) || trainings.length === 0) return [];
//   trainings.forEach((training) => {
//     if (!training) return;
//     const depot = training.depot || training.location || training.branch;
//     const state = training.state;
//     const division = training.division || "N/A";
//     const category = training.type || training.trainingType || training.courseType || "General Training";
//     if (selectedState && selectedState.value !== "all" && state !== selectedState.label) return;
//     if (selectedDivision && selectedDivision.value !== "all" && division !== selectedDivision.label) return;
//     if (selectedDepot && selectedDepot.value !== "all" && depot !== selectedDepot.label) return;
//     if (!depot || !state || depot === "Unknown Depot" || state === "Unknown State" || depot.includes("Unknown") || state.includes("Unknown") || depot.trim() === "" || state.trim() === "") return;
//     const key = `${depot.trim()}-${state.trim()}-${category}`;
//     if (!distributionMap[key]) distributionMap[key] = { depot: depot.trim(), state: state.trim(), division: division, category, count: 0 };
//     if (training.candidates && Array.isArray(training.candidates)) distributionMap[key].count += training.candidates.length;
//     else if (training.participants && Array.isArray(training.participants)) distributionMap[key].count += training.participants.length;
//     else if (training.count && typeof training.count === "number") distributionMap[key].count += training.count;
//     else distributionMap[key].count += 1;
//   });
//   return Object.values(distributionMap).filter(item => item.count > 0).sort((a, b) => b.count - a.count);
// };

// // Chart configuration function
// const makeChart = (title, categories, data, type = "bar", colors = []) => ({
//   title: { text: title, align: "left", style: { fontSize: "16px", fontWeight: "bold", color: "#333" } },
//   chart: { type: type, toolbar: { show: true }, animations: { enabled: true, speed: 800 } },
//   plotOptions: {
//     bar: { horizontal: false, columnWidth: "60%", borderRadius: 6, dataLabels: { position: "top" } },
//     pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Total", formatter: (w) => w.globals.seriesTotals.reduce((a, b) => a + b, 0) } } } },
//   },
//   dataLabels: { enabled: true, formatter: (val) => Math.round(val), offsetY: -20, style: { fontSize: "12px", colors: ["#304758"] } },
//   stroke: { show: true, width: 2, colors: ["transparent"] },
//   xaxis: { categories, labels: { style: { fontSize: "12px" } } },
//   yaxis: { title: { text: type === "bar" ? "Number of Candidates" : "Count" }, labels: { formatter: (val) => Math.round(val) } },
//   fill: { opacity: 1, type: "gradient", gradient: { shade: "light", type: "vertical", shadeIntensity: 0.25, gradientToColors: colors, inverseColors: false, opacityFrom: 0.85, opacityTo: 0.85, stops: [0, 100] } },
//   tooltip: { y: { formatter: (val) => `${Math.round(val)} candidates` } },
//   colors: colors.length ? colors : ["#4299e1", "#48bb78", "#ed8936", "#ecc94b"],
//   grid: { borderColor: "#f1f1f1" },
//   series: [{ name: "Count", data }],
// });

// // Print Preview Modal Component
// const PrintPreviewModal = ({ isOpen, toggle, data, dateRange, trainingDistribution, stats, filters, chartsData, batchChartOptions, batchChartSeries }) => {
//   const totalCandidates = stats.totalCandidates || 0;
//   const nominated = stats.nominated || 0;
//   const attempted = stats.attempted || 0;
//   const passed = stats.passed || 0;
//   const nominationRate = totalCandidates > 0 ? ((nominated / totalCandidates) * 100).toFixed(1) : 0;
//   const attemptRate = nominated > 0 ? ((attempted / nominated) * 100).toFixed(1) : 0;
//   const passRate = attempted > 0 ? ((passed / attempted) * 100).toFixed(1) : 0;
//   const completionRate = data.batches?.planned > 0 ? ((data.batches.completed / data.batches.planned) * 100).toFixed(1) : 0;
//   const totalDistributionCount = trainingDistribution.reduce((sum, t) => sum + (t.count || 0), 0);
//   const getFilterText = () => {
//     const parts = [];
//     if (filters.selectedState && filters.selectedState.value !== "all") parts.push(`State: ${filters.selectedState.label}`);
//     if (filters.selectedDivision && filters.selectedDivision.value !== "all") parts.push(`Division: ${filters.selectedDivision.label}`);
//     if (filters.selectedDepot && filters.selectedDepot.value !== "all") parts.push(`Depot: ${filters.selectedDepot.label}`);
//     if (filters.division) parts.push(`Division Text: ${filters.division}`);
//     return parts.length > 0 ? parts.join(" • ") : "All Locations";
//   };

//   const generateChartSVG = (chart) => {
//     const width = 400, height = 200, barWidth = 70, startX = 60, spacing = 25;
//     let svg = `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" style="background: white;">`;
//     svg += `<rect width="${width}" height="${height}" fill="white" rx="4" ry="4" />`;
//     svg += `<text x="10" y="20" font-family="Arial" font-size="12" font-weight="bold" fill="#1e3c72">${chart.title}</text>`;
//     const maxValue = Math.max(...chart.data, 1);
//     const scale = 130 / maxValue;
//     const gridEndY = 170;
//     for (let i = 0; i <= 4; i++) {
//       const y = gridEndY - (i * 32.5);
//       const value = Math.round((maxValue * i) / 4);
//       svg += `<line x1="45" y1="${y}" x2="${width - 30}" y2="${y}" stroke="#e2e8f0" stroke-width="0.5" stroke-dasharray="3,2" />`;
//       svg += `<text x="35" y="${y + 3}" font-family="Arial" font-size="8" fill="#718096" text-anchor="end">${value}${chart.title.includes("Pass Rate") ? "%" : ""}</text>`;
//     }
//     chart.categories.forEach((cat, i) => {
//       const x = startX + i * (barWidth + spacing);
//       const barHeight = chart.data[i] * scale;
//       const y = gridEndY - barHeight;
//       svg += `<rect x="${x}" y="${y}" width="${barWidth}" height="${Math.max(barHeight, 2)}" fill="${chart.colors[i % chart.colors.length]}" rx="4" ry="4" />`;
//       svg += `<text x="${x + barWidth / 2}" y="${y - 5}" font-family="Arial" font-size="10" font-weight="bold" fill="#2d3748" text-anchor="middle">${Math.round(chart.data[i])}${chart.title.includes("Pass Rate") ? "%" : ""}</text>`;
//       svg += `<text x="${x + barWidth / 2}" y="${gridEndY + 15}" font-family="Arial" font-size="9" font-weight="600" fill="#4a5568" text-anchor="middle">${cat}</text>`;
//     });
//     svg += `<text x="20" y="120" font-family="Arial" font-size="9" fill="#718096" transform="rotate(-90, 20, 120)" text-anchor="middle">${chart.yAxisTitle}</text>`;
//     svg += `<line x1="45" y1="${gridEndY}" x2="${width - 30}" y2="${gridEndY}" stroke="#cbd5e0" stroke-width="1" />`;
//     svg += `</svg>`;
//     return svg;
//   };

//   const handlePrint = () => {
//     const printWindow = window.open("", "_blank");
//     const chartData = [
//       { title: "Total Candidates vs Nominated", categories: ["Total", "Nominated"], data: [totalCandidates, nominated], colors: ["#4299e1", "#48bb78"], yAxisTitle: "Candidates" },
//       { title: "Nominated vs Attempted", categories: ["Nominated", "Attempted"], data: [nominated, attempted], colors: ["#ed8936", "#ecc94b"], yAxisTitle: "Candidates" },
//       { title: "Attempted vs Passed", categories: ["Attempted", "Passed"], data: [attempted, passed], colors: ["#ecc94b", "#48bb78"], yAxisTitle: "Candidates" },
//       { title: "Pass Rate Analysis", categories: ["Pass Rate"], data: [parseFloat(passRate)], colors: ["#48bb78"], yAxisTitle: "Percentage" },
//       { title: "Batch Completion", categories: ["Planned", "Completed", "Ongoing"], data: [data.batches?.planned || 0, data.batches?.completed || 0, data.batches?.ongoing || 0], colors: ["#4299e1", "#48bb78", "#ecc94b"], yAxisTitle: "Batches" },
//     ];
//     printWindow.document.write(`<!DOCTYPE html><html><head><title>Training Analytics Report - ${format(new Date(), "dd-MM-yyyy")}</title><style>${printStyles}@media print{.print-chart-container{margin:5px 0;page-break-inside:avoid;}.print-chart-svg{width:100%;height:auto;max-height:200px;}.print-stats-grid{margin-bottom:15px;}.print-section{margin-bottom:15px;}}</style></head><body><div class="print-report"><div class="print-header"><h1 class="print-title">TML SMART CITY MOBILITY SOLUTIONS LIMITED</h1><p class="print-subtitle">"We will be the most admired public transportation company."</p><h2 style="font-size:18px;color:#1e3c72;margin-top:5px;">TRAINING ANALYTICS REPORT</h2><p style="font-size:11px;color:#4a5568;margin-top:3px;">${format(new Date(dateRange.fromDate), "dd MMM yyyy")} - ${format(new Date(dateRange.toDate), "dd MMM yyyy")}</p></div><div class="print-meta"><div class="print-meta-item"><span class="print-meta-label">Filters:</span><span class="print-meta-value">${getFilterText()}</span></div><div class="print-meta-item"><span class="print-meta-label">Database:</span><span class="print-meta-value">${filters.allStatesList?.length || 0} States • ${filters.allDivisionsList?.length || 0} Divisions • ${filters.totalDepotsCount || 0} Depots</span></div><div class="print-meta-item"><span class="print-meta-label">Generated:</span><span class="print-meta-value">${format(new Date(), "dd MMM yyyy HH:mm")}</span></div></div><div class="print-section"><div class="print-section-header"><div class="print-section-icon">📊</div><h3 class="print-section-title">Summary Statistics</h3></div><div class="print-stats-grid"><div class="print-stat-card"><div class="print-stat-label">Total</div><div class="print-stat-value">${totalCandidates.toLocaleString()}</div></div><div class="print-stat-card"><div class="print-stat-label">Nominated</div><div class="print-stat-value" style="color:#48bb78;">${nominated.toLocaleString()}</div><div class="print-stat-percent">${nominationRate}%</div></div><div class="print-stat-card"><div class="print-stat-label">Attempted</div><div class="print-stat-value" style="color:#ed8936;">${attempted.toLocaleString()}</div><div class="print-stat-percent">${attemptRate}%</div></div><div class="print-stat-card"><div class="print-stat-label">Passed</div><div class="print-stat-value" style="color:#4299e1;">${passed.toLocaleString()}</div><div class="print-stat-percent">${passRate}%</div></div></div></div><div class="print-section"><div class="print-section-header"><div class="print-section-icon">📦</div><h3 class="print-section-title">Batch Status</h3></div><div class="print-stats-grid"><div class="print-stat-card"><div class="print-stat-label">Planned</div><div class="print-stat-value">${data.batches?.planned || 0}</div></div><div class="print-stat-card"><div class="print-stat-label">Completed</div><div class="print-stat-value" style="color:#48bb78;">${data.batches?.completed || 0}</div></div><div class="print-stat-card"><div class="print-stat-label">Ongoing</div><div class="print-stat-value" style="color:#ed8936;">${data.batches?.ongoing || 0}</div></div><div class="print-stat-card"><div class="print-stat-label">Completion</div><div class="print-stat-value" style="color:#4299e1;">${completionRate}%</div></div></div></div><div class="print-section"><div class="print-section-header"><div class="print-section-icon">📈</div><h3 class="print-section-title">Training Analytics Charts</h3></div><div class="print-charts-grid"><div class="print-chart-row"><div class="print-chart-card"><div class="print-chart-header"><div class="print-chart-icon">📊</div><div class="print-chart-title">Total vs Nominated</div></div><div class="print-chart-container">${generateChartSVG(chartData[0])}</div></div><div class="print-chart-card"><div class="print-chart-header"><div class="print-chart-icon">📊</div><div class="print-chart-title">Nominated vs Attempted</div></div><div class="print-chart-container">${generateChartSVG(chartData[1])}</div></div></div><div class="print-chart-row"><div class="print-chart-card"><div class="print-chart-header"><div class="print-chart-icon">📊</div><div class="print-chart-title">Attempted vs Passed</div></div><div class="print-chart-container">${generateChartSVG(chartData[2])}</div></div><div class="print-chart-card"><div class="print-chart-header"><div class="print-chart-icon">📈</div><div class="print-chart-title">Pass Rate</div></div><div class="print-chart-container">${generateChartSVG(chartData[3])}</div></div></div><div class="print-chart-row"><div class="print-chart-card print-chart-full"><div class="print-chart-header"><div class="print-chart-icon">📊</div><div class="print-chart-title">Batch Completion Status</div></div><div class="print-chart-container" style="height:200px;">${generateChartSVG(chartData[4])}</div></div></div></div></div><div class="print-section"><div class="print-section-header"><div class="print-section-icon">📍</div><h3 class="print-section-title">Training Distribution</h3></div><div class="print-table-container"><table class="print-table"><thead><tr><th style="width:5%;">#</th><th style="width:15%;">State</th><th style="width:20%;">Depot</th><th style="width:30%;">Category</th><th style="width:30%; text-align:right;">Count</th></tr></thead><tbody>${trainingDistribution.slice(0, 10).map((item, index) => `<tr><td style="text-align:center;">${index + 1}</td><td>${item.state}</td><td>${item.depot}</td><td><span class="print-badge print-badge-info">${item.category}</span></td><td style="text-align:right;font-weight:bold;">${item.count.toLocaleString()}</td></tr>`).join("")}</tbody><tfoot><tr style="background:#edf2f7;font-weight:bold;"><td colspan="5" style="text-align:right;">Total:</td><td style="text-align:right;color:#1e3c72;">${trainingDistribution.reduce((sum, t) => sum + (t.count || 0), 0).toLocaleString()}</td></tr></tfoot></table></div></div><div class="print-section"><div class="print-section-header"><div class="print-section-icon">🎯</div><h3 class="print-section-title">Performance Summary</h3></div><div class="print-performance-grid"><div class="print-performance-card"><div class="print-performance-item"><span>Training Coverage</span><span style="font-weight:bold;color:#1e3c72;">${nominationRate}%</span></div><div class="print-performance-item"><span>Attempt Rate</span><span style="font-weight:bold;color:#1e3c72;">${attemptRate}%</span></div><div class="print-performance-item"><span>Success Rate</span><span style="font-weight:bold;color:#48bb78;">${passRate}%</span></div></div><div class="print-performance-card"><div class="print-performance-item"><span>Trained</span><span style="font-weight:bold;color:#1e3c72;">${nominated.toLocaleString()}</span></div><div class="print-performance-item"><span>Passed</span><span style="font-weight:bold;color:#1e3c72;">${passed.toLocaleString()}</span></div><div class="print-performance-item"><span>Pending</span><span style="font-weight:bold;color:#ed8936;">${(nominated - passed).toLocaleString()}</span></div></div></div></div><div class="print-certification"><div class="print-certification-title">🏆 Certificate of Completion</div><div class="print-certification-text"><p style="margin-bottom:8px;"><strong style="color:#1e3c72;">${passed.toLocaleString()}</strong> participants completed training</p><p style="margin-bottom:5px;"><span style="background:#1e3c72;color:white;padding:3px 10px;border-radius:16px;font-size:10px;">Pass Rate: ${passRate}%</span></p><p style="font-size:9px;color:#4a5568;">Minimum score: 80% • Based on ${attempted} exam attempts</p></div></div><div class="print-signature"><div class="print-signature-box"><p style="font-size:10px;margin-bottom:5px;">Training Manager</p><div style="border-bottom:1px solid #000;width:80%;margin-bottom:3px;"></div><p style="font-size:9px;color:#718096;">Authorized Signatory</p></div><div class="print-signature-box"><p style="font-size:10px;margin-bottom:5px;">Date</p><div style="border-bottom:1px solid #000;width:80%;margin-bottom:3px;"></div><p style="font-size:9px;color:#718096;">${format(new Date(), "dd MMM yyyy")}</p></div></div><div class="print-footer"><p>System Generated Report • TML Smart City Mobility • ${format(new Date(), "dd MMM yyyy HH:mm")}</p></div></div><script>window.onload=function(){setTimeout(function(){window.print();window.onafterprint=function(){window.close();};},500);};<\/script></body></html>`);
//     printWindow.document.close();
//   };

//   return (
//     <Modal isOpen={isOpen} toggle={toggle} size="xl" fullscreen>
//       <ModalHeader toggle={toggle} style={{ background: "#1e3c72", color: "white", borderBottom: "none" }}>
//         <div className="d-flex align-items-center"><AssessmentIcon style={{ marginRight: "12px", fontSize: "24px" }} /><span style={{ fontSize: "18px", fontWeight: "600" }}>Print Preview - Training Analytics Report</span></div>
//       </ModalHeader>
//       <ModalBody style={{ background: "#f8fafc", padding: "20px" }}>
//         <div style={{ background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 5px 15px rgba(0,0,0,0.05)" }}>
//           <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px", paddingBottom: "10px", borderBottom: "1px solid #e2e8f0" }}>
//             <div><h4 style={{ color: "#1e3c72", marginBottom: "3px", fontSize: "18px" }}>TML SMART CITY MOBILITY</h4><p style={{ color: "#718096", fontSize: "11px", margin: 0 }}>Training Analytics Report</p></div>
//             <div style={{ background: "#1e3c72", color: "white", padding: "8px 15px", borderRadius: "8px", textAlign: "center" }}><div style={{ fontSize: "18px", fontWeight: "bold" }}>{totalDistributionCount}</div><div style={{ fontSize: "10px" }}>Participants</div></div>
//           </div>
//           <Row className="mb-3">
//             <Col md={3}><Card className="border-0 shadow-sm"><CardBody className="p-2 text-center"><div style={{ fontSize: "11px", color: "#718096" }}>Total</div><div style={{ fontSize: "22px", fontWeight: "bold", color: "#1e3c72" }}>{totalCandidates.toLocaleString()}</div></CardBody></Card></Col>
//             <Col md={3}><Card className="border-0 shadow-sm"><CardBody className="p-2 text-center"><div style={{ fontSize: "11px", color: "#718096" }}>Nominated</div><div style={{ fontSize: "22px", fontWeight: "bold", color: "#48bb78" }}>{nominated.toLocaleString()}</div><div style={{ fontSize: "9px", color: "#718096" }}>{nominationRate}%</div></CardBody></Card></Col>
//             <Col md={3}><Card className="border-0 shadow-sm"><CardBody className="p-2 text-center"><div style={{ fontSize: "11px", color: "#718096" }}>Attempted</div><div style={{ fontSize: "22px", fontWeight: "bold", color: "#ed8936" }}>{attempted.toLocaleString()}</div><div style={{ fontSize: "9px", color: "#718096" }}>{attemptRate}%</div></CardBody></Card></Col>
//             <Col md={3}><Card className="border-0 shadow-sm"><CardBody className="p-2 text-center"><div style={{ fontSize: "11px", color: "#718096" }}>Passed</div><div style={{ fontSize: "22px", fontWeight: "bold", color: "#4299e1" }}>{passed.toLocaleString()}</div><div style={{ fontSize: "9px", color: "#718096" }}>{passRate}%</div></CardBody></Card></Col>
//           </Row>
//           <h6 style={{ color: "#1e3c72", marginBottom: "10px", display: "flex", alignItems: "center" }}><BarChartIcon style={{ marginRight: "8px", fontSize: "18px" }} />Chart Preview</h6>
//           <Row className="mb-3">{chartsData.slice(0, 2).map((chart, i) => (<Col md={6} key={i}><Card className="mb-2 shadow-sm"><CardBody className="p-2"><div style={{ fontSize: "12px", fontWeight: "bold", color: "#2d3748", marginBottom: "5px" }}>{chart.title.text}</div><Chart options={chart} series={chart.series} type="bar" height={180} /></CardBody></Card></Col>))}</Row>
//           <div style={{ display: "flex", justifyContent: "center", gap: "15px", marginTop: "20px", padding: "15px", background: "#f8fafc", borderRadius: "8px" }}>
//             <Button color="primary" size="md" onClick={handlePrint} style={{ padding: "10px 25px", fontSize: "14px", background: "#1e3c72", border: "none", borderRadius: "8px", display: "flex", alignItems: "center", gap: "8px" }}><PrintIcon style={{ fontSize: "18px" }} /> Print Report</Button>
//             <Button color="secondary" size="md" onClick={toggle} style={{ padding: "10px 25px", fontSize: "14px", borderRadius: "8px", display: "flex", alignItems: "center", gap: "8px" }}><CloseIcon style={{ fontSize: "18px" }} /> Cancel</Button>
//           </div>
//         </div>
//       </ModalBody>
//     </Modal>
//   );
// };

// // ============================
// // BATCH DETAILS TAB COMPONENT (Optimized with Background Fetching)
// // ============================
// const BatchDetailsTab = ({ datePeriod, customStartDate, customEndDate, selectedState, selectedDepot, selectedDivision }) => {
//   const [loader, setLoader] = useState(true);
//   const [backgroundLoading, setBackgroundLoading] = useState(false);
//   const [allBatch, setAllBatch] = useState([]);
//   const [filteredBatches, setFilteredBatches] = useState([]);
//   const [searchText, setSearchText] = useState("");
//   const [batchTrainersMap, setBatchTrainersMap] = useState({});
//   const [batchStudentsMap, setBatchStudentsMap] = useState({});
//   const [batchStudentsCount, setBatchStudentsCount] = useState({});
//   const [passFailStatsMap, setPassFailStatsMap] = useState({});
//   const [fetchProgress, setFetchProgress] = useState({ active: false, current: 0, total: 0, message: "" });
//   const [expandedBatch, setExpandedBatch] = useState(null);
//   const [failedStudentsList, setFailedStudentsList] = useState([]);
//   const [notAttemptedStudentsList, setNotAttemptedStudentsList] = useState([]);
//   const [passedStudentsList, setPassedStudentsList] = useState([]);
//   const [showFailedModal, setShowFailedModal] = useState(false);
//   const [showNotAttemptedModal, setShowNotAttemptedModal] = useState(false);
//   const [showPassedModal, setShowPassedModal] = useState(false);
//   const [reportModalShow, setReportModalShow] = useState(false);
//   const [selectedBatch, setSelectedBatch] = useState(null);
//   const [selectedReportType, setSelectedReportType] = useState(null);
//   const [allReportsModalShow, setAllReportsModalShow] = useState(false);
//   const [selectedAllReportType, setSelectedAllReportType] = useState(null);
//   const [consolidatedModalShow, setConsolidatedModalShow] = useState(false);
//   const [consolidatedReportType, setConsolidatedReportType] = useState("DDT");
//   const [batchReportData, setBatchReportData] = useState({});
//   const [missingReports, setMissingReports] = useState([]);
//   const [isDataLoaded, setIsDataLoaded] = useState(false);
//   const [isCacheUsed, setIsCacheUsed] = useState(false);

//   // Chart states
//   const [statusDistributionChart, setStatusDistributionChart] = useState(null);
//   const [topBatchesChart, setTopBatchesChart] = useState(null);
//   const [batchStatsChart, setBatchStatsChart] = useState(null);

//   const reportOptions = [
//     { value: "DDT", label: "DDT Report" },
//     { value: "DASP", label: "DASP Report" },
//     { value: "Other", label: "Other Report" },
//   ];

//   const consolidatedOptions = [
//     { value: "DDT", label: "DDT Reports (Sheet per Batch)" },
//     { value: "DASP", label: "DASP Reports (Sheet per Batch)" },
//     { value: "Other", label: "Other Reports (Sheet per Batch)" },
//   ];

//   // ============================
//   // CACHE MANAGEMENT
//   // ============================
//   const CACHE_KEYS = {
//     ALL_BATCHES: "batch_details_all_batches_v2",
//     BATCH_TRAINERS: "batch_details_trainers_v2",
//     BATCH_STUDENTS: "batch_details_students_v2",
//     PASS_FAIL_STATS: "batch_details_pass_fail_v2",
//     CACHE_TIMESTAMP: "batch_details_timestamp_v2",
//   };
//   const CACHE_DURATION = 30 * 60 * 1000;

//   const saveToCache = (key, data) => {
//     try {
//       localStorage.setItem(key, JSON.stringify({ timestamp: Date.now(), data }));
//     } catch (error) { console.error("Error saving to cache:", error); }
//   };

//   const loadFromCache = (key) => {
//     try {
//       const cached = localStorage.getItem(key);
//       if (!cached) return null;
//       const cacheData = JSON.parse(cached);
//       if (Date.now() - cacheData.timestamp > CACHE_DURATION) {
//         localStorage.removeItem(key);
//         return null;
//       }
//       return cacheData.data;
//     } catch (error) { return null; }
//   };

//   const clearCache = () => {
//     Object.values(CACHE_KEYS).forEach(key => localStorage.removeItem(key));
//   };

//   // ============================
//   // FETCH EXAM RESULTS FOR STUDENT
//   // ============================
//   const fetchStudentExamResult = async (batchId, studentId) => {
//     if (!batchId || !studentId) return null;
//     try {
//       const response = await getResult(`${batchId}/${studentId}`);
//       if (response?.success && response.result) {
//         let preTestScore = null, postTestScore = null;
//         const preTest = response.result.preTest;
//         const postTest = response.result.postTest;
//         if (preTest !== undefined && preTest !== null) preTestScore = typeof preTest === "number" ? preTest : (preTest.percentage || preTest.score || 0);
//         if (postTest !== undefined && postTest !== null) postTestScore = typeof postTest === "number" ? postTest : (postTest.percentage || postTest.score || 0);
//         return { preTest: preTestScore !== null ? parseFloat(preTestScore) : null, postTest: postTestScore !== null ? parseFloat(postTestScore) : null, studentId, batchId };
//       }
//       return null;
//     } catch (error) { return null; }
//   };

//   // ============================
//   // FETCH EXAM RESULTS FOR BATCH
//   // ============================
//   const fetchExamResultsForBatch = async (batchId, students) => {
//     if (!batchId || !students || students.length === 0) {
//       return { passed: 0, failed: 0, notAttempted: students?.length || 0, totalStudents: students?.length || 0, students: [], examResults: {} };
//     }
//     try {
//       const examResults = {};
//       let passed = 0, failed = 0, notAttempted = 0;
//       const studentDetails = [];
      
//       const results = await asyncPool(10, students, async (student) => {
//         const studentId = student.id || student._id;
//         if (!studentId) return null;
//         const result = await fetchStudentExamResult(batchId, studentId);
//         return { student, studentId, result };
//       });
      
//       for (const { student, studentId, result } of results) {
//         if (!student) continue;
//         const studentName = student.name || student.fullName || `${student.firstName || ""} ${student.lastName || ""}`.trim() || "Unknown";
//         const studentData = {
//           id: studentId,
//           name: studentName,
//           mobile: student.mobile || student.phone || "",
//           email: student.email || "",
//           aadhaarNo: student.aadhaarNo || student.aadhaar_number || student.tataStudentDetailsDto?.aadhaarNo || "--",
//           contractorToken: student.contractorToken || student.tataStudentDetailsDto?.contractorTokenNo || "--",
//           studentState: student.tataStudentDetailsDto?.state || student.state || "",
//           studentDepot: student.tataStudentDetailsDto?.depot || student.depot || "",
//           trainingState: student.tataStudentDetailsDto?.trainingState || "",
//           trainingDepot: student.tataStudentDetailsDto?.trainingDepot || "",
//           postTestScore: null, preTestScore: null, status: "Not Attempted", attempted: false, batchId: batchId
//         };
//         if (result && result.postTest !== null) {
//           examResults[studentId] = result;
//           studentData.postTestScore = result.postTest;
//           studentData.preTestScore = result.preTest;
//           studentData.attempted = true;
//           if (result.postTest >= 80) { passed++; studentData.status = "Passed"; }
//           else { failed++; studentData.status = "Failed"; }
//         } else { notAttempted++; }
//         studentDetails.push(studentData);
//       }
//       return { passed, failed, notAttempted, totalStudents: students.length, students: studentDetails, examResults };
//     } catch (error) {
//       console.error(`Error fetching exam results for batch ${batchId}:`, error);
//       return { passed: 0, failed: 0, notAttempted: students.length, totalStudents: students.length, students: [], examResults: {} };
//     }
//   };

//   // ============================
//   // INITIALIZE DATA WITH CACHE
//   // ============================
//   const initializeData = async () => {
//     const cachedBatches = loadFromCache(CACHE_KEYS.ALL_BATCHES);
//     const cachedTrainers = loadFromCache(CACHE_KEYS.BATCH_TRAINERS);
//     const cachedStudents = loadFromCache(CACHE_KEYS.BATCH_STUDENTS);
//     const cachedStats = loadFromCache(CACHE_KEYS.PASS_FAIL_STATS);

//     if (cachedBatches && cachedTrainers && cachedStudents && cachedStats) {
//       setAllBatch(cachedBatches);
//       setBatchTrainersMap(cachedTrainers);
//       setBatchStudentsMap(cachedStudents);
//       setBatchStudentsCount(Object.fromEntries(Object.entries(cachedStudents).map(([id, students]) => [id, students.length])));
//       setPassFailStatsMap(cachedStats);
      
//       const allPassed = [], allFailed = [], allNotAttempted = [];
//       Object.values(cachedStats).forEach(stats => {
//         if (stats.students) {
//           stats.students.forEach(s => {
//             const studentWithBatch = { ...s };
//             if (s.status === "Passed") allPassed.push(studentWithBatch);
//             else if (s.status === "Failed") allFailed.push(studentWithBatch);
//             else if (s.status === "Not Attempted") allNotAttempted.push(studentWithBatch);
//           });
//         }
//       });
//       setPassedStudentsList(allPassed);
//       setFailedStudentsList(allFailed);
//       setNotAttemptedStudentsList(allNotAttempted);
      
//       applyFilters(cachedBatches, cachedStats, Object.fromEntries(Object.entries(cachedStudents).map(([id, students]) => [id, students.length])));
//       updateCharts(cachedBatches, Object.fromEntries(Object.entries(cachedStudents).map(([id, students]) => [id, students.length])), cachedStats);
      
//       setIsCacheUsed(true);
//       setIsDataLoaded(true);
//       setLoader(false);
//       toast.info(`Loaded ${cachedBatches.length} batches from cache`, { autoClose: 2000 });
      
//       refreshDataInBackground();
//     } else {
//       await getAllBatches();
//     }
//   };

//   // ============================
//   // REFRESH DATA IN BACKGROUND
//   // ============================
//   const refreshDataInBackground = async () => {
//     setBackgroundLoading(true);
//     try {
//       const type = JSON.parse(localStorage.getItem("authUser"))?.userType;
//       const res = type === "Tata Trainer" ? await fetchAllBatchOfTrainer() : await fetchAllBatch();
      
//       if (res?.success) {
//         const batches = (res.result || []).map(batch => ({
//           ...batch, id: batch.id || batch._id, batchName: batch.batchName, department: batch.department,
//           state: batch.state, depot: batch.depot, trainingStartDate: batch.trainingStartDate,
//           trainingEndDate: batch.trainingEndDate, totalStudents: batch.totalStudents || batch.students?.length || 0,
//           passedStudents: batch.passedStudents || 0, failedStudents: batch.failedStudents || 0,
//           notAttemptedStudents: batch.notAttemptedStudents || 0, passPercentage: batch.passPercentage || 0,
//           trainerNames: batch.trainerNames || (batch.trainers?.map(t => t.name || t.firstName).join(", ") || "No Trainer Assigned"),
//           totalTrainers: batch.totalTrainers || batch.trainers?.length || 0,
//         }));
        
//         const trainersMap = {}, studentsMap = {}, studentsCountMap = {};
//         await asyncPool(5, batches, async (batch) => {
//           const batchId = batch.id;
//           if (!batchId) return;
//           try {
//             const [trainers, students] = await Promise.all([
//               getAllTrainerOfBatch(batchId).catch(() => []),
//               getAllStudentOfBatch(batchId).catch(() => []),
//             ]);
//             trainersMap[batchId] = trainers?.success ? trainers.result || [] : [];
//             studentsMap[batchId] = students?.success ? students.result || [] : [];
//             studentsCountMap[batchId] = studentsMap[batchId].length;
//           } catch (error) {
//             trainersMap[batchId] = [];
//             studentsMap[batchId] = [];
//             studentsCountMap[batchId] = 0;
//           }
//         });
        
//         const passFailMap = {};
//         const allFailed = [], allNotAttempted = [], allPassed = [];
        
//         await asyncPool(5, batches, async (batch) => {
//           const batchId = batch.id;
//           if (batchId && studentsMap[batchId]) {
//             const data = await fetchExamResultsForBatch(batchId, studentsMap[batchId]);
//             passFailMap[batchId] = data;
//             const batchInfo = batches.find(b => b.id === batchId);
//             data.students.forEach((student) => {
//               const studentWithBatch = { ...student, batchName: batchInfo?.batchName || "N/A", batchState: batchInfo?.state || "", batchDepot: batchInfo?.depot || "" };
//               if (student.status === "Failed") allFailed.push(studentWithBatch);
//               else if (student.status === "Not Attempted") allNotAttempted.push(studentWithBatch);
//               else if (student.status === "Passed") allPassed.push(studentWithBatch);
//             });
//           }
//         });
        
//         setAllBatch(batches);
//         setBatchTrainersMap(trainersMap);
//         setBatchStudentsMap(studentsMap);
//         setBatchStudentsCount(studentsCountMap);
//         setPassFailStatsMap(passFailMap);
//         setFailedStudentsList(allFailed);
//         setNotAttemptedStudentsList(allNotAttempted);
//         setPassedStudentsList(allPassed);
        
//         saveToCache(CACHE_KEYS.ALL_BATCHES, batches);
//         saveToCache(CACHE_KEYS.BATCH_TRAINERS, trainersMap);
//         saveToCache(CACHE_KEYS.BATCH_STUDENTS, studentsMap);
//         saveToCache(CACHE_KEYS.PASS_FAIL_STATS, passFailMap);
        
//         applyFilters(batches, passFailMap, studentsCountMap);
//         updateCharts(batches, studentsCountMap, passFailMap);
//       }
//     } catch (error) {
//       console.error("Background refresh failed:", error);
//     } finally {
//       setBackgroundLoading(false);
//     }
//   };

//   // ============================
//   // GET ALL BATCHES (Initial Load)
//   // ============================
//   const getAllBatches = async () => {
//     try {
//       setLoader(true);
//       const type = JSON.parse(localStorage.getItem("authUser"))?.userType;
//       const res = type === "Tata Trainer" ? await fetchAllBatchOfTrainer() : await fetchAllBatch();

//       if (res?.success) {
//         const batches = (res.result || []).map(batch => ({
//           ...batch, id: batch.id || batch._id, batchName: batch.batchName, department: batch.department,
//           state: batch.state, depot: batch.depot, trainingStartDate: batch.trainingStartDate,
//           trainingEndDate: batch.trainingEndDate, totalStudents: batch.totalStudents || batch.students?.length || 0,
//           passedStudents: batch.passedStudents || 0, failedStudents: batch.failedStudents || 0,
//           notAttemptedStudents: batch.notAttemptedStudents || 0, passPercentage: batch.passPercentage || 0,
//           trainerNames: batch.trainerNames || (batch.trainers?.map(t => t.name || t.firstName).join(", ") || "No Trainer Assigned"),
//           totalTrainers: batch.totalTrainers || batch.trainers?.length || 0,
//         }));
        
//         const trainersMap = {}, studentsMap = {}, studentsCountMap = {};
//         setFetchProgress({ active: true, current: 0, total: batches.length, message: "Loading batch data..." });
        
//         await asyncPool(5, batches, async (batch) => {
//           const batchId = batch.id;
//           if (!batchId) return;
//           try {
//             const [trainers, students] = await Promise.all([
//               getAllTrainerOfBatch(batchId).catch(() => []),
//               getAllStudentOfBatch(batchId).catch(() => []),
//             ]);
//             trainersMap[batchId] = trainers?.success ? trainers.result || [] : [];
//             studentsMap[batchId] = students?.success ? students.result || [] : [];
//             studentsCountMap[batchId] = studentsMap[batchId].length;
//           } catch (error) {
//             trainersMap[batchId] = [];
//             studentsMap[batchId] = [];
//             studentsCountMap[batchId] = 0;
//           }
//         });
        
//         const passFailMap = {};
//         const allFailed = [], allNotAttempted = [], allPassed = [];
        
//         setFetchProgress({ active: true, current: 0, total: batches.length, message: "Fetching exam results..." });
        
//         let processed = 0;
//         await asyncPool(5, batches, async (batch) => {
//           const batchId = batch.id;
//           if (batchId && studentsMap[batchId]) {
//             const data = await fetchExamResultsForBatch(batchId, studentsMap[batchId]);
//             passFailMap[batchId] = data;
//             data.students.forEach((student) => {
//               const studentWithBatch = { ...student, batchName: batch.batchName || "N/A", batchState: batch.state || "", batchDepot: batch.depot || "" };
//               if (student.status === "Failed") allFailed.push(studentWithBatch);
//               else if (student.status === "Not Attempted") allNotAttempted.push(studentWithBatch);
//               else if (student.status === "Passed") allPassed.push(studentWithBatch);
//             });
//           }
//           processed++;
//           setFetchProgress(prev => ({ ...prev, current: processed }));
//         });
        
//         setAllBatch(batches);
//         setBatchTrainersMap(trainersMap);
//         setBatchStudentsMap(studentsMap);
//         setBatchStudentsCount(studentsCountMap);
//         setPassFailStatsMap(passFailMap);
//         setFailedStudentsList(allFailed);
//         setNotAttemptedStudentsList(allNotAttempted);
//         setPassedStudentsList(allPassed);
        
//         saveToCache(CACHE_KEYS.ALL_BATCHES, batches);
//         saveToCache(CACHE_KEYS.BATCH_TRAINERS, trainersMap);
//         saveToCache(CACHE_KEYS.BATCH_STUDENTS, studentsMap);
//         saveToCache(CACHE_KEYS.PASS_FAIL_STATS, passFailMap);
        
//         applyFilters(batches, passFailMap, studentsCountMap);
//         updateCharts(batches, studentsCountMap, passFailMap);
        
//         toast.success(`Loaded ${batches.length} batches | ${allPassed.length} Passed | ${allFailed.length} Failed | ${allNotAttempted.length} Not Attempted`);
//       } else {
//         toast.error(res?.message || "Failed to load batches");
//       }
//     } catch (error) {
//       console.error("Error in getAllBatches:", error);
//       toast.error(error?.response?.data?.message || "Failed to load batches");
//     } finally {
//       setLoader(false);
//       setFetchProgress({ active: false, current: 0, total: 0, message: "" });
//       setIsDataLoaded(true);
//     }
//   };

//   // ============================
//   // APPLY FILTERS
//   // ============================
//   const applyFilters = (batches = allBatch, passMap = passFailStatsMap, studentsCount = batchStudentsCount) => {
//     let filtered = [...batches];
    
//     if (selectedState && selectedState !== "") {
//       filtered = filtered.filter(batch => batch.state === selectedState);
//     }
    
//     if (selectedDepot && selectedDepot !== "") {
//       filtered = filtered.filter(batch => batch.depot === selectedDepot);
//     }
    
//     if (datePeriod && datePeriod !== "ALL") {
//       const today = moment().startOf('day');
//       filtered = filtered.filter(batch => {
//         if (!batch.trainingStartDate) return false;
//         let startDate = parseDate(batch.trainingStartDate);
//         if (!startDate || !startDate.isValid()) return false;
//         startDate = startDate.startOf('day');
//         switch (datePeriod) {
//           case "TODAY": return startDate.isSame(today, 'day');
//           case "WEEKLY": return startDate.isSame(today, 'week');
//           case "MONTHLY": return startDate.isSame(today, 'month');
//           case "YEARLY": return startDate.isSame(today, 'year');
//           default: return true;
//         }
//       });
//     }
    
//     if (customStartDate && customEndDate) {
//       const start = moment(customStartDate).startOf('day');
//       const end = moment(customEndDate).endOf('day');
//       filtered = filtered.filter(batch => {
//         if (!batch.trainingStartDate) return false;
//         const batchDate = parseDate(batch.trainingStartDate);
//         if (!batchDate || !batchDate.isValid()) return false;
//         return batchDate.isBetween(start, end, null, '[]');
//       });
//     }
    
//     if (searchText) {
//       const search = searchText.toLowerCase().trim();
//       filtered = filtered.filter(batch => {
//         const trainers = batchTrainersMap[batch.id] || [];
//         const trainerNames = trainers.map(t => t.name || `${t.firstName || ""} ${t.lastName || ""}`).join(" ").toLowerCase();
//         return (batch.batchName || "").toLowerCase().includes(search) ||
//           (batch.department || "").toLowerCase().includes(search) ||
//           (batch.depot || "").toLowerCase().includes(search) ||
//           (batch.state || "").toLowerCase().includes(search) ||
//           trainerNames.includes(search) ||
//           (batch.trainerNames || "").toLowerCase().includes(search) ||
//           formatDateToDDMMYYYY(batch.trainingStartDate).includes(search) ||
//           formatDateToDDMMYYYY(batch.trainingEndDate).includes(search);
//       });
//     }
    
//     setFilteredBatches(filtered);
//   };

//   // Update charts
//   const updateCharts = (batches, studentsCountMap, passFailMap) => {
//     const totalStudents = Object.values(studentsCountMap).reduce((a, b) => a + (b || 0), 0);
//     const totalPassed = Object.values(passFailMap).reduce((a, b) => a + (b.passed || 0), 0);
//     const totalFailed = Object.values(passFailMap).reduce((a, b) => a + (b.failed || 0), 0);
//     const totalNotAttempted = Object.values(passFailMap).reduce((a, b) => a + (b.notAttempted || 0), 0);
    
//     setStatusDistributionChart({
//       options: {
//         chart: { type: "donut" },
//         labels: ["Passed (≥80%)", "Failed (<80%)", "Not Attempted"],
//         colors: ["#48bb78", "#ed8936", "#ecc94b"],
//         legend: { position: "bottom" },
//         plotOptions: { pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Total", formatter: () => totalStudents } } } } },
//       },
//       series: [totalPassed, totalFailed, totalNotAttempted],
//     });
    
//     const topBatches = batches
//       .map((b) => ({ name: b.batchName || "N/A", students: studentsCountMap[b.id] || 0 }))
//       .sort((a, b) => b.students - a.students)
//       .slice(0, 10);
//     setTopBatchesChart({
//       options: {
//         chart: { type: "bar" },
//         plotOptions: { bar: { horizontal: true } },
//         xaxis: { categories: topBatches.map((d) => d.name.substring(0, 20)) },
//         colors: ["#4299e1"],
//         title: { text: "Top 10 Batches by Student Count", align: "left", style: { fontSize: "14px", fontWeight: "bold" } },
//       },
//       series: [{ name: "Students", data: topBatches.map((d) => d.students) }],
//     });
    
//     const perf = batches
//       .map((b) => {
//         const total = studentsCountMap[b.id] || 0;
//         const passed = passFailMap[b.id]?.passed || 0;
//         return { name: b.batchName || "N/A", passRate: total > 0 ? (passed / total) * 100 : 0 };
//       })
//       .sort((a, b) => b.passRate - a.passRate)
//       .slice(0, 10);
//     setBatchStatsChart({
//       options: {
//         chart: { type: "bar" },
//         plotOptions: { bar: { borderRadius: 6 } },
//         xaxis: { categories: perf.map((d) => d.name.substring(0, 20)) },
//         colors: ["#48bb78"],
//         title: { text: "Top 10 Batches by Pass Rate", align: "left" },
//         yaxis: { title: { text: "Pass Rate (%)" }, max: 100 },
//       },
//       series: [{ name: "Pass Rate (%)", data: perf.map((d) => d.passRate) }],
//     });
//   };

//   // Apply filters when dependencies change
//   useEffect(() => {
//     if (allBatch.length > 0) {
//       applyFilters();
//     }
//   }, [datePeriod, customStartDate, customEndDate, selectedState, selectedDepot, searchText, allBatch]);

//   // Refresh data
//   const refreshData = () => {
//     clearCache();
//     getAllBatches();
//     toast.info("Refreshing data from server...");
//   };

//   // Get filtered student lists
//   const getFilteredFailedStudents = () => {
//     const filteredBatchIds = new Set(filteredBatches.map(b => b.id));
//     return failedStudentsList.filter(s => filteredBatchIds.has(s.batchId));
//   };

//   const getFilteredNotAttemptedStudents = () => {
//     const filteredBatchIds = new Set(filteredBatches.map(b => b.id));
//     return notAttemptedStudentsList.filter(s => filteredBatchIds.has(s.batchId));
//   };

//   const getFilteredPassedStudents = () => {
//     const filteredBatchIds = new Set(filteredBatches.map(b => b.id));
//     return passedStudentsList.filter(s => filteredBatchIds.has(s.batchId));
//   };

//   // Export student lists to Excel
//   const exportStudentsToExcel = (list, filename, includeDivisionState = true) => {
//     if (!list.length) {
//       toast.error(`No ${filename.replace("_", " ")} to export`);
//       return;
//     }
//     const exportData = list.map((s, idx) => ({
//       "S.No": idx + 1,
//       "Student Name": s.name,
//       "Mobile": s.mobile,
//       "Email": s.email,
//       "Aadhaar No": s.aadhaarNo,
//       "Contractor Token": s.contractorToken,
//       "Post-Test Score": s.postTestScore ? `${s.postTestScore}%` : "N/A",
//       "Pre-Test Score": s.preTestScore ? `${s.preTestScore}%` : "N/A",
//       "Status": s.status,
//       "Student Division": s.studentDivision || "N/A",
//       "Student State": s.studentState || "N/A",
//       "Student Depot": s.studentDepot || "N/A",
//       "Training State": s.trainingState || "N/A",
//       "Training Depot": s.trainingDepot || "N/A",
//       "Batch Name": s.batchName,
//       "Batch State": s.batchState || "N/A",
//       "Batch Depot": s.batchDepot || "N/A",
//     }));
//     const ws = XLSX.utils.json_to_sheet(exportData);
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, filename);
//     XLSX.writeFile(wb, `${filename}_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
//     toast.success(`Exported ${list.length} students`);
//   };

//   // Export all filtered batches to Excel
//   const downloadAllBatchesExcel = () => {
//     if (!filteredBatches.length) {
//       toast.error("No data to export");
//       return;
//     }
//     const data = filteredBatches.map((b, idx) => {
//       const stats = passFailStatsMap[b.id] || { passed: 0, failed: 0, notAttempted: 0 };
//       const trainers = batchTrainersMap[b.id] || [];
//       return {
//         "S.No": idx + 1,
//         "Batch Name": b.batchName || "",
//         "Department": b.department || "",
//         "Trainer(s)": formatTrainerNames(trainers),
//         "Start Date": formatDateToDDMMYYYY(b.trainingStartDate),
//         "End Date": formatDateToDDMMYYYY(b.trainingEndDate),
//         "State": b.state || "N/A",
//         "Depot": b.depot || "N/A",
//         "Total Students": batchStudentsCount[b.id] || 0,
//         "Passed (≥80%)": stats.passed || 0,
//         "Failed (<80%)": stats.failed || 0,
//         "Not Attempted": stats.notAttempted || 0,
//         "Pass %": batchStudentsCount[b.id] ? ((stats.passed / batchStudentsCount[b.id]) * 100).toFixed(2) + "%" : "0%",
//       };
//     });
//     const ws = XLSX.utils.json_to_sheet(data);
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, "Batches");
//     XLSX.writeFile(wb, `All_Batches_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
//     toast.success("Excel file downloaded!");
//   };

//   // ============================
//   // BATCH REPORT FUNCTIONS
//   // ============================
//   const fetchAndProcessBatchReport = async (batch, reportType) => {
//     try {
//       const batchId = batch.id;
//       if (!batchId) return { success: false, error: "No batch ID" };
//       const apiUrl = `${downloadBatchReport}${batchId}?type=${reportType}`;
//       const response = await axios.get(apiUrl, { responseType: "blob", timeout: 30000 });
//       const zip = await JSZip.loadAsync(response.data);
//       const sheets = [];
//       const excelFiles = Object.keys(zip.files).filter(f => f.endsWith(".xlsx") || f.endsWith(".xls") || f.endsWith(".csv"));
//       if (excelFiles.length === 0) return { success: false, error: "No Excel files found in report" };
//       for (const excelFile of excelFiles) {
//         const fileContent = await zip.files[excelFile].async("blob");
//         const workbook = XLSX.read(await fileContent.arrayBuffer(), { type: "array" });
//         for (const sheetName of workbook.SheetNames) {
//           const worksheet = workbook.Sheets[sheetName];
//           const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
//           sheets.push({ name: sheetName, data, fileName: excelFile });
//         }
//       }
//       return { success: true, batchId, batchName: batch.batchName, reportType, sheets };
//     } catch (error) {
//       return { success: false, error: error.message };
//     }
//   };

//   const fetchFilteredBatchReports = async (batches, reportType) => {
//     const reportData = {};
//     const missingReportsList = [];
//     const total = batches.length;
//     setFetchProgress({ active: true, current: 0, total, message: `Fetching ${reportType} reports for ${total} batches...` });
//     let processed = 0;
//     for (const batch of batches) {
//       const batchId = batch.id;
//       if (!batchId) continue;
//       const result = await fetchAndProcessBatchReport(batch, reportType);
//       if (result.success) reportData[batchId] = result;
//       else missingReportsList.push({ batchId, batchName: batch.batchName || "Unknown", error: result.error });
//       processed++;
//       setFetchProgress(prev => ({ ...prev, current: processed }));
//     }
//     setBatchReportData(reportData);
//     setMissingReports(missingReportsList);
//     setFetchProgress({ active: false, current: 0, total: 0, message: "" });
//     if (missingReportsList.length > 0) toast.warning(`${missingReportsList.length} batch(es) have missing reports`);
//     return reportData;
//   };

//   const generateSheetPerBatchExcelReport = async () => {
//     const wb = XLSX.utils.book_new();
//     const sortedBatches = [...filteredBatches].sort((a, b) => (a.batchName || "").localeCompare(b.batchName || ""));
//     const coverData = [
//       [`${consolidatedReportType} REPORTS - SHEET PER BATCH`],
//       [`Generated: ${new Date().toLocaleString()}`],
//       [`Total Batches: ${sortedBatches.length}`],
//       [`Batches with Reports: ${Object.keys(batchReportData).length}`],
//       [`Batches with Missing Reports: ${missingReports.length}`],
//       [],
//     ];
//     if (missingReports.length > 0) {
//       coverData.push(["⚠️ MISSING REPORTS ⚠️"]);
//       coverData.push(["S.No", "Batch Name", "Error"]);
//       missingReports.forEach((report, idx) => coverData.push([idx + 1, report.batchName, report.error]));
//       coverData.push([]);
//     }
//     coverData.push(["BATCH INDEX"]);
//     coverData.push(["S.No", "Batch Serial No", "Batch Name", "Start Date", "End Date", "Trainer Name", "Total Students", "Passed", "Failed", "Not Attempted", "Pass %", "Sheet Name", "Report Status"]);
//     for (let i = 0; i < sortedBatches.length; i++) {
//       const batch = sortedBatches[i];
//       const batchId = batch.id;
//       const students = batchStudentsMap[batchId] || [];
//       const stats = passFailStatsMap[batchId] || { passed: 0, failed: 0, notAttempted: 0 };
//       const passPercent = students.length > 0 ? ((stats.passed / students.length) * 100).toFixed(2) : 0;
//       const trainers = batchTrainersMap[batchId] || [];
//       const hasReport = batchReportData[batchId] ? "✅ Available" : "❌ Missing";
//       let sheetName = (batch.batchName || `Batch_${i + 1}`).replace(/[\[\]\*\/\\\?\:]/g, "_").substring(0, 25);
//       coverData.push([i + 1, `BATCH-${String(i + 1).padStart(3, '0')}`, batch.batchName || "N/A", formatDateToDDMMYYYY(batch.trainingStartDate), formatDateToDDMMYYYY(batch.trainingEndDate), formatTrainerNames(trainers), students.length, stats.passed || 0, stats.failed || 0, stats.notAttempted || 0, `${passPercent}%`, hasReport === "✅ Available" ? sheetName : "NO REPORT AVAILABLE", hasReport]);
//     }
//     const wsCover = XLSX.utils.aoa_to_sheet(coverData);
//     XLSX.utils.book_append_sheet(wb, wsCover, "Index");
    
//     for (let i = 0; i < sortedBatches.length; i++) {
//       const batch = sortedBatches[i];
//       const batchId = batch.id;
//       const report = batchReportData[batchId];
//       let sheetName = (batch.batchName || `Batch_${i + 1}`).replace(/[\[\]\*\/\\\?\:]/g, "_").substring(0, 25);
//       if (wb.SheetNames.includes(sheetName)) sheetName = `${sheetName}_${i + 1}`;
//       let allSheetData = [];
//       if (report && report.sheets?.length > 0) {
//         allSheetData.push([`Report: ${batch.batchName || "N/A"}`]);
//         allSheetData.push([`Batch Serial Number: BATCH-${String(i + 1).padStart(3, '0')}`]);
//         allSheetData.push([`Start Date: ${formatDateToDDMMYYYY(batch.trainingStartDate)}`]);
//         allSheetData.push([`End Date: ${formatDateToDDMMYYYY(batch.trainingEndDate)}`]);
//         allSheetData.push([`State: ${batch.state || "N/A"}`]);
//         allSheetData.push([`Depot: ${batch.depot || "N/A"}`]);
//         allSheetData.push([`Report Type: ${consolidatedReportType}`]);
//         allSheetData.push([`Generated: ${new Date().toLocaleString()}`]);
//         allSheetData.push([]);
//         report.sheets.forEach((sheet, idx) => {
//           if (idx > 0) allSheetData.push([]);
//           allSheetData.push([`Sheet: ${sheet.name}`]);
//           allSheetData.push([]);
//           if (sheet.data?.length > 0) allSheetData = [...allSheetData, ...sheet.data];
//           else allSheetData.push(["No data found in sheet"]);
//         });
//       } else {
//         allSheetData.push([`⚠️ REPORT NOT AVAILABLE ⚠️`]);
//         allSheetData.push([`Batch: ${batch.batchName || "N/A"}`]);
//         allSheetData.push([`Batch Serial Number: BATCH-${String(i + 1).padStart(3, '0')}`]);
//         allSheetData.push([`Start Date: ${formatDateToDDMMYYYY(batch.trainingStartDate)}`]);
//         allSheetData.push([`End Date: ${formatDateToDDMMYYYY(batch.trainingEndDate)}`]);
//         allSheetData.push([`State: ${batch.state || "N/A"}`]);
//         allSheetData.push([`Depot: ${batch.depot || "N/A"}`]);
//         allSheetData.push([]);
//         allSheetData.push([`Reason: Report could not be fetched from server`]);
//         allSheetData.push([]);
//         allSheetData.push([`Batch Statistics:`]);
//         const students = batchStudentsMap[batchId] || [];
//         const stats = passFailStatsMap[batchId] || { passed: 0, failed: 0, notAttempted: 0 };
//         allSheetData.push([`Total Students: ${students.length}`]);
//         allSheetData.push([`Passed: ${stats.passed || 0}`]);
//         allSheetData.push([`Failed: ${stats.failed || 0}`]);
//         allSheetData.push([`Not Attempted: ${stats.notAttempted || 0}`]);
//       }
//       const wsBatch = XLSX.utils.aoa_to_sheet(allSheetData);
//       XLSX.utils.book_append_sheet(wb, wsBatch, sheetName);
//     }
//     const fileName = `${consolidatedReportType}_Reports_${new Date().toISOString().split("T")[0]}.xlsx`;
//     const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
//     saveAs(new Blob([buffer]), fileName);
//     toast.success(`Report generated successfully!`);
//   };

//   const openReportModal = (batch) => {
//     setSelectedBatch(batch);
//     setSelectedReportType(null);
//     setReportModalShow(true);
//   };

//   const fetchSingleBatchReportDirectly = async (batch, reportTypeValue) => {
//     try {
//       const batchId = batch.id;
//       if (!batchId) return null;
//       const apiUrl = `${downloadBatchReport}${batchId}?type=${reportTypeValue}`;
//       const response = await axios.get(apiUrl, { responseType: "blob", timeout: 30000 });
//       return new Blob([response.data], { type: "application/zip" });
//     } catch (error) { return null; }
//   };

//   const confirmDownloadReport = async () => {
//     if (!selectedReportType) { toast.error("Please select a report type!"); return; }
//     try {
//       setLoader(true);
//       setReportModalShow(false);
//       const reportBlob = await fetchSingleBatchReportDirectly(selectedBatch, selectedReportType.value);
//       if (!reportBlob) { toast.error("Failed to fetch report"); return; }
//       const downloadUrl = window.URL.createObjectURL(reportBlob);
//       const link = document.createElement("a");
//       link.href = downloadUrl;
//       link.setAttribute("download", `Batch_${selectedBatch.batchName}_${selectedReportType.value}_Report.zip`);
//       document.body.appendChild(link);
//       link.click();
//       link.remove();
//       window.URL.revokeObjectURL(downloadUrl);
//       toast.success(`${selectedReportType.label} downloaded!`);
//     } catch (error) { toast.error("Error downloading report"); }
//     finally { setLoader(false); }
//   };

//   const openAllReportsModal = () => { setSelectedAllReportType(null); setAllReportsModalShow(true); };

//   const confirmDownloadAllReports = async () => {
//     if (!selectedAllReportType) { toast.error("Please select a report type!"); return; }
//     try {
//       setLoader(true);
//       setAllReportsModalShow(false);
//       const batches = filteredBatches;
//       if (batches.length === 0) { toast.error("No batches to download"); setLoader(false); return; }
//       setFetchProgress({ active: true, current: 0, total: batches.length, message: `Downloading ${selectedAllReportType.label} for ${batches.length} batches...` });
//       const mainZip = new JSZip();
//       const timestamp = new Date().toISOString().split("T")[0];
//       const reportsFolder = mainZip.folder(`All_Reports_${selectedAllReportType.value}_${timestamp}`);
//       let successCount = 0, failCount = 0;
//       const failedBatches = [];
//       await asyncPool(3, batches, async (batch) => {
//         const batchId = batch.id;
//         if (!batchId) return;
//         try {
//           const reportBlob = await fetchSingleBatchReportDirectly(batch, selectedAllReportType.value);
//           if (!reportBlob) { failedBatches.push(batch.batchName); failCount++; return; }
//           const batchFolder = reportsFolder.folder(`${batch.batchName || batchId}_${batchId}`);
//           batchFolder.file(`${selectedAllReportType.value}_Report.zip`, reportBlob);
//           successCount++;
//         } catch (error) { failedBatches.push(batch.batchName); failCount++; }
//       });
//       const summaryReport = ["DOWNLOAD SUMMARY", "================", `Report Type: ${selectedAllReportType.label}`, `Date: ${new Date().toLocaleString()}`, `Total Batches: ${batches.length}`, `Successfully Downloaded: ${successCount}`, `Failed: ${failCount}`, "", failCount > 0 ? "FAILED BATCHES:" : "All batches downloaded successfully!", ...failedBatches.map((name, i) => `${i + 1}. ${name}`)].join("\n");
//       reportsFolder.file("Download_Summary.txt", summaryReport);
//       setFetchProgress({ active: false, current: 0, total: 0, message: "" });
//       const finalZipBlob = await mainZip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
//       const downloadUrl = window.URL.createObjectURL(finalZipBlob);
//       const link = document.createElement("a");
//       link.href = downloadUrl;
//       link.setAttribute("download", `All_Batches_${selectedAllReportType.value}_${timestamp}.zip`);
//       document.body.appendChild(link);
//       link.click();
//       link.remove();
//       window.URL.revokeObjectURL(downloadUrl);
//       if (failCount === 0) toast.success(`All ${successCount} reports downloaded successfully!`);
//       else toast.warning(`${successCount} downloaded, ${failCount} failed. Check summary for details.`);
//     } catch (error) { toast.error("Error downloading reports"); }
//     finally { setLoader(false); }
//   };

//   const openSheetPerBatchModal = () => setConsolidatedModalShow(true);

//   const confirmSheetPerBatchReport = async () => {
//     try {
//       setLoader(true);
//       setConsolidatedModalShow(false);
//       const batches = filteredBatches;
//       if (batches.length === 0) { toast.error("No batches to process"); setLoader(false); return; }
//       await fetchFilteredBatchReports(batches, consolidatedReportType);
//       await generateSheetPerBatchExcelReport();
//     } catch (error) { toast.error("Error generating report"); }
//     finally { setLoader(false); }
//   };

//   useEffect(() => { initializeData(); }, []);

//   const totalStudents = getFilteredPassedStudents().length + getFilteredFailedStudents().length + getFilteredNotAttemptedStudents().length;
//   const totalPassed = getFilteredPassedStudents().length;
//   const totalFailed = getFilteredFailedStudents().length;
//   const totalNotAttempted = getFilteredNotAttemptedStudents().length;
//   const overallPassRate = totalStudents > 0 ? ((totalPassed / totalStudents) * 100).toFixed(1) : 0;

//   const rows = filteredBatches.map((batch, idx) => {
//     const stats = passFailStatsMap[batch.id] || { passed: 0, failed: 0, notAttempted: 0 };
//     const trainers = batchTrainersMap[batch.id] || [];
//     return {
//       id: batch.id,
//       serialNo: idx + 1,
//       batchName: batch.batchName || "N/A",
//       department: batch.department || "N/A",
//       trainers: trainers.map(t => t.name || `${t.firstName || ""} ${t.lastName || ""}`.trim()),
//       startDate: formatDateToDDMMYYYY(batch.trainingStartDate),
//       endDate: formatDateToDDMMYYYY(batch.trainingEndDate),
//       state: batch.state || "N/A",
//       depot: batch.depot || "N/A",
//       studentsCount: batchStudentsCount[batch.id] || 0,
//       passedCount: stats.passed || 0,
//       failedCount: stats.failed || 0,
//       notAttemptedCount: stats.notAttempted || 0,
//       passPercentage: batchStudentsCount[batch.id] ? ((stats.passed / batchStudentsCount[batch.id]) * 100).toFixed(1) : 0,
//       originalBatch: batch,
//     };
//   });

//   return (
//     <>
//       {loader && <Loader />}
      
//       {backgroundLoading && !loader && (
//         <div style={{ position: "fixed", bottom: "20px", right: "20px", zIndex: 9999, backgroundColor: "#28a745", color: "white", padding: "8px 16px", borderRadius: "20px", fontSize: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.2)", display: "flex", alignItems: "center", gap: "8px" }}>
//           <Spinner size="sm" style={{ color: "white" }} />
//           <span>Updating data in background...</span>
//         </div>
//       )}
      
//       {isCacheUsed && !backgroundLoading && !loader && (
//         <div style={{ position: "fixed", bottom: "20px", right: "20px", zIndex: 9999, backgroundColor: "#17a2b8", color: "white", padding: "6px 12px", borderRadius: "20px", fontSize: "11px", boxShadow: "0 2px 10px rgba(0,0,0,0.2)", cursor: "pointer" }} onClick={() => toast.info("Data loaded from cache. Click Refresh to get latest data.", { autoClose: 3000 })}>
//           <i className="fas fa-database me-1"></i> Cached Data
//         </div>
//       )}

//       {fetchProgress.active && (
//         <div style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 9999, backgroundColor: "rgba(255,255,255,0.95)", padding: "15px 20px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
//           <div className="d-flex justify-content-between align-items-center mb-2">
//             <span className="fw-bold text-primary"><i className="fas fa-spinner fa-spin me-2"></i>{fetchProgress.message}</span>
//             <span className="text-muted">{fetchProgress.current}/{fetchProgress.total}</span>
//           </div>
//           <Progress value={fetchProgress.total > 0 ? (fetchProgress.current / fetchProgress.total) * 100 : 0} />
//         </div>
//       )}

//       {/* Stats Cards Row */}
//       <Row className="mb-4">
//         <Col md={2}>
//           <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
//             <CardBody className="text-center p-2"><GroupsIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} /><h6 className="mb-0">Filtered Batches</h6><h2 className="mb-0 fw-bold">{filteredBatches.length}</h2></CardBody>
//           </Card>
//         </Col>
//         <Col md={2}>
//           <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)", color: "white" }}>
//             <CardBody className="text-center p-2"><SchoolIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} /><h6 className="mb-0">Total Students</h6><h2 className="mb-0 fw-bold">{totalStudents}</h2></CardBody>
//           </Card>
//         </Col>
//         <Col md={2}>
//           <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)", color: "white", cursor: "pointer" }} onClick={() => setShowFailedModal(true)}>
//             <CardBody className="text-center p-2"><ThumbDownIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} /><h6 className="mb-0">Failed Students</h6><h2 className="mb-0 fw-bold">{totalFailed}</h2><small>Click to view</small></CardBody>
//           </Card>
//         </Col>
//         <Col md={2}>
//           <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)", color: "white", cursor: "pointer" }} onClick={() => setShowNotAttemptedModal(true)}>
//             <CardBody className="text-center p-2"><RemoveCircleIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} /><h6 className="mb-0">Not Attempted</h6><h2 className="mb-0 fw-bold">{totalNotAttempted}</h2><small>Click to view</small></CardBody>
//           </Card>
//         </Col>
//         <Col md={2}>
//           <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #48bb78 0%, #2c7a4d 100%)", color: "white", cursor: "pointer" }} onClick={() => setShowPassedModal(true)}>
//             <CardBody className="text-center p-2"><ThumbUpIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} /><h6 className="mb-0">Passed Students</h6><h2 className="mb-0 fw-bold">{totalPassed}</h2><small>Click to view</small></CardBody>
//           </Card>
//         </Col>
//         <Col md={2}>
//           <Card className="shadow-sm border-0" style={{ background: "linear-gradient(135deg, #4299e1 0%, #2b6cb0 100%)", color: "white" }}>
//             <CardBody className="text-center p-2"><TrendingUpIcon style={{ fontSize: "2rem", marginBottom: "5px", opacity: 0.9 }} /><h6 className="mb-0">Pass Rate</h6><h2 className="mb-0 fw-bold">{overallPassRate}%</h2><small>{totalPassed} out of {totalStudents}</small></CardBody>
//           </Card>
//         </Col>
//       </Row>

//       <Card className="mt-2">
//         <CardBody>
//           <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
//             <div>
//               <h5 className="card-title mb-0 d-flex align-items-center">
//                 <GroupsIcon style={{ marginRight: "10px", color: "#1e3c72" }} />
//                 Batch Details Dashboard
//               </h5>
//               <div className="mt-2">
//                 <Badge color="primary" className="me-2 p-2"><span className="fw-bold">{filteredBatches.length}</span> Batches</Badge>
//                 <Badge color="success" className="me-2 p-2"><span className="fw-bold">{totalStudents}</span> Students</Badge>
//                 <Badge color="success" className="me-2 p-2"><span className="fw-bold">{totalPassed}</span> Passed</Badge>
//                 <Badge color="danger" className="me-2 p-2" style={{ cursor: "pointer" }} onClick={() => setShowFailedModal(true)}><span className="fw-bold">{totalFailed}</span> Failed</Badge>
//                 <Badge color="warning" className="me-2 p-2" style={{ cursor: "pointer" }} onClick={() => setShowNotAttemptedModal(true)}><span className="fw-bold">{totalNotAttempted}</span> Not Attempted</Badge>
//                 <Badge color="info" className="p-2"><span className="fw-bold">{overallPassRate}%</span> Pass Rate</Badge>
//               </div>
//             </div>
//             <div className="d-flex gap-2 flex-wrap">
//               <Input type="text" placeholder="🔍 Search..." value={searchText} onChange={(e) => setSearchText(e.target.value)} style={{ width: "250px" }} className="border-primary" />
//               <Button color="info" size="sm" onClick={downloadAllBatchesExcel}><i className="fas fa-download me-1"></i> Export Excel</Button>
//               <Button color="success" size="sm" onClick={openAllReportsModal}><i className="fas fa-file-archive me-1"></i> Download All</Button>
//               <Button color="warning" size="sm" onClick={openSheetPerBatchModal}><i className="fas fa-table me-1"></i> Sheet/Batch</Button>
//               <Button color="primary" size="sm" onClick={refreshData}><RefreshIcon style={{ fontSize: "1rem" }} /> Refresh</Button>
//             </div>
//           </div>

//           {/* Charts Section */}
//           <Row className="mb-4">
//             <Col md={4}>
//               <Card className="shadow-sm h-100 border-0">
//                 <CardBody>
//                   <h6 className="mb-3 d-flex align-items-center"><PieChartIcon style={{ marginRight: "8px", color: "#1e3c72" }} /> Student Status Distribution</h6>
//                   {statusDistributionChart && <Chart options={statusDistributionChart.options} series={statusDistributionChart.series} type="donut" height={300} />}
//                 </CardBody>
//               </Card>
//             </Col>
//             <Col md={4}>
//               <Card className="shadow-sm h-100 border-0">
//                 <CardBody>
//                   <h6 className="mb-3 d-flex align-items-center"><BarChartIcon style={{ marginRight: "8px", color: "#1e3c72" }} /> Top 10 Batches by Student Count</h6>
//                   {topBatchesChart && <Chart options={topBatchesChart.options} series={topBatchesChart.series} type="bar" height={280} />}
//                 </CardBody>
//               </Card>
//             </Col>
//             <Col md={4}>
//               <Card className="shadow-sm h-100 border-0">
//                 <CardBody>
//                   <h6 className="mb-3 d-flex align-items-center"><TrendingUpIcon style={{ marginRight: "8px", color: "#1e3c72" }} /> Top 10 Batches by Pass Rate</h6>
//                   {batchStatsChart && <Chart options={batchStatsChart.options} series={batchStatsChart.series} type="bar" height={280} />}
//                 </CardBody>
//               </Card>
//             </Col>
//           </Row>

//           {/* Batches Table */}
//           <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
//             <table className="table table-hover table-sm">
//               <thead className="table-primary sticky-top">
//                 <tr>
//                   <th>S.No</th><th>Batch Name</th><th>Department</th><th>Trainer(s)</th><th>Start Date</th><th>End Date</th>
//                   <th>State</th><th>Depot</th><th>Students</th><th>Passed</th><th>Failed</th><th>Not Attempted</th><th>Pass %</th><th>Actions</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {rows.map((row) => (
//                   <React.Fragment key={row.id}>
//                     <tr onClick={() => setExpandedBatch(expandedBatch === row.id ? null : row.id)} style={{ cursor: "pointer" }}>
//                       <td className="fw-bold">{row.serialNo}</td>
//                       <td><span style={{ fontWeight: 600, color: "#1e3c72" }}>{row.batchName}</span></td>
//                       <td>{row.department}</td>
//                       <td>{row.trainers[0]}{row.trainers.length > 1 && <Badge color="secondary" className="ms-1" pill>+{row.trainers.length - 1}</Badge>}</td>
//                       <td>{row.startDate}</td>
//                       <td>{row.endDate}</td>
//                       <td>{row.state}</td>
//                       <td>{row.depot}</td>
//                       <td><span className="fw-bold text-primary">{row.studentsCount}</span></td>
//                       <td><span className="text-success fw-bold">{row.passedCount}</span></td>
//                       <td><span className="text-danger fw-bold" style={{ cursor: "pointer", textDecoration: "underline" }} onClick={(e) => { e.stopPropagation(); setShowFailedModal(true); }}>{row.failedCount}</span></td>
//                       <td><span className="text-warning fw-bold" style={{ cursor: "pointer", textDecoration: "underline" }} onClick={(e) => { e.stopPropagation(); setShowNotAttemptedModal(true); }}>{row.notAttemptedCount}</span></td>
//                       <td><span className={`fw-bold ${parseFloat(row.passPercentage) >= 80 ? "text-success" : parseFloat(row.passPercentage) >= 50 ? "text-warning" : "text-danger"}`}>{row.passPercentage}%</span></td>
//                       <td><Button size="sm" color="success" onClick={(e) => { e.stopPropagation(); openReportModal(row.originalBatch); }}><i className="fas fa-download me-1"></i> Report</Button></td>
//                     </tr>
//                     {expandedBatch === row.id && (
//                       <tr><td colSpan="14" className="p-3 bg-light">
//                         <div className="row">
//                           <div className="col-md-6">
//                             <h6>Batch Details</h6>
//                             <table className="table table-sm table-borderless">
//                               <tbody><tr><td><strong>Batch ID:</strong></td><td>{row.id}</td></tr><tr><td><strong>Department:</strong></td><td>{row.originalBatch.department || "N/A"}</td></tr></tbody>
//                             </table>
//                           </div>
//                           <div className="col-md-6">
//                             <h6>Trainers List</h6>
//                             {batchTrainersMap[row.id]?.length > 0 ? (
//                               <ul className="list-unstyled">{batchTrainersMap[row.id].map((trainer, idx) => (<li key={idx}>• {trainer.name || `${trainer.firstName || ""} ${trainer.lastName || ""}`.trim()} ({trainer.email || "No email"})</li>))}</ul>
//                             ) : <span className="text-muted">No trainers assigned</span>}
//                           </div>
//                         </div>
//                       </td></tr>)}
//                   </React.Fragment>
//                 ))}
//               </tbody>
//             </table>
//           </div>

//           {rows.length === 0 && (
//             <div className="text-center py-5 text-muted"><i className="fas fa-inbox fa-3x mb-3"></i><p>No batches found</p></div>
//           )}
//         </CardBody>
//       </Card>

//       {/* Failed Students Modal */}
//       <Modal isOpen={showFailedModal} toggle={() => setShowFailedModal(false)} size="xl">
//         <ModalHeader toggle={() => setShowFailedModal(false)} style={{ background: "#dc3545", color: "white" }}>
//           <ThumbDownIcon style={{ marginRight: "10px" }} /> Failed Students ({totalFailed})
//         </ModalHeader>
//         <ModalBody>
//           <div className="mb-3"><Button color="danger" size="sm" onClick={() => exportStudentsToExcel(getFilteredFailedStudents(), "Failed_Students")}><i className="fas fa-file-excel me-2"></i> Export to Excel</Button></div>
//           <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
//             <table className="table table-bordered table-hover">
//               <thead className="table-danger"><tr><th>S.No</th><th>Student Name</th><th>Mobile</th><th>Email</th><th>Post Score</th><th>Student Division</th><th>Student State</th><th>Student Depot</th><th>Training State</th><th>Training Depot</th><th>Batch Name</th></tr></thead>
//               <tbody>{getFilteredFailedStudents().map((s, i) => (<tr key={i}><td>{i + 1}</td><td><strong>{s.name}</strong></td><td>{s.mobile || "N/A"}</td><td>{s.email || "N/A"}</td><td><Badge color="danger">{s.postTestScore ? `${s.postTestScore}%` : "N/A"}</Badge></td><td>{s.studentDivision || "N/A"}</td><td>{s.studentState || "N/A"}</td><td>{s.studentDepot || "N/A"}</td><td>{s.trainingState || "N/A"}</td><td>{s.trainingDepot || "N/A"}</td><td>{s.batchName}</td></tr>))}</tbody>
//             </table>
//           </div>
//         </ModalBody>
//         <ModalFooter><Button color="secondary" onClick={() => setShowFailedModal(false)}>Close</Button></ModalFooter>
//       </Modal>

//       {/* Not Attempted Students Modal */}
//       <Modal isOpen={showNotAttemptedModal} toggle={() => setShowNotAttemptedModal(false)} size="xl">
//         <ModalHeader toggle={() => setShowNotAttemptedModal(false)} style={{ background: "#ffc107", color: "#856404" }}>
//           <RemoveCircleIcon style={{ marginRight: "10px" }} /> Not Attempted Students ({totalNotAttempted})
//         </ModalHeader>
//         <ModalBody>
//           <div className="mb-3"><Button color="warning" size="sm" onClick={() => exportStudentsToExcel(getFilteredNotAttemptedStudents(), "Not_Attempted_Students")}><i className="fas fa-file-excel me-2"></i> Export to Excel</Button></div>
//           <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
//             <table className="table table-bordered table-hover">
//               <thead className="table-warning"><tr><th>S.No</th><th>Student Name</th><th>Mobile</th><th>Email</th><th>Student Division</th><th>Student State</th><th>Student Depot</th><th>Training State</th><th>Training Depot</th><th>Batch Name</th></tr></thead>
//               <tbody>{getFilteredNotAttemptedStudents().map((s, i) => (<tr key={i}><td>{i + 1}</td><td><strong>{s.name}</strong></td><td>{s.mobile || "N/A"}</td><td>{s.email || "N/A"}</td><td>{s.studentDivision || "N/A"}</td><td>{s.studentState || "N/A"}</td><td>{s.studentDepot || "N/A"}</td><td>{s.trainingState || "N/A"}</td><td>{s.trainingDepot || "N/A"}</td><td>{s.batchName}</td></tr>))}</tbody>
//             </table>
//           </div>
//         </ModalBody>
//         <ModalFooter><Button color="secondary" onClick={() => setShowNotAttemptedModal(false)}>Close</Button></ModalFooter>
//       </Modal>

//       {/* Passed Students Modal */}
//       <Modal isOpen={showPassedModal} toggle={() => setShowPassedModal(false)} size="xl">
//         <ModalHeader toggle={() => setShowPassedModal(false)} style={{ background: "#28a745", color: "white" }}>
//           <ThumbUpIcon style={{ marginRight: "10px" }} /> Passed Students ({totalPassed})
//         </ModalHeader>
//         <ModalBody>
//           <div className="mb-3"><Button color="success" size="sm" onClick={() => exportStudentsToExcel(getFilteredPassedStudents(), "Passed_Students")}><i className="fas fa-file-excel me-2"></i> Export to Excel</Button></div>
//           <div className="table-responsive" style={{ maxHeight: "500px", overflowY: "auto" }}>
//             <table className="table table-bordered table-hover">
//               <thead className="table-success"><tr><th>S.No</th><th>Student Name</th><th>Mobile</th><th>Email</th><th>Post Score</th><th>Student Division</th><th>Student State</th><th>Student Depot</th><th>Training State</th><th>Training Depot</th><th>Batch Name</th></tr></thead>
//               <tbody>{getFilteredPassedStudents().map((s, i) => (<tr key={i}><td>{i + 1}</td><td><strong>{s.name}</strong></td><td>{s.mobile || "N/A"}</td><td>{s.email || "N/A"}</td><td><Badge color="success">{s.postTestScore ? `${s.postTestScore}%` : "N/A"}</Badge></td><td>{s.studentDivision || "N/A"}</td><td>{s.studentState || "N/A"}</td><td>{s.studentDepot || "N/A"}</td><td>{s.trainingState || "N/A"}</td><td>{s.trainingDepot || "N/A"}</td><td>{s.batchName}</td></tr>))}</tbody>
//             </table>
//           </div>
//         </ModalBody>
//         <ModalFooter><Button color="secondary" onClick={() => setShowPassedModal(false)}>Close</Button></ModalFooter>
//       </Modal>

//       {/* Single Batch Report Modal */}
//       <Modal isOpen={reportModalShow} centered onHide={() => setReportModalShow(false)}>
//         <ModalHeader closeButton>Download Batch Report</ModalHeader>
//         <ModalBody>
//           <div className="mb-3">
//             <div className="d-flex align-items-center mb-3 p-3 bg-light rounded">
//               <SchoolIcon className="me-2 text-primary" style={{ fontSize: "1.5rem" }} />
//               <div><small className="text-muted d-block">Batch Name</small><strong>{selectedBatch?.batchName || "N/A"}</strong></div>
//             </div>
//             <div className="mb-3">
//               <label className="mb-2 fw-bold">Select Report Type:</label>
//               <Select options={reportOptions} value={selectedReportType} onChange={setSelectedReportType} placeholder="Choose report type..." isSearchable={false} />
//             </div>
//           </div>
//         </ModalBody>
//         <ModalFooter>
//           <Button color="secondary" onClick={() => setReportModalShow(false)}>Cancel</Button>
//           <Button color="success" onClick={confirmDownloadReport} disabled={!selectedReportType}><i className="fas fa-download me-2"></i> Download</Button>
//         </ModalFooter>
//       </Modal>

//       {/* All Batches Report Modal */}
//       <Modal isOpen={allReportsModalShow} centered onHide={() => setAllReportsModalShow(false)} size="lg">
//         <ModalHeader closeButton>Download All Reports</ModalHeader>
//         <ModalBody>
//           <div className="d-flex gap-3 mb-3 p-3 bg-primary bg-opacity-10 rounded flex-wrap">
//             <div><small className="text-muted">Batches</small><div className="fw-bold fs-4">{filteredBatches.length}</div></div>
//             <div><small className="text-muted">Students</small><div className="fw-bold fs-4">{totalStudents}</div></div>
//             <div><small className="text-muted">Pass Rate</small><div className="fw-bold fs-4">{overallPassRate}%</div></div>
//           </div>
//           <label className="mb-2 fw-bold">Report Type:</label>
//           <Select options={reportOptions} value={selectedAllReportType} onChange={setSelectedAllReportType} placeholder="Choose..." isSearchable={false} />
//           {filteredBatches.length > 5 && <div className="mt-3 p-2 bg-warning bg-opacity-10 rounded"><small><i className="fas fa-exclamation-triangle text-warning me-1"></i>Processing {filteredBatches.length} batches may take a few minutes.</small></div>}
//         </ModalBody>
//         <ModalFooter>
//           <Button variant="secondary" onClick={() => setAllReportsModalShow(false)}>Cancel</Button>
//           <Button color="success" onClick={confirmDownloadAllReports} disabled={!selectedAllReportType}><i className="fas fa-download me-2"></i>Download All</Button>
//         </ModalFooter>
//       </Modal>

//       {/* Sheet per Batch Modal */}
//       <Modal isOpen={consolidatedModalShow} centered onHide={() => setConsolidatedModalShow(false)} size="lg">
//         <ModalHeader closeButton>Sheet per Batch Report</ModalHeader>
//         <ModalBody>
//           <div className="d-flex align-items-center mb-3 p-3 bg-success bg-opacity-10 rounded">
//             <i className="fas fa-table me-3 text-success" style={{ fontSize: "2rem" }}></i>
//             <div><strong>Complete Batch Reports</strong><small className="d-block text-muted">Index + Original Reports + Batch Summary</small></div>
//           </div>
//           <div className="d-flex gap-3 mb-3 p-2 bg-light rounded flex-wrap">
//             <div><small className="text-muted">Batches to Process</small><div className="fw-bold fs-5">{filteredBatches.length}</div></div>
//             <div><small className="text-muted">Total Students</small><div className="fw-bold fs-5">{totalStudents}</div></div>
//           </div>
//           <label className="mb-2 fw-bold">Report Type:</label>
//           <Select options={consolidatedOptions} value={consolidatedOptions.find(opt => opt.value === consolidatedReportType)} onChange={(option) => setConsolidatedReportType(option.value)} placeholder="Choose..." isSearchable={false} />
//           {filteredBatches.length > 5 && <div className="mt-3 p-2 bg-warning bg-opacity-10 rounded"><small><i className="fas fa-exclamation-triangle text-warning me-1"></i>Processing {filteredBatches.length} batches may take a few minutes.</small></div>}
//         </ModalBody>
//         <ModalFooter>
//           <Button variant="secondary" onClick={() => setConsolidatedModalShow(false)}>Cancel</Button>
//           <Button color="success" onClick={confirmSheetPerBatchReport}><i className="fas fa-file-excel me-2"></i>Generate</Button>
//         </ModalFooter>
//       </Modal>
//     </>
//   );
// };

// // ============================
// // MAIN DASHBOARD COMPONENT (Original UI/UX Restored)
// // ============================
// const PowerBIDashboard = () => {
//   const navigate = useNavigate();
//   const [fromDate, setFromDate] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
//   const [toDate, setToDate] = useState(format(new Date(), "yyyy-MM-dd"));
//   const [divisionText, setDivisionText] = useState("");
//   const [selectedState, setSelectedState] = useState(null);
//   const [selectedDivision, setSelectedDivision] = useState(null);
//   const [selectedDepot, setSelectedDepot] = useState(null);
//   const [datePeriod, setDatePeriod] = useState("ALL");
//   const [customStartDate, setCustomStartDate] = useState(null);
//   const [customEndDate, setCustomEndDate] = useState(null);
//   const [isCustomDateRangeActive, setIsCustomDateRangeActive] = useState(false);
//   const [showDateRangeModal, setShowDateRangeModal] = useState(false);
//   const [stateOptions, setStateOptions] = useState([{ value: "all", label: "All States" }]);
//   const [divisionOptions, setDivisionOptions] = useState([{ value: "all", label: "All Divisions" }]);
//   const [depotOptions, setDepotOptions] = useState([{ value: "all", label: "All Depots" }]);
//   const [allStatesList, setAllStatesList] = useState([]);
//   const [allDivisionsList, setAllDivisionsList] = useState([]);
//   const [allDepotsByState, setAllDepotsByState] = useState({});
//   const [allDepotsByDivision, setAllDepotsByDivision] = useState({});
//   const [totalDepotsCount, setTotalDepotsCount] = useState(0);
//   const [loader, setLoader] = useState(true);
//   const [chartsData, setChartsData] = useState([]);
//   const [batchChartSeries, setBatchChartSeries] = useState([]);
//   const [batchChartOptions, setBatchChartOptions] = useState({});
//   const [liveData, setLiveData] = useState(null);
//   const [exportLoading, setExportLoading] = useState(false);
//   const [refreshing, setRefreshing] = useState(false);
//   const [trainingDistribution, setTrainingDistribution] = useState([]);
//   const [distributionLoading, setDistributionLoading] = useState(false);
//   const [allTrainings, setAllTrainings] = useState([]);
//   const [authChecked, setAuthChecked] = useState(false);
//   const [printPreviewOpen, setPrintPreviewOpen] = useState(false);
//   const [activeTab, setActiveTab] = useState("analytics");
//   const [stats, setStats] = useState({ totalCandidates: 0, nominated: 0, attempted: 0, passed: 0 });
//   const isAuthenticated = useRef(false);
//   const initialFetchDone = useRef(false);
//   const locationDataCache = useRef(null);
//   const trainingDataCache = useRef(null);

//   const periodOptions = [
//     { value: "ALL", label: "All Time" },
//     { value: "TODAY", label: "Today" },
//     { value: "WEEKLY", label: "This Week" },
//     { value: "MONTHLY", label: "This Month" },
//     { value: "YEARLY", label: "This Year" },
//   ];

//   // Check authentication
//   useEffect(() => {
//     const checkAuth = () => {
//       const authUser = localStorage.getItem("authUser");
//       isAuthenticated.current = !!authUser;
//       setAuthChecked(true);
//     };
//     checkAuth();
//     window.addEventListener("storage", checkAuth);
//     return () => window.removeEventListener("storage", checkAuth);
//   }, []);

//   // Fetch student data to extract filters
//   const fetchStudentDataAndExtractFilters = useCallback(async (forceRefresh = false) => {
//     const authUser = localStorage.getItem("authUser");
//     if (!authUser) return null;
//     if (!forceRefresh && locationDataCache.current) return locationDataCache.current;
//     try {
//       setDistributionLoading(true);
//       const response = await getAllStudentsForTata();
//       let students = response?.success ? (response?.result || response?.data || []) : [];
//       const locationFilters = extractLocationFilters(students);
//       locationDataCache.current = locationFilters;
//       return locationFilters;
//     } catch (error) {
//       console.error("Error fetching student data:", error);
//       if (error?.response?.status === 401) { localStorage.removeItem("authUser"); isAuthenticated.current = false; navigate("/login"); }
//       return null;
//     } finally { setDistributionLoading(false); }
//   }, [navigate]);

//   // Fetch training data
//   const fetchTrainingData = useCallback(async (forceRefresh = false) => {
//     const authUser = localStorage.getItem("authUser");
//     if (!authUser) return [];
//     if (!forceRefresh && trainingDataCache.current) return trainingDataCache.current;
//     try {
//       const response = await fetchAllTraining();
//       if (response?.success) {
//         const trainings = response?.result || response?.data || [];
//         trainingDataCache.current = trainings;
//         return trainings;
//       }
//       return [];
//     } catch (error) {
//       console.error("Error fetching training data:", error);
//       if (error?.response?.status === 401) { localStorage.removeItem("authUser"); isAuthenticated.current = false; navigate("/login"); }
//       return [];
//     }
//   }, [navigate]);

//   // Update depot options
//   const updateDepotOptions = useCallback(() => {
//     let depotOpts = [];
//     if (selectedState && selectedState.value !== "all" && allDepotsByState[selectedState.label]) {
//       depotOpts = [{ value: "all", label: "All Depots" }, ...allDepotsByState[selectedState.label].map(d => ({ value: d.toLowerCase().replace(/\s+/g, "_"), label: d }))];
//     } else if (selectedDivision && selectedDivision.value !== "all" && allDepotsByDivision[selectedDivision.label]) {
//       depotOpts = [{ value: "all", label: "All Depots" }, ...allDepotsByDivision[selectedDivision.label].map(d => ({ value: d.toLowerCase().replace(/\s+/g, "_"), label: d }))];
//     } else {
//       const allDepots = [...new Set(Object.values(allDepotsByState).flat().sort())];
//       depotOpts = [{ value: "all", label: "All Depots" }, ...allDepots.map(d => ({ value: d.toLowerCase().replace(/\s+/g, "_"), label: d }))];
//     }
//     setDepotOptions(depotOpts);
//   }, [selectedState, selectedDivision, allDepotsByState, allDepotsByDivision]);

//   useEffect(() => { updateDepotOptions(); }, [selectedState, selectedDivision, updateDepotOptions]);

//   // Initialize location filters
//   const initializeLocationFilters = useCallback(async (forceRefresh = false) => {
//     const locationFilters = await fetchStudentDataAndExtractFilters(forceRefresh);
//     if (locationFilters) {
//       setAllStatesList(locationFilters.statesList);
//       setAllDivisionsList(locationFilters.divisionsList);
//       setAllDepotsByState(locationFilters.depotsByState);
//       setAllDepotsByDivision(locationFilters.depotsByDivision);
//       setTotalDepotsCount(locationFilters.totalDepotsCount);
//       setStateOptions([{ value: "all", label: "All States" }, ...locationFilters.statesList.map(s => ({ value: s.toLowerCase().replace(/\s+/g, "_"), label: s }))]);
//       setDivisionOptions([{ value: "all", label: "All Divisions" }, ...locationFilters.divisionsList.map(d => ({ value: d.toLowerCase().replace(/\s+/g, "_"), label: d }))]);
//       updateDepotOptions();
//     }
//   }, [fetchStudentDataAndExtractFilters, updateDepotOptions]);

//   // Load dashboard data
//   const loadDashboardData = useCallback(async (forceRefresh = false) => {
//     if (!isAuthenticated.current) { setLoader(false); return; }
//     try {
//       setLoader(true);
//       await initializeLocationFilters(forceRefresh);
//       const trainings = await fetchTrainingData(forceRefresh);
//       setAllTrainings(trainings);
//       const distribution = processTrainingDistribution(trainings, selectedState, selectedDivision, selectedDepot);
//       setTrainingDistribution(distribution);
//       const params = {
//         division: divisionText || (selectedDivision && selectedDivision.value !== "all" ? selectedDivision.label : undefined),
//         state: selectedState && selectedState.value !== "all" ? selectedState.label : undefined,
//         depot: selectedDepot && selectedDepot.value !== "all" ? selectedDepot.label : undefined
//       };
//       const data = await getTataDashboardData(params.division, params.state, params.depot);
//       if (data?.success) {
//         const result = data?.tataResult || data?.result || {};
//         const totalStudent = result?.totalStudent || 0;
//         const nominatedStudent = result?.nominatedStudent || 0;
//         const completedBatch = result?.completedBatch || 0;
//         const totalBatch = result?.totalBatch || 0;
//         const attemptedStudents = result?.attemptedStudent || 0;
//         const passedStudents = result?.passedStudent || 0;
//         const totalCandidatesFromDistribution = distribution.reduce((sum, item) => sum + item.count, 0);
//         const finalTotalCandidates = totalStudent > 0 ? totalStudent : totalCandidatesFromDistribution;
//         setStats({ totalCandidates: finalTotalCandidates, nominated: nominatedStudent, attempted: attemptedStudents, passed: passedStudents });
//         setLiveData({
//           courseName: result?.courseName || "Driver/Technical/IT/Safety Training",
//           project: result?.project || "Smart City Mobility",
//           batches: { planned: totalBatch, completed: completedBatch, ongoing: totalBatch - completedBatch },
//           stats: { totalCandidates: finalTotalCandidates, nominated: nominatedStudent, attempted: attemptedStudents, passed: passedStudents, passPercentage: attemptedStudents > 0 ? ((passedStudents / attemptedStudents) * 100).toFixed(1) : 0 }
//         });
//         const newChartsData = [
//           { ...makeChart("Total Candidates vs Nominated", ["Total", "Nominated"], [finalTotalCandidates || 0, nominatedStudent || 0], "bar", ["#4299e1", "#48bb78"]), series: [{ name: "Count", data: [finalTotalCandidates || 0, nominatedStudent || 0] }] },
//           { ...makeChart("Nominated vs Attempted", ["Nominated", "Attempted"], [nominatedStudent || 0, attemptedStudents || 0], "bar", ["#ed8936", "#ecc94b"]), series: [{ name: "Count", data: [nominatedStudent || 0, attemptedStudents || 0] }] },
//           { ...makeChart("Attempted vs Passed", ["Attempted", "Passed"], [attemptedStudents || 0, passedStudents || 0], "bar", ["#ecc94b", "#48bb78"]), series: [{ name: "Count", data: [attemptedStudents || 0, passedStudents || 0] }] },
//           { ...makeChart("Pass Rate Analysis", ["Pass Rate"], [attemptedStudents > 0 ? (passedStudents / attemptedStudents * 100) : 0], "bar", ["#48bb78"]), series: [{ name: "Pass Rate %", data: [attemptedStudents > 0 ? (passedStudents / attemptedStudents * 100) : 0] }] }
//         ];
//         setChartsData(newChartsData);
//         setBatchChartSeries([{ name: "Batches", data: [totalBatch || 0, completedBatch || 0, totalBatch - completedBatch || 0] }]);
//         setBatchChartOptions({ ...makeChart("Batch Completion Status", ["Planned", "Completed", "Ongoing"], [totalBatch || 0, completedBatch || 0, totalBatch - completedBatch || 0], "bar", ["#4299e1", "#48bb78", "#ecc94b"]), chart: { type: "bar" } });
//       } else {
//         setLiveData({ courseName: "Driver/Technical/IT/Safety Training", project: "Smart City Mobility", batches: { planned: 0, completed: 0, ongoing: 0 }, stats: { totalCandidates: 0, nominated: 0, attempted: 0, passed: 0, passPercentage: 0 } });
//       }
//     } catch (error) {
//       console.error("Dashboard error:", error);
//       if (error?.response?.status === 401) { localStorage.removeItem("authUser"); isAuthenticated.current = false; navigate("/login"); }
//       toast.error("Something went wrong loading data.");
//     } finally { setLoader(false); setRefreshing(false); }
//   }, [divisionText, selectedState, selectedDivision, selectedDepot, initializeLocationFilters, fetchTrainingData, navigate]);

//   const handlePeriodChange = (option) => {
//     setDatePeriod(option.value);
//     if (option.value === "CUSTOM") setShowDateRangeModal(true);
//     else { setIsCustomDateRangeActive(false); setCustomStartDate(null); setCustomEndDate(null); }
//   };

//   const applyCustomDateRange = () => {
//     if (!customStartDate || !customEndDate) { toast.error("Please select both start and end dates"); return; }
//     if (new Date(customStartDate) > new Date(customEndDate)) { toast.error("Start date cannot be after end date"); return; }
//     setIsCustomDateRangeActive(true);
//     setShowDateRangeModal(false);
//     setDatePeriod("CUSTOM");
//     toast.success("Custom date range applied");
//   };

//   const clearCustomDateRange = () => { setCustomStartDate(null); setCustomEndDate(null); setIsCustomDateRangeActive(false); setDatePeriod("ALL"); };
//   const handleFilter = () => { setRefreshing(true); loadDashboardData(true); };
//   const handleRefresh = () => { setRefreshing(true); loadDashboardData(true); };
//   const resetFilters = () => {
//     setSelectedState(null); setSelectedDivision(null); setSelectedDepot(null);
//     setDivisionText(""); setDatePeriod("ALL");
//     setCustomStartDate(null); setCustomEndDate(null);
//     setIsCustomDateRangeActive(false);
//     setFromDate(format(subDays(new Date(), 30), "yyyy-MM-dd"));
//     setToDate(format(new Date(), "yyyy-MM-dd"));
//     loadDashboardData(true);
//   };
//   const handlePrintPreview = () => setPrintPreviewOpen(true);

//   const exportToExcel = () => {
//     setExportLoading(true);
//     try {
//       const filteredDist = trainingDistribution.filter(t => {
//         if (selectedState && selectedState.value !== "all" && t.state !== selectedState.label) return false;
//         if (selectedDivision && selectedDivision.value !== "all" && t.division !== selectedDivision.label) return false;
//         if (selectedDepot && selectedDepot.value !== "all" && t.depot !== selectedDepot.label) return false;
//         return true;
//       });
//       const wb = XLSX.utils.book_new();
//       const summaryData = [
//         ["Training Analytics Report"], [`Generated: ${new Date().toLocaleString()}`],
//         [`Date Period: ${datePeriod}${isCustomDateRangeActive && customStartDate ? ` (${formatDateToDDMMYYYY(customStartDate)} to ${formatDateToDDMMYYYY(customEndDate)})` : ""}`],
//         [`Division Text: ${divisionText || "All"}`], [`State: ${selectedState?.label || "All"}`],
//         [`Division: ${selectedDivision?.label || "All"}`], [`Depot: ${selectedDepot?.label || "All"}`], [],
//         ["Candidate Statistics"], ["Metric", "Count", "Percentage"],
//         ["Total Candidates", stats.totalCandidates, "100%"],
//         ["Nominated", stats.nominated, stats.totalCandidates ? `${((stats.nominated / stats.totalCandidates) * 100).toFixed(1)}%` : "0%"],
//         ["Attempted", stats.attempted, stats.nominated ? `${((stats.attempted / stats.nominated) * 100).toFixed(1)}%` : "0%"],
//         ["Passed (≥80%)", stats.passed, stats.attempted ? `${((stats.passed / stats.attempted) * 100).toFixed(1)}%` : "0%"], [],
//         ["Training Distribution"], ["S.No", "State", "Depot", "Category", "Count"],
//         ...filteredDist.map((t, i) => [i + 1, t.state, t.depot, t.category, t.count]),
//       ];
//       const ws = XLSX.utils.aoa_to_sheet(summaryData);
//       XLSX.utils.book_append_sheet(wb, ws, "Training Report");
//       XLSX.writeFile(wb, `Training_Report_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
//       toast.success("Excel exported!");
//     } catch (error) { toast.error("Export failed"); }
//     finally { setExportLoading(false); }
//   };

//   useEffect(() => {
//     if (authChecked && isAuthenticated.current && !initialFetchDone.current) {
//       initialFetchDone.current = true;
//       loadDashboardData();
//     }
//   }, [authChecked, loadDashboardData]);

//   if (loader && !refreshing) return <Loader />;
//   if (!isAuthenticated.current && authChecked) {
//     return (
//       <div className="d-flex justify-content-center align-items-center" style={{ height: "70vh" }}>
//         <Card style={{ width: "400px" }}>
//           <CardBody className="text-center p-4">
//             <h4 className="mb-3">Authentication Required</h4>
//             <p className="text-muted mb-4">Please log in to view the dashboard.</p>
//             <Button color="primary" onClick={() => navigate("/login")}>Go to Login</Button>
//           </CardBody>
//         </Card>
//       </div>
//     );
//   }

//   return (
//     <Row>
//       <Col xl="12">
//         <Card>
//           <CardBody>
//             {/* Tab Navigation */}
//             <div className="d-flex border-bottom mb-4">
//               <button className={`btn btn-link ${activeTab === "analytics" ? "active" : ""}`} onClick={() => setActiveTab("analytics")} style={{ textDecoration: "none", fontWeight: activeTab === "analytics" ? "bold" : "normal", borderBottom: activeTab === "analytics" ? "2px solid #1e3c72" : "none", color: activeTab === "analytics" ? "#1e3c72" : "#6c757d" }}>
//                 <BarChartIcon style={{ fontSize: "1rem", marginRight: "8px", verticalAlign: "middle" }} /> Training Analytics
//               </button>
//               <button className={`btn btn-link ${activeTab === "batchDetails" ? "active" : ""}`} onClick={() => setActiveTab("batchDetails")} style={{ textDecoration: "none", fontWeight: activeTab === "batchDetails" ? "bold" : "normal", borderBottom: activeTab === "batchDetails" ? "2px solid #1e3c72" : "none", color: activeTab === "batchDetails" ? "#1e3c72" : "#6c757d" }}>
//                 <GroupsIcon style={{ fontSize: "1rem", marginRight: "8px", verticalAlign: "middle" }} /> Batch Details
//               </button>
//             </div>

//             {/* Training Analytics Tab (Original UI/UX) */}
//             {activeTab === "analytics" && (
//               <>
//                 <div className="d-flex justify-content-between align-items-center mb-4">
//                   <div><h4 className="card-title mb-0">Training Analytics Dashboard</h4><small className="text-muted"><WarehouseIcon style={{ fontSize: "1rem", marginRight: "4px", verticalAlign: "middle" }} /> Database: {allStatesList.length} States • {allDivisionsList.length} Divisions • {totalDepotsCount} Depots</small></div>
//                   <div className="d-flex gap-2"><Button color="light" onClick={handleRefresh} disabled={refreshing}>{refreshing ? <><Spinner size="sm" className="me-2" /> Refreshing...</> : <><i className="mdi mdi-refresh me-2"></i>Refresh</>}</Button></div>
//                 </div>

//                 {stats.totalCandidates > 0 ? (
//                   <Alert color="success" className="mb-4"><i className="mdi mdi-check-circle me-2"></i><strong>Live Data Loaded</strong> • {stats.nominated} nominated • {stats.attempted} attempted • {stats.passed} passed • {stats.attempted > 0 ? `${((stats.passed / stats.attempted) * 100).toFixed(1)}%` : "0%"} pass rate</Alert>
//                 ) : (
//                   <Alert color="warning" className="mb-4"><i className="mdi mdi-alert-circle me-2"></i><strong>No Data Available</strong> - Database: {allStatesList.length} states, {allDivisionsList.length} divisions, {totalDepotsCount} depots</Alert>
//                 )}

//                 <Row className="mb-4 align-items-end">
//                   <Col md={2}><Label>From Date</Label><Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} max={toDate} /></Col>
//                   <Col md={2}><Label>To Date</Label><Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} min={fromDate} max={format(new Date(), "yyyy-MM-dd")} /></Col>
//                   <Col md={2}><Label>Division (Text)</Label><Input type="text" placeholder="Enter Division" value={divisionText} onChange={(e) => setDivisionText(e.target.value)} /></Col>
//                   <Col md={2}><Label><LocationOnIcon style={{ fontSize: "1rem", marginRight: "4px", verticalAlign: "middle" }} /> State ({allStatesList.length})</Label><Select options={stateOptions} value={selectedState} onChange={setSelectedState} placeholder="Select State" isClearable isLoading={distributionLoading} /></Col>
//                   <Col md={2}><Label><FilterListIcon style={{ fontSize: "1rem", marginRight: "4px", verticalAlign: "middle" }} /> Division ({allDivisionsList.length})</Label><Select options={divisionOptions} value={selectedDivision} onChange={setSelectedDivision} placeholder="Select Division" isClearable isLoading={distributionLoading} /></Col>
//                   <Col md={2}><div className="d-flex gap-2 mt-4"><Button color="primary" onClick={handleFilter} disabled={refreshing || !fromDate || !toDate} style={{ flex: 1 }}>{refreshing ? <Spinner size="sm" /> : "Apply"}</Button><Button color="secondary" onClick={resetFilters} disabled={refreshing}><i className="mdi mdi-refresh"></i></Button></div></Col>
//                 </Row>

//                 <Row className="mb-3">
//                   <Col md={12}>
//                     <Label><WarehouseIcon style={{ fontSize: "1rem", marginRight: "4px", verticalAlign: "middle" }} /> Depot<small className="text-muted ms-1">{selectedState?.value !== "all" && selectedState ? `${allDepotsByState[selectedState.label]?.length || 0} in ${selectedState.label}` : selectedDivision?.value !== "all" && selectedDivision ? `${allDepotsByDivision[selectedDivision.label]?.length || 0} in ${selectedDivision.label}` : `${totalDepotsCount} total`}</small></Label>
//                     <Select options={depotOptions} value={selectedDepot} onChange={setSelectedDepot} placeholder="Select Depot" isClearable isLoading={distributionLoading} />
//                   </Col>
//                 </Row>

//                 <Row className="mb-4">
//                   <Col md={3}><Card className="border"><CardBody className="p-3 text-center"><h6 className="text-muted mb-1">Total Candidates</h6><h3 className="mb-0">{stats.totalCandidates.toLocaleString()}</h3></CardBody></Card></Col>
//                   <Col md={3}><Card className="border"><CardBody className="p-3 text-center"><h6 className="text-muted mb-1">Nominated</h6><h3 className="mb-0 text-success">{stats.nominated.toLocaleString()}</h3><small className="text-muted">{stats.totalCandidates ? `${((stats.nominated / stats.totalCandidates) * 100).toFixed(1)}%` : "0%"}</small></CardBody></Card></Col>
//                   <Col md={3}><Card className="border"><CardBody className="p-3 text-center"><h6 className="text-muted mb-1">Attempted</h6><h3 className="mb-0 text-warning">{stats.attempted.toLocaleString()}</h3><small className="text-muted">{stats.nominated ? `${((stats.attempted / stats.nominated) * 100).toFixed(1)}%` : "0%"}</small></CardBody></Card></Col>
//                   <Col md={3}><Card className="border"><CardBody className="p-3 text-center"><h6 className="text-muted mb-1">Passed (≥80%)</h6><h3 className="mb-0 text-primary">{stats.passed.toLocaleString()}</h3><small className="text-muted">{stats.attempted ? `${((stats.passed / stats.attempted) * 100).toFixed(1)}%` : "0%"} pass rate</small></CardBody></Card></Col>
//                 </Row>

//                 <Row>
//                   <Col md={6}><h5 className="mt-4">Batch Completion Status</h5><div id="column-chart" className="apex-charts" dir="ltr">{batchChartSeries.length > 0 ? <Chart options={batchChartOptions} series={batchChartSeries} type="bar" height={320} /> : <div className="text-center py-5"><Spinner color="primary" /></div>}</div></Col>
//                   <Col md={6}><div className="d-flex justify-content-between align-items-center mb-3"><h5 className="mt-4">Training Distribution</h5><small className="text-muted">{trainingDistribution.length} locations</small></div><div className="table-responsive" style={{ maxHeight: "350px", overflowY: "auto" }}><table className="table table-hover mb-0"><thead><tr><th>Depot</th><th>State</th><th>Category</th><th className="text-end">Count</th></tr></thead><tbody>{distributionLoading ? <tr><td colSpan="4" className="text-center py-4"><Spinner size="sm" /> Loading...</td></tr> : trainingDistribution.length > 0 ? trainingDistribution.slice(0, 10).map((t, i) => (<tr key={i}><td><i className="mdi mdi-warehouse me-1 text-primary"></i>{t.depot}</td><td><i className="mdi mdi-map-marker me-1 text-success"></i>{t.state}</td><td><span className="badge bg-light text-dark"><i className="mdi mdi-tag me-1"></i>{t.category}</span></td><td className="text-end"><strong>{t.count.toLocaleString()}</strong></td></tr>)) : <tr><td colSpan="4" className="text-center text-muted py-4">No data available</td></tr>}</tbody>{trainingDistribution.length > 0 && <tfoot><tr className="table-light"><th colSpan="3">Total</th><th className="text-end">{trainingDistribution.reduce((sum, t) => sum + (t.count || 0), 0).toLocaleString()}</th></tr></tfoot>}</table></div></Col>
//                 </Row>

//                 <Row className="mt-4">{chartsData.map((chart, i) => (<Col md={6} key={i}><Card><CardBody><Chart options={chart.options || chart} series={chart.series} type="bar" height={280} /></CardBody></Card></Col>))}</Row>

//                 <Row className="mt-3">
//                   <Col xl="12">
//                     <Card><CardBody><div className="d-flex justify-content-between align-items-center mb-4"><h4 className="card-title mb-0">Reports & Exports</h4><div className="d-flex gap-2"><Button color="info" onClick={handlePrintPreview} disabled={!stats.totalCandidates}><PrintIcon style={{ fontSize: "1.1rem", marginRight: "8px" }} /> Print Report</Button><Button color="success" onClick={exportToExcel} disabled={exportLoading || !stats.totalCandidates}>{exportLoading ? <Spinner size="sm" className="me-2" /> : <i className="mdi mdi-file-excel me-2"></i>}Export Excel</Button><PDFDownloadLink document={<TrainingReportPDF data={liveData} dateRange={{ fromDate, toDate }} trainingDistribution={trainingDistribution} stats={stats} filters={{ division: divisionText, selectedState, selectedDivision, selectedDepot, allStatesList, allDivisionsList, depotsByState: allDepotsByState, depotsByDivision: allDepotsByDivision, totalDepotsCount }} />} fileName={`TML_Report_${format(new Date(), "yyyy-MM-dd")}.pdf`}>{({ loading }) => (<Button color="danger" disabled={loading || !stats.totalCandidates}>{loading ? <Spinner size="sm" className="me-2" /> : <PictureAsPdfIcon style={{ fontSize: "1.1rem", marginRight: "8px" }} />}{loading ? "Generating..." : "Export PDF"}</Button>)}</PDFDownloadLink></div></div></CardBody></Card>
//                   </Col>
//                 </Row>
//               </>
//             )}

//             {/* Batch Details Tab */}
//             {activeTab === "batchDetails" && (
//               <BatchDetailsTab 
//                 datePeriod={datePeriod}
//                 customStartDate={customStartDate}
//                 customEndDate={customEndDate}
//                 selectedState={selectedState?.label}
//                 selectedDepot={selectedDepot?.label}
//                 selectedDivision={selectedDivision?.label}
//               />
//             )}
//           </CardBody>
//         </Card>
//       </Col>

//       {/* Custom Date Range Modal */}
//       <Modal isOpen={showDateRangeModal} centered toggle={() => setShowDateRangeModal(false)}>
//         <ModalHeader toggle={() => setShowDateRangeModal(false)}>Select Custom Date Range</ModalHeader>
//         <ModalBody>
//           <Row>
//             <Col md={6}><Label>Start Date</Label><DatePicker selected={customStartDate} onChange={(date) => setCustomStartDate(date)} className="form-control" dateFormat="dd/MM/yyyy" placeholderText="Select start date" /></Col>
//             <Col md={6}><Label>End Date</Label><DatePicker selected={customEndDate} onChange={(date) => setCustomEndDate(date)} className="form-control" dateFormat="dd/MM/yyyy" placeholderText="Select end date" /></Col>
//           </Row>
//         </ModalBody>
//         <ModalFooter><Button color="secondary" onClick={() => setShowDateRangeModal(false)}>Cancel</Button><Button color="primary" onClick={applyCustomDateRange}>Apply</Button></ModalFooter>
//       </Modal>

//       {/* Print Preview Modal */}
//       <PrintPreviewModal isOpen={printPreviewOpen} toggle={() => setPrintPreviewOpen(false)} data={liveData} dateRange={{ fromDate, toDate }} trainingDistribution={trainingDistribution} stats={stats} filters={{ division: divisionText, selectedState, selectedDivision, selectedDepot, allStatesList, allDivisionsList, depotsByState: allDepotsByState, depotsByDivision: allDepotsByDivision, totalDepotsCount }} chartsData={chartsData} batchChartOptions={batchChartOptions} batchChartSeries={batchChartSeries} />
//     </Row>
//   );
// };

// export default PowerBIDashboard;